import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, ApiError, Button, Card, ConfirmDialog, Field, InfoTip, PillButton, SaveFlash, apiErrorMessage, clientAdminLabel, clientAdminName, collectDisplayYears, displayYearLabel, formatMoney, inDisplayYear, isChargeClassActiveNow, formatIsoDay, creditNotePreview, creditPriorsFrom, invoiceLineBalanceFactor, invoiceLineTaxShare, brandLocalDate, type CreditPriors, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import type {
  Business,
  ChargeClass,
  Client,
  CreateInvoiceRequest,
  CreditLineReason,
  DiscountType,
  Invoice,
  InvoiceLineInput,
  InvoiceLineItem,
  InvoiceListScope,
  InvoiceState,
  LineItemKind,
  Payment,
  PaymentAllocationContext,
  PaymentAllocationView,
  PaymentMethod,
  PaymentTerms,
  PullItemRef,
  RecordPaymentRequest,
  RecordSettlementRequest,
  ConfirmPaymentRequest,
  EarlyPayQuote,
  EarlyPayDiscountOverride,
  AlsoVoidedAdjustment,
  CreateAdjustmentRequest,
  CreditOrAdjustment,
  RefundMethod,
  NonPaymentSuspendedUnit,
  WouldReleaseDocument,
  PaidLateFeeHeadsUp,
  ResolvedTaxInfo,
  TaxResolutionSource,
  CatalogTaxCategory,
  InvoiceLineTaxExemptReason,
  UnbilledItemsResponse,
  UnbilledPickerItem,
  UpdateInvoiceRequest,
  PendingCredit,
  PendingCreditTrigger,
  PendingCreditsResponse,
} from '@aralere/shared-types';
import { TAX_CATEGORY_OPTIONS, isCatalogTaxCategory, taxCategoryLabel } from './taxCategory';
import { PendingCreditsDialog, refundPrefill, resolvedSummary } from './PendingCreditsDialog';

// Invoices admin (spec §7; Stage-4 slice 2 — invoice CORE + ISSUE lifecycle). Invoices are
// partitioned by CLIENT, so the page is client-scoped: leave the picker on "All invoices" to see
// every client's invoices, or pick a client to see theirs and create one. A DRAFT is fully editable
// (§7.13); ISSUING stamps a permanent gapless number + the billing owner (billedToClientId) and
// freezes the Fork-1 totals — after that it's immutable (void/credit-note to correct, later slices).
// LINES are manual AND pulled (§7.11, canonical slice 3): "Add unbilled items" opens the unified
// picker over the client's unbilled billable time / mileage / expense lines — pulling marks the
// source billed IMMEDIATELY (draft add; the 409 entry_billed lock is live from that moment) and the
// pulled line carries a read-only source cost + a locked qty of 1; description and charge stay
// editable (never written back to the source). Removing a pulled line and saving — or deleting the
// draft — RELEASES the source back to the unbilled pool. Opening the picker saves the draft first
// (a pull is a server op against the stored draft). PAYMENTS (Stage-4): "Record payment" records
// REAL payment documents (method/date/reference/memo; partials supported, §7.16) — the invoice flips
// Paid only when the method-aware balance hits 0. The list groups Drafts / Awaiting payment (with a
// due-date countdown + payment-terms days remaining) / Paid / Closed, each collapsible. A recorded
// payment is immutable: correction = Void (reason required) + re-record; voiding a settling payment
// reopens the invoice (and re-locks any delivery-on-payment documents). Both totals always shown
// (§7.4). Required fields carry the red asterisk via the shared Field.

const inputClass = 'w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]';

const fmtMoney = formatMoney;
const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100;
/** Fraction rate → percent DISPLAY string at FULL precision (s75 invariant: NEVER round the RATE —
 *  only the tax AMOUNT rounds, half-up, in computeInvoiceTotals). 0.08625 → "8.625",
 *  0.05728474584 → "5.728474584". `toPrecision(12)` then Number strips binary-float noise without
 *  truncating meaningful digits; 0/null/undefined → ''. Used at EVERY tax-rate prefill so the
 *  submitted override carries the unrounded rate (the s74 RED: 8.625 was being shown/submitted as 8.63). */
const taxRatePct = (rate: number | null | undefined): string =>
  rate ? String(Number((rate * 100).toPrecision(12))) : '';
/** "July 2026" for a composed monthly invoice's covered month (from coversPeriodStart; UTC-safe,
 *  no ICU). Empty when there's no coverage period (s75 fix 4 — the composed-period tag). */
const coverageLabel = (inv: { coversPeriodStart?: string | null }): string => {
  if (!inv.coversPeriodStart) return '';
  const s = inv.coversPeriodStart.slice(0, 10);
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${months[(Number(s.slice(5, 7)) || 1) - 1]} ${s.slice(0, 4)}`;
};

/** YYYY-MM-DD -> MM-DD-YYYY. */
function displayDate(iso: string | null): string {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-');
  return `${m}-${d}-${y}`;
}

const STATE_LABEL: Record<InvoiceState, string> = {
  draft: 'Draft',
  issued: 'Issued',
  paid: 'Paid',
  void: 'Void',
  'written-off': 'Written off',
};
const STATE_TONE: Record<InvoiceState, string> = {
  draft: 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)]',
  issued: 'bg-[var(--tint-blue-bg)] text-[var(--tint-blue-text)]',
  paid: 'bg-[var(--tint-green-bg)] text-[var(--tint-green-text)]',
  void: 'bg-[var(--tint-slate-bg)] text-[var(--tint-slate-text)] line-through',
  'written-off': 'bg-[var(--tint-red-bg)] text-[var(--tint-red-text)]',
};

const TERMS: { value: PaymentTerms; label: string }[] = [
  { value: 'due-on-receipt', label: 'Due on receipt' },
  { value: 'net15', label: 'Net 15' },
  { value: 'net30', label: 'Net 30' },
  { value: 'net45', label: 'Net 45' },
  { value: 'net60', label: 'Net 60' },
];
const TERM_DAYS: Record<PaymentTerms, number> = { net15: 15, net30: 30, net45: 45, net60: 60, 'due-on-receipt': 0 };

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'check', label: 'Check' },
  { value: 'money-order', label: 'Money order' },
  { value: 'cash', label: 'Cash' }, // #64: admin-only (the client §7.8 flow keeps zelle/check/money-order)
];
const METHOD_LABEL: Record<string, string> = {
  zelle: 'Zelle', check: 'Check', 'money-order': 'Money order', cash: 'Cash', stripe: 'Stripe', square: 'Square',
};

/** Whole days from today ON THE BRAND CLOCK (the invoice's brand, 02 s177) to an ISO date: 0 = today,
 *  negative = past. Both ends are calendar dates, so the day arithmetic itself runs at UTC midnight. */
function daysUntil(iso: string, timezone: string | null): number {
  const today = new Date(`${brandLocalDate(timezone)}T00:00:00Z`).getTime();
  const target = new Date(`${iso}T00:00:00Z`).getTime();
  return Math.round((target - today) / 86_400_000);
}

/** "Due today" / "Due in N days" / "Overdue N days". */
function dueCountdown(dueDate: string, timezone: string | null): { label: string; overdue: boolean } {
  const d = daysUntil(dueDate, timezone);
  if (d === 0) return { label: 'Due today', overdue: false };
  if (d > 0) return { label: `Due in ${d} day${d === 1 ? '' : 's'}`, overdue: false };
  return { label: `Overdue ${-d} day${d === -1 ? '' : 's'}`, overdue: true };
}

/** Payment-terms window remaining, e.g. "Net 30 · 12 of 30 term days left". */
function termsCountdown(inv: Invoice, timezone: string | null): string {
  const total = TERM_DAYS[inv.paymentTerms] ?? 0;
  const termLabel = TERMS.find((t) => t.value === inv.paymentTerms)?.label ?? inv.paymentTerms;
  if (total === 0) return termLabel; // Due on receipt has no window
  const left = daysUntil(inv.dueDate, timezone);
  return left >= 0 ? `${termLabel} · ${left} of ${total} term days left` : `${termLabel} · term expired`;
}

/**
 * Remaining balance for a NON-CARD payment — mirror of api/src/lib/invoices.ts#computeSettlement
 * (keep in step): once any payment is being made by Zelle/check/money order, the invoice settles at
 * the WITHOUT-fee total (§7.4 discount-for-non-card); card payments (Stripe/Square, a later slice)
 * keep the with-fee target. amountPaid is the server-stamped snapshot on the invoice.
 */
function nonCardRemaining(inv: Invoice): number {
  const credits = round2((inv.appliedCredits ?? []).reduce((s, c) => s + c.amount, 0));
  return round2(Math.max(0, inv.totalWithoutConvenienceFee - inv.amountPaid - credits));
}

/** Σ of an invoice's applied (non-void) credits — mirrors api/src/lib/invoices.ts#sumAppliedCredits. */
function appliedCreditsTotal(inv: Invoice): number {
  return round2((inv.appliedCredits ?? []).reduce((s, c) => s + c.amount, 0));
}

/** 04 #69: an ISSUED invoice carries frozen early-pay terms iff dueBy + basis are stored with a
 *  positive rate (the server's frozenEarlyPay shape test; a DRAFT holds only an override). Shape only:
 *  every figure the dialogs show comes from the server quote (02 s97). */
function hasEarlyPayTerms(inv: Invoice): boolean {
  const t = inv.earlyPayDiscount;
  return !!t && typeof t.dueBy === 'string' && typeof t.basis === 'number' && t.rate > 0;
}
/** 04 #69: a stored rate as a percent label ("2", "2.5"), never a float artefact. */
function pctLabel(rate: number): string {
  return String(Number((rate * 100).toPrecision(12)));
}
/** 04 #69: the per-invoice override a draft save sends: '' = inherit (null), 'none' = rate 0 (no
 *  discount on this invoice), 'custom' = { rate, days } (percent 0/blank = none as well). */
function builtDraftEarlyPay(f: FormState): EarlyPayDiscountOverride | null {
  if (f.earlyPayMode === 'none') return { rate: 0, days: 0 };
  if (f.earlyPayMode !== 'custom') return null;
  const raw = parseFloat(f.earlyPayRatePct);
  const rate = f.earlyPayRatePct.trim() && Number.isFinite(raw) && raw > 0 ? Math.round((raw / 100) * 1e6) / 1e6 : 0;
  const d = parseInt(f.earlyPayDays, 10);
  return { rate, days: Number.isFinite(d) && d >= 0 && d <= 365 ? d : 0 };
}

// #64 multi-invoice allocation — one editable row per open invoice of the chosen billing entity.
type CrossRow = {
  invoiceId: string;
  /** 04 #88: the invoice's OWN partition owner. Both pickers built on this row span a billing
   *  entity's LINKED PEOPLE, so a row has to say whose invoice it is - at s95 the verification
   *  fixture had to be identified out of band by cross-referencing another list first. It was
   *  already on AllocatableInvoice and simply not carried through, so no server change is needed. */
  clientId: string;
  documentNumber: string | null;
  issueDate: string;
  dueDate: string;
  remaining: number;
  pastDue: boolean;
  amount: string;
  /** 04 #69 rule 16: the row's SERVER early-pay quote (present only when the invoice carries terms),
   *  evaluated on the dialog's date received, and whether the admin keeps its discount ticked. */
  earlyPay?: EarlyPayQuote;
  applyEarlyPay: boolean;
};

/** 04 #69: the figure a row is prefilled toward: its pay-in-full-with-discount amount while its
 *  discount is ticked and eligible, else its remaining balance (both server figures). */
function crossRowTarget(r: CrossRow): number {
  return r.applyEarlyPay && r.earlyPay?.eligible && r.earlyPay.payInFullAmount != null ? r.earlyPay.payInFullAmount : r.remaining;
}

/** 04 #93 (c): one settlement-screen credit row: an issued client credit of the invoice's billing
 *  entity with remaining balance, plus the amount the admin chooses to fund from it (starts empty). */
type PayCredit = {
  creditId: string;
  documentNumber: string | null;
  remaining: number;
  amount: string;
};

/** Distribute an amount received across rows OLDEST-first (rows arrive oldest-first from the server),
 *  filling each to its remaining until the amount is exhausted (owner rule: oldest-first pre-fill). */
/** 04 #60 WS4: the voluntary tip typed into the cross dialog, as a number (0 when blank or junk). */
const crossTipOf = (f: { tip: string }): number => round2(parseFloat(f.tip) || 0);

function crossPrefill(rows: CrossRow[], received: number): CrossRow[] {
  let left = round2(Math.max(0, received));
  return rows.map((r) => {
    const take = round2(Math.max(0, Math.min(crossRowTarget(r), left)));
    left = round2(left - take);
    return { ...r, amount: take > 0 ? String(take) : '' };
  });
}

const REFUND_METHODS: { value: RefundMethod; label: string }[] = [
  { value: 'zelle', label: 'Zelle' },
  { value: 'check', label: 'Check' },
  { value: 'money-order', label: 'Money order' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

const ADJ_LABEL: Record<string, string> = {
  'credit-note': 'Credit note',
  refund: 'Refund',
  'client-credit': 'Client credit',
  'write-off': 'Write-off',
  'vendor-credit': 'Vendor credit',
  'early-pay-discount': 'Early-pay discount', // 04 #69: a CRN of its own kind, reversed only with its payment
};

/** Mirror of api/src/lib/invoices.ts#computeDueDate — keep in step. */
function previewDueDate(issueDate: string, terms: PaymentTerms): string {
  if (!issueDate) return '';
  const d = new Date(`${issueDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + (TERM_DAYS[terms] ?? 0));
  return d.toISOString().slice(0, 10);
}

/** A charge-class row's engine facts (04 #60 WS2) - the preview mirror's input, resolved from the
 *  loaded brand catalog. */
interface PreviewChargeClass { rateType: 'percent' | 'flat'; value: number; taxMode: 'nontaxable' | 'taxable' | 'apportioned'; percentBasis?: 'pre-discount' | 'post-discount' }

/** One preview line's tax facts (04 #160 WS-C M1): the share of its net that is taxed and the rate
 *  it is taxed at. `fraction` absent = the boolean `taxable`; `rate` absent = the invoice rate. */
interface PreviewLineTax { fraction?: number; rate?: number; gated?: boolean }

/** Mirror of api/src/lib/invoices.ts#computeInvoiceTotals (Fork-1, AMENDED slice 4: per-line
 *  discounts + per-line taxability with pro-rata invoice-discount allocation; AMENDED 04 #60 WS2:
 *  charge-class lines are excluded from the invoice discount on BOTH sides of the ratio, a percent
 *  class derives from the post-discount ORDINARY net, and taxability follows the class taxMode with
 *  'apportioned' = the ordinary taxable ratio; AMENDED 04 #45 O5: a composed CREDIT line nets to its
 *  negative gross inside the ordinary subtotal, never taxable, and the ordinary ratio clamps at 1;
 *  AMENDED 04 #160 WS-C M1 (02i §8 DP-1 / DP-2 / DP-7): the invoice discount's base and its uniform
 *  pro-rata allocation are the POSITIVE ordinary lines, each line's taxable share is its resolved
 *  FRACTION of its net after the discount, the lines group by RATE (a rule's reduced rate makes its
 *  own group) with each group's base summed exactly and rounded once, the class lines join the
 *  invoice-rate group, a GATED class line (no registration / an exempt client) taxes nothing, and
 *  `registered: false` zeroes the tax outright; AMENDED 04 #160 WS-C M2a (02i T1.3 / T1.4 / T2.2): a
 *  credit row adds its FIXED tax to the total as carried and its frozen fraction to the apportioned
 *  ratio's numerator, the ratio clamped to [0, 1]) - keep in step. Preview only. `lineAmounts` is the
 *  per-row net, index-aligned, so a charge-class row can display its DERIVED amount live. */
function previewTotals(
  lines: { charge: number; qty: number; taxable: boolean; discount: { type: DiscountType; value: number } | null; chargeClass?: PreviewChargeClass | null; credit?: boolean; creditTaxAmount?: number; tax?: PreviewLineTax }[],
  discount: { type: DiscountType; value: number } | null,
  tax: { enabled: boolean; rate: number; registered?: boolean },
  convenienceFeeRate: number,
): { subtotal: number; ordinarySubtotal: number; chargeSubtotal: number; lineDiscountTotal: number; discountAmount: number; taxAmount: number; feeAmount: number; totalWithConvenienceFee: number; totalWithoutConvenienceFee: number; lineAmounts: number[]; taxGroups: { rate: number; taxableNet: number; amount: number }[] } {
  const invoiceRate = tax.rate;
  const computed = lines.map((l) => {
    if (l.chargeClass) return { discountAmount: 0, net: 0, ordinary: false, positive: false };
    const gross = round2(l.charge * l.qty);
    if (l.credit) return { discountAmount: 0, net: gross, ordinary: true, positive: false };
    const d = l.discount
      ? l.discount.type === 'percent'
        ? round2(gross * l.discount.value)
        : round2(Math.min(Math.max(l.discount.value, 0), gross))
      : 0;
    return { discountAmount: d, net: round2(Math.max(0, gross - d)), ordinary: true, positive: true };
  });
  // 04 #160 WS-C M2a (02i T2.2): a CREDIT row's frozen fraction enters the apportioned ratio's numerator
  // (x its negative net) and nothing else - its tax is the fixed creditTaxAmount below.
  const fractionOf = (i: number): number => {
    const l = lines[i];
    if (!computed[i].ordinary) return 0;
    if (l.credit) return l.tax?.fraction !== undefined ? Math.min(1, Math.max(0, l.tax.fraction)) : 0;
    if (l.tax?.fraction !== undefined) return Math.min(1, Math.max(0, l.tax.fraction));
    return l.taxable ? 1 : 0;
  };
  const ordinarySubtotal = round2(computed.reduce((s, l) => s + (l.ordinary ? l.net : 0), 0));
  const positiveOrdinarySubtotal = round2(computed.reduce((s, l) => s + (l.positive ? l.net : 0), 0));
  const lineDiscountTotal = round2(computed.reduce((s, l) => s + l.discountAmount, 0));
  const ordinaryTaxableNet = round2(computed.reduce((s, l, i) => s + fractionOf(i) * l.net, 0));
  const ordinaryTaxableRatio = ordinarySubtotal > 0 ? Math.min(1, Math.max(0, ordinaryTaxableNet / ordinarySubtotal)) : 0;
  let discountAmount = 0;
  if (discount) {
    discountAmount = discount.type === 'percent'
      ? round2(positiveOrdinarySubtotal * discount.value)
      : round2(Math.min(Math.max(discount.value, 0), positiveOrdinarySubtotal));
  }
  const ordinaryDiscountedNet = round2(Math.max(0, ordinarySubtotal - discountAmount));
  // Rate groups: exact shares summed, each group's base rounded once, its tax rounded once.
  const groupBase = new Map<number, number>();
  computed.forEach((c, i) => {
    if (!c.positive) return;
    const netAfter = Math.max(0, positiveOrdinarySubtotal > 0 ? c.net - (discountAmount * c.net) / positiveOrdinarySubtotal : c.net);
    const share = fractionOf(i) * netAfter;
    if (share === 0) return;
    const r = lines[i].tax?.rate;
    const rate = r !== undefined && Number.isFinite(r) ? r : invoiceRate;
    groupBase.set(rate, (groupBase.get(rate) ?? 0) + share);
  });
  let chargeSubtotal = 0;
  let classTaxable = 0;
  const lineAmounts = computed.map((c, i) => {
    const cc = lines[i].chargeClass;
    if (!cc) return c.net;
    const percentBase = cc.percentBasis === 'post-discount' ? ordinaryDiscountedNet : ordinarySubtotal;
    const amount = cc.rateType === 'flat' ? round2(Math.max(0, cc.value)) : round2(percentBase * cc.value);
    const gated = lines[i].tax?.gated === true || lines[i].tax?.fraction === 0;
    const taxableNet = gated || cc.taxMode === 'nontaxable' ? 0 : cc.taxMode === 'taxable' ? amount : round2(amount * ordinaryTaxableRatio);
    chargeSubtotal = round2(chargeSubtotal + amount);
    classTaxable = round2(classTaxable + taxableNet);
    return amount;
  });
  groupBase.set(invoiceRate, (groupBase.get(invoiceRate) ?? 0) + classTaxable);
  const taxOn = tax.enabled && tax.registered !== false;
  const taxGroups: { rate: number; taxableNet: number; amount: number }[] = [];
  let taxAmount = 0;
  for (const [rate, exact] of groupBase) {
    const base = round2(exact);
    if (base === 0 && rate !== invoiceRate) continue;
    const amount = taxOn ? round2(base * rate) : 0;
    taxGroups.push({ rate, taxableNet: base, amount });
    taxAmount = round2(taxAmount + amount);
  }
  // 04 #160 WS-C M2a (02i T1.4 / §8 DP-2): + the credit rows' FIXED tax, as carried - never recomputed at
  // this invoice's rate and not zeroed by a gate or tax off (the tax was collected on the source invoice).
  const creditTaxAmount = round2(lines.reduce((s, l) => s + (l.credit && typeof l.creditTaxAmount === 'number' && Number.isFinite(l.creditTaxAmount) ? Math.min(0, l.creditTaxAmount) : 0), 0));
  taxAmount = round2(taxAmount + creditTaxAmount);
  const subtotal = round2(ordinarySubtotal + chargeSubtotal);
  const discountedBase = round2(Math.max(0, subtotal - discountAmount));
  const taxedBase = round2(discountedBase + taxAmount);
  const feeAmount = round2(taxedBase * convenienceFeeRate);
  return { subtotal, ordinarySubtotal, chargeSubtotal, lineDiscountTotal, discountAmount, taxAmount, feeAmount, totalWithConvenienceFee: round2(taxedBase + feeAmount), totalWithoutConvenienceFee: taxedBase, lineAmounts, taxGroups };
}

/** Plain-language label for a stored tax resolution source (slice 4). */
const TAX_SOURCE_LABEL: Record<TaxResolutionSource, string> = {
  'invoice-override': 'set on this invoice',
  'client-override': "the client's tax override",
  'address-profile': "the client's address jurisdiction",
  'business-backup': "the brand's backup jurisdiction",
  'brand-default': "the brand's default rate",
};

interface LineRow {
  /** The server line id — echoed on save so pulled lines keep their sourceRef and manual lines
   *  keep a stable identity across edits (slice 3 reconcile). Absent on a brand-new manual row. */
  lineId?: string;
  description: string;
  qty: string;
  cost: string;
  charge: string;
  kind: LineItemKind;
  /** True for a §7.11 pulled line: qty/cost/kind are locked (cost is read-only from the source);
   *  description + charge stay editable; removing it releases the source when the draft is saved. */
  pulled?: boolean;
  /** "Time" | "Mileage" | "Expense" — the pulled line's source badge. */
  sourceLabel?: string;
  /** Per-line taxability (slice 4) — the "Exclude tax" checkbox is the INVERSE of this. 04 #160
   *  WS-C M1: on a rule-decided row this is the RULE's answer (read-only until the admin touches the
   *  checkbox); on a manual row it is the admin's choice. */
  taxable: boolean;
  /** True when the server auto-excluded the line because its source expense already paid tax at
   *  purchase (the 04 A9 notation; shown while the line stays excluded). */
  paidAtPurchase?: boolean;
  // ── 04 #160 WS-C M1 (02i §8 DP-6; the editor contract, 02i §8 item 12 (c)) ──
  /** The line's tax category (manual + pulled rows; the select). Absent on class / credit rows and
   *  on composed rows, whose category is the catalog's and shows read-only as `taxCategoryFixed`. */
  taxCategory?: CatalogTaxCategory;
  /** A composed row's category label (the catalog service's category, or the package bundle). */
  taxCategoryFixed?: string;
  /** 'rule' = the destination's rule decides (re-resolved on every save); 'override' = the admin
   *  overrode the line (stored taxRuleId null; never re-resolved until "Use the rule"). */
  taxMode: 'rule' | 'override';
  /** The admin touched the checkbox during THIS edit: the save sends `taxable` (a value that differs
   *  from the rule's answer makes the line an override; one that agrees leaves it rule-decided). */
  taxTouched?: boolean;
  /** "Use the rule" was clicked during this edit: the save sends `useTaxRule: true`. */
  useTaxRule?: boolean;
  /** The stored resolution, for the per-row display and the preview mirror. */
  taxableFraction?: number;
  taxRate?: number;
  taxRuleId?: string | null;
  taxExemptReason?: InvoiceLineTaxExemptReason | null;
  /** Per-line discount (slice 4). 'none' = no line discount. */
  discType: 'none' | DiscountType;
  discValue: string;
  /** 04 #60 WS2: set on a CHARGE-CLASS row. The class facts are resolved from the loaded brand
   *  catalog so the row can show its derived amount live and flag a deactivated class. A class the
   *  catalog no longer resolves (never expected - classes are never deleted) renders as
   *  'unresolved' and the server refuses the save. qty/cost/charge/kind/taxable/discount are
   *  ignored on save; only the description is sent. */
  chargeClassId?: string;
  /** `active` = effectively active TODAY (04 #60 WS2b: a scheduled deactivation is resolved in the
   *  brand timezone, mirroring the server predicate); `deactivatesAt` is shown on the marker. */
  chargeClass?: { name: string; rateType: 'percent' | 'flat'; value: number; taxMode: 'nontaxable' | 'taxable' | 'apportioned'; percentBasis: 'pre-discount' | 'post-discount'; treatment: 'revenue' | 'pass-through'; active: boolean; deactivatesAt: string | null } | null;
  /** 04 #60 WS2b: the stored "charge it anyway" acknowledgement on a class line (echoed on save;
   *  issue no longer asks about an acknowledged line). */
  chargeClassAcknowledged?: boolean;
  /** 04 #45 (02g #45 O5): a composed CREDIT line. Read-only in the editor (the server keeps it exactly
   *  as composed when its lineId is echoed); removing it DEFERS the credit, which the next compose
   *  re-derives. `charge` holds the negative figure as a string. */
  creditReason?: CreditLineReason;
  /** 04 #160 WS-C M2a (02i T1.3): a credit row's FIXED tax (≤ 0, its source line's at the credited
   *  fraction), carried for the preview mirror and the caption; absent on a pre-M2a credit row. */
  creditTaxAmount?: number;
  /** 04 #45 O4: the days a composed line covers (shown on composed rows; never typed). */
  coversFrom?: string;
  coversTo?: string;
}
const emptyLine = (): LineRow => ({ description: '', qty: '1', cost: '', charge: '', kind: 'one-time', taxable: true, taxMode: 'rule', taxCategory: 'service.professional', discType: 'none', discValue: '' });

const SOURCE_LABEL: Record<string, string> = { time: 'Time', mileage: 'Mileage', expense: 'Expense' };

/** The brand catalog as the editor sees it (04 #60 WS2). */
const catalogOf = (b: Business | undefined): ChargeClass[] => (b && Array.isArray(b.chargeClasses) ? b.chargeClasses : []);
/** 04 #60 WS2b: effectively active TODAY in the brand's timezone (the server predicate's mirror). */
const classActiveNow = (cls: ChargeClass, b: Business | undefined): boolean => isChargeClassActiveNow(cls, b?.timezone ?? null);

/** An editor row for a charge class (04 #60 WS2). `activeNow` = effectively active today (WS2b). */
function chargeClassRow(cls: ChargeClass, activeNow: boolean, lineId?: string, description?: string, acknowledged?: boolean): LineRow {
  return {
    ...(lineId ? { lineId } : {}),
    description: (description ?? '').trim() || cls.name,
    qty: '1', cost: '', charge: '0', kind: 'one-time', taxable: cls.taxMode !== 'nontaxable', taxMode: 'rule', discType: 'none', discValue: '',
    chargeClassId: cls.id,
    chargeClass: { name: cls.name, rateType: cls.rateType, value: cls.value, taxMode: cls.taxMode, percentBasis: cls.percentBasis ?? 'pre-discount', treatment: cls.treatment ?? 'revenue', active: activeNow, deactivatesAt: cls.deactivatesAt ?? null },
    ...(acknowledged ? { chargeClassAcknowledged: true } : {}),
  };
}

/** Map a stored invoice's lines into editor rows (pulled metadata included; charge-class rows
 *  resolved against the brand catalog, 04 #60 WS2; class status resolved in the brand tz, WS2b). */
function rowsFromInvoice(inv: Invoice, brand: Business | undefined): LineRow[] {
  const catalog = catalogOf(brand);
  if (inv.lineItems.length === 0) return [emptyLine()];
  return inv.lineItems.map((l) => {
    if (l.chargeClassId) {
      const cls = catalog.find((c) => c.id === l.chargeClassId);
      return cls
        ? chargeClassRow(cls, classActiveNow(cls, brand), l.lineId, l.description, l.chargeClassAcknowledged === true)
        : { lineId: l.lineId, description: l.description, qty: '1', cost: '', charge: String(l.charge), kind: l.kind, taxable: l.taxable !== false, taxMode: 'rule' as const, discType: 'none' as const, discValue: '', chargeClassId: l.chargeClassId, chargeClass: null };
    }
    return {
      lineId: l.lineId,
      description: l.description,
      qty: String(l.qty),
      cost: l.cost ? String(l.cost) : '',
      charge: l.charge || l.charge === 0 ? String(l.charge) : '0',
      kind: l.kind,
      pulled: !!l.sourceRef,
      sourceLabel: l.sourceRef ? SOURCE_LABEL[l.sourceRef.sourceType] ?? 'Source' : undefined,
      taxable: l.taxable !== false,
      paidAtPurchase: l.taxExemptReason === 'paid-at-purchase',
      // 04 #160 WS-C M1: the stored resolution (a composed line's category is the composer's and is
      // shown, not edited; a manual / pulled line's is the select's, 'service.professional' when the
      // line predates M1).
      ...(l.creditReason
        ? {}
        : l.coversFrom || l.packageId
          ? { taxCategoryFixed: taxCategoryLabel(l.taxCategory ?? (l.packageId ? 'bundle' : null)) }
          : { taxCategory: isCatalogTaxCategory(l.taxCategory) ? l.taxCategory : ('service.professional' as const) }),
      taxMode: l.taxRuleId === null ? ('override' as const) : ('rule' as const),
      ...(typeof l.taxableFraction === 'number' ? { taxableFraction: l.taxableFraction } : {}),
      ...(typeof l.taxRate === 'number' ? { taxRate: l.taxRate } : {}),
      ...(l.taxRuleId !== undefined ? { taxRuleId: l.taxRuleId } : {}),
      ...(l.taxExemptReason !== undefined ? { taxExemptReason: l.taxExemptReason } : {}),
      discType: l.discount ? l.discount.type : 'none',
      discValue: l.discount ? String(l.discount.type === 'percent' ? round2(l.discount.value * 100) : l.discount.value) : '',
      ...(l.creditReason ? { creditReason: l.creditReason } : {}),
      ...(l.creditReason && typeof l.taxAmount === 'number' ? { creditTaxAmount: l.taxAmount } : {}),
      ...(l.coversFrom ? { coversFrom: l.coversFrom } : {}),
      ...(l.coversTo ? { coversTo: l.coversTo } : {}),
    };
  });
}

/** 04 #160 WS-C M1: the stored rule id in words (01b s227 forms). */
function taxRuleWords(ruleId: string | null | undefined, category: string | undefined): string {
  if (!ruleId) return 'resolves when saved';
  const parts = ruleId.split(':');
  const label = category ?? '';
  if (parts[0] === 'bundle') return `package: ${parts[2] ?? '?'} members taxable by ${parts[1] ?? 'list price'}`;
  if (parts[0] === 'brand') {
    const from = parts[4] && parts[4] !== '0000-00-00' ? ` from ${parts[4]}` : '';
    return `the brand's own ${parts[2] ?? ''} rule for ${label}${from}`;
  }
  const from = parts[3];
  if (from === 'none') return `no ${parts[1] ?? ''} rule for ${label} (not taxable there)`;
  if (from === 'legacy') return `the ${parts[1] ?? ''} late-fee setting`;
  return `the ${parts[1] ?? ''} rule for ${label}${from && from !== '0000-00-00' ? ` from ${from}` : ''}`;
}

/** 04 #160 WS-C M1 (02i §8 DP-6; the per-row display, 02i §8 item 12 (c)): what decides this line's
 *  tax and why, from the STORED resolution (the server re-resolves on every save; the editor never
 *  resolves itself - the 02 s97 "import the authority" rule). `gate` = the invoice-level registration /
 *  exemption gate. Returns the sentence and whether the "Exclude tax" checkbox applies. */
function lineTaxStatus(
  row: LineRow,
  gate: 'not-registered' | 'exempt-client' | null,
  brandName: string,
  invoiceRatePct: string,
): { text: string; checkbox: boolean; useRule: boolean } {
  const label = row.taxCategoryFixed ?? taxCategoryLabel(row.taxCategory ?? null);
  if (gate === 'not-registered') return { text: `No tax: ${brandName} is not registered to collect sales tax at this client's address, so this line collects nothing and the use-tax footnote prints.`, checkbox: false, useRule: false };
  if (gate === 'exempt-client') return { text: 'No tax: the client holds a tax exemption certificate (frozen on the invoice at issue).', checkbox: false, useRule: false };
  if (row.useTaxRule) return { text: `Resolves by rule when saved (${label}).`, checkbox: false, useRule: false };
  if (row.taxTouched) return { text: row.taxable ? 'Taxed at the invoice rate (your choice; saved as a manual override if the rule says otherwise).' : 'Not taxed (your choice; saved as a manual override if the rule says otherwise).', checkbox: true, useRule: false };
  if (row.taxMode === 'override') return { text: row.taxable ? 'Manual: taxed at the invoice rate (overrides the rule; never re-resolved).' : 'Manual: not taxed (overrides the rule; never re-resolved).', checkbox: true, useRule: true };
  if (row.paidAtPurchase) return { text: 'Not taxed: sales tax was paid to the vendor at purchase. Re-include it (uncheck) for a resale-style charge.', checkbox: true, useRule: false };
  if (row.taxRuleId === undefined) return { text: `Resolves by rule when saved (${label}).`, checkbox: false, useRule: false };
  const words = taxRuleWords(row.taxRuleId, label);
  if (row.taxExemptReason === 'rule' || !row.taxable) return { text: `Not taxable by rule: ${words}.`, checkbox: true, useRule: false };
  const f = row.taxableFraction ?? 1;
  const share = f < 1 ? `${Number((f * 100).toPrecision(6))}% of the line` : 'the whole line';
  const rate = row.taxRate !== undefined ? `${taxRatePct(row.taxRate) || '0'}% (the rule's reduced rate)` : `${invoiceRatePct || '0'}%`;
  return { text: `Taxable by rule: ${share} at ${rate} (${words}).`, checkbox: true, useRule: false };
}

/** Plain-language rate + tax mode for a charge-class row (04 #60 WS2; 04 #125: a collected-on-behalf
 *  class says so, because the amount is owed to a payee rather than earned). */
function chargeClassHint(c: NonNullable<LineRow['chargeClass']>): string {
  const rate = c.rateType === 'percent' ? `${round2(c.value * 100)}% of the ordinary lines ${c.percentBasis === 'post-discount' ? 'after' : 'before'} the invoice discount` : `flat ${fmtMoney(c.value)}`;
  const tax = c.taxMode === 'nontaxable' ? 'not taxed' : c.taxMode === 'taxable' ? 'taxed at the invoice rate' : 'taxed in proportion to the taxable lines';
  const owed = c.treatment === 'pass-through' ? ' Collected on behalf of a payee, not revenue.' : '';
  return `${rate}; ${tax}. The invoice discount is never taken off this charge.${owed}`;
}

interface FormState {
  clientId: string;
  billingType: 'monthly' | 'one-off';
  issueDate: string;
  paymentTerms: PaymentTerms;
  /** 04 #69: the per-invoice early-pay override. '' = inherit (client override, else brand default);
   *  'custom' = the rate + days below; 'none' = no discount on this invoice (rate 0). Resolved and
   *  frozen at issue; a draft stores only this override. */
  earlyPayMode: '' | 'custom' | 'none';
  earlyPayRatePct: string;
  earlyPayDays: string;
  lines: LineRow[];
  discountType: 'none' | DiscountType;
  discountValue: string;
  taxEnabled: boolean;
  taxRate: string; // percent as entered, e.g. "9.75"
  /** False until the admin touches the tax controls: untouched, the save OMITS tax and the server
   *  resolves the Fork-2/Q47 chain; touched = an explicit per-invoice override (slice 4). */
  taxTouched: boolean;
  notes: string;
}

export function AdminInvoices(): JSX.Element {
  const { brand: portalBrand } = useBrand();
  const [clients, setClients] = useState<Client[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [clientId, setClientId] = useState('');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Invoice | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  /** s167 owner rule: whatever is opened or edited must show within the viewing window, so the
   *  editor scrolls itself into view when it opens (the Drafts list can be far below it). */
  const formCardRef = useRef<HTMLDivElement | null>(null);
  /** Bumped on every open (a re-click on the draft already open changes no other state). */
  const [openSeq, setOpenSeq] = useState(0);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** 04 #60 WS2: the class row whose inline "Replace" picker is open. */
  const [replaceIdx, setReplaceIdx] = useState<number | null>(null);

  const [issueTarget, setIssueTarget] = useState<Invoice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Invoice | null>(null);
  const [busy, setBusy] = useState(false);

  // Payments (slice 4; s77 #15 removed the redundant one-click "Mark paid" — "Record payment"
  // prefills the full remaining balance but stays editable for partials/overpayments).
  // 04 #93 (c): the dialog is now the SETTLEMENT SCREEN. When the invoice's BILLING ENTITY has
  // issued client credits with remaining balance, a credits section renders (only then; 02d ruling
  // 6/11) and one action funds the invoice from credit + cash together via the composed server op.
  // Rows load EMPTY (the system never spends a credit on its own initiative; 02d #73 lock 4) and
  // the cash amount live-reduces to remaining minus credits until the admin edits cash by hand.
  // 04 #60 WS4 (owner ruling s175): `amount` is the total RECEIVED, tip included, exactly as the cross
  // dialog's "Amount received" and as payments.amount is stored (s77 #1); the tip is taken OUT of it and
  // the remainder faces the invoice. It is prefilled to the balance, which is the received total when
  // there is no tip. The `tip` field is separate; the running line states what is applied.
  const [payForm, setPayForm] = useState<{
    invoice: Invoice; amount: string; method: PaymentMethod;
    dateReceived: string; receiptNumber: string; memo: string; overpaymentToCredit: boolean;
    credits: PayCredit[]; creditsLoading: boolean; cashEdited: boolean;
    tip: string; tipChargeClassId: string;
    /** 04 #69: the SERVER early-pay quote for this invoice on the dialog's date and cash (null until the
     *  first answer; absent terms = never loaded), and whether the admin keeps the discount ticked. */
    earlyPay: EarlyPayQuote | null; earlyPayLoading: boolean; applyEarlyPay: boolean;
  } | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  // s108 owner finding: with many credit rows the dialog scrolls, and an error appended near the
  // footer sat off-screen, reading as "nothing happened". Scroll it into view whenever it appears.
  const payErrorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (payError) payErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [payError]);
  // #64 "Record a payment across invoices": one non-card receipt spread across a billing entity's open
  // invoices (oldest-first prefill, editable rows), any leftover → an on-account credit or a refund.
  const [crossForm, setCrossForm] = useState<{
    entityId: string;
    entityName: string | null;
    method: PaymentMethod;
    amountReceived: string;
    dateReceived: string;
    receiptNumber: string;
    memo: string;
    leftoverDisposition: 'credit' | 'refund';
    leftoverRefundMethod: RefundMethod;
    rows: CrossRow[];
    /** 04 #60 WS4: a voluntary tip inside `amountReceived`; the rows are prefilled from received − tip. */
    tip: string;
    tipChargeClassId: string;
  } | null>(null);
  const [crossError, setCrossError] = useState<string | null>(null);
  const [crossLoading, setCrossLoading] = useState(false);
  const [paymentsByInvoice, setPaymentsByInvoice] = useState<Record<string, Payment[]>>({});
  /** 04 #72 overlay B: document numbers + client names for every ALLOCATED payment we have seen,
   *  keyed by payment id. Server-supplied, because `Payment.allocations` holds ids + amounts only and
   *  a cross-partition allocation is not in this page's client-scoped invoice list. Accumulates. */
  const [allocationCtx, setAllocationCtx] = useState<PaymentAllocationContext>({});
  /** The #72(c) allocation PREVIEW: one invoice named by an allocation, with its own payments. */
  const [allocPreview, setAllocPreview] = useState<PaymentAllocationView | null>(null);
  const [allocPreviewData, setAllocPreviewData] = useState<{ invoice: Invoice; payments: Payment[] } | null>(null);
  const [allocPreviewError, setAllocPreviewError] = useState<string | null>(null);
  /** Which dialog the preview was opened from, so its footer offers the right way back. */
  const [allocPreviewFrom, setAllocPreviewFrom] = useState<'void' | 'receipt'>('void');
  /** 04 #70 the RECEIPT DIALOG: the payment whose number was clicked (identity only, so the header can
   *  render at once) and the FRESH server read that supplies every figure. They are deliberately two
   *  pieces of state: the row that was clicked comes from accumulated page state, and no financial
   *  number in this dialog is allowed to come from there. */
  const [receiptTarget, setReceiptTarget] = useState<Payment | null>(null);
  const [receiptFresh, setReceiptFresh] = useState<Payment | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  // §7.8: invoice ids with a client-reported payment awaiting confirmation (badged in the list); the
  // invoice a payment-deep-link should open + flash; and the payment currently being confirmed/rejected.
  const [pendingNotationSet, setPendingNotationSet] = useState<Set<string>>(new Set());
  const [deepLinkPaymentsFor, setDeepLinkPaymentsFor] = useState<string | null>(null);
  // 04 #160 WS-C M2b (02i §8 DP-13): the lapse dialog - from the compose's nothing_to_compose (the compose
  // trigger) and the on-demand "Pending credits" read; and the paid-source branch's consume-once deep link
  // (&refund=<lineId>:<listAmount>[,...] beside client + open) that opens the refund dialog pre-filled.
  const [pendingCredits, setPendingCredits] = useState<PendingCreditsResponse | null>(null);
  const [pendingTrigger, setPendingTrigger] = useState<PendingCreditTrigger>('manual');
  const [deepLinkRefundFor, setDeepLinkRefundFor] = useState<{ invoiceId: string; lines: Array<{ lineId: string; amount: number; coversFrom?: string; coversTo?: string }> } | null>(null);
  /** 04 #93 (b): `&detail=1` opens the invoice's DETAIL modal once the list has it (the Money register's
   *  adjustment rows link here, where an applied credit's Un-apply lives). Same consume-once contract. */
  const [deepLinkDetailFor, setDeepLinkDetailFor] = useState<string | null>(null);
  const [flashInvoiceId, setFlashInvoiceId] = useState<string | null>(null);
  const [notationBusyId, setNotationBusyId] = useState<string | null>(null);
  /** 04 #69 rule 17: the SERVER's early-pay outcome per PENDING client notation (keyed by payment id),
   *  from ListPaymentsResponse.earlyPayOutcomes; the row states it and the confirm dialog pre-ticks from it. */
  const [earlyPayOutcomes, setEarlyPayOutcomes] = useState<Record<string, EarlyPayQuote>>({});
  /** 04 #69 rule 17: the confirm dialog for a client-reported payment on an invoice that carries terms
   *  (an invoice without terms keeps the one-click Confirm, owner ruling s201). */
  const [notationConfirm, setNotationConfirm] = useState<{
    payment: Payment; invoice: Invoice; dateReceived: string; receiptNumber: string;
    quote: EarlyPayQuote; quoteLoading: boolean; applyEarlyPay: boolean;
  } | null>(null);
  const [notationError, setNotationError] = useState<string | null>(null);
  const notationQuoteSeq = useRef(0);
  // Issued-invoice detail view (s77 #16): the invoice whose full breakdown + source links are shown.
  const [detailTarget, setDetailTarget] = useState<Invoice | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [paymentsOpen, setPaymentsOpen] = useState<Record<string, boolean>>({});
  const [voidTarget, setVoidTarget] = useState<Payment | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidReturned, setVoidReturned] = useState(false); // #68: NSF/returned vs a correction void
  const [voidError, setVoidError] = useState<string | null>(null);

  // Credits / refunds / write-offs (slice 7). A credit note and a refund both use a line grid where
  // the admin selects lines + the net to credit/reverse from each; the credit total (with the
  // proportional tax reversal) previews live. A fully-satisfying credit trips the release heads-up.
  // WS3 (s173, 08 Q59): `taxShare` (0..1) = the line's FROZEN taxed share of its net - 1 for a fully
  // taxable line, 0 for a non-taxable one, in between for an apportioned charge-class line. Every
  // cap and preview reads it through the ONE shared-ui helper (credit-math.ts); `taxable` stays for
  // display only. `partlyTaxable` is the FROZEN stamp's taxMode === 'apportioned', never taxShare < 1:
  // an ordinary taxable line under an invoice discount has a share below 1 and is not partly taxable.
  // 04 #149 commit 2: balanceFactor = how much balance one LIST dollar on this line consumes (its discount factor f, or 1 for a class line, plus the tax it carries) - the linear cap unit; the exact legs come from creditNotePreview.
  // 04 #160 WS-C M2b: `coversFrom` / `coversTo` ride a row pre-filled by the lapse dialog's refund branch and
  // land on the paired credit note's line (the days it settles, 02i T1.6); absent on a hand refund.
  type CreditLineRow = { lineId: string; description: string; net: number; originalNet: number; credited: number; taxable: boolean; taxShare: number; partlyTaxable: boolean; balanceFactor: number; discounted: boolean; sel: boolean; amount: string; coversFrom?: string; coversTo?: string };
  // 04 #93 (c) / 02d ruling 13 USE-TIME GUARD: entityCredit = the billing entity's available credit
  // balance at open; when > 0 the dialog states it and offers the settlement path BEFORE the note
  // is committed (the s99 misclick's re-diagnosis: the absence of the alternative was the defect).
  const [creditForm, setCreditForm] = useState<{ invoice: Invoice; lines: CreditLineRow[]; priors: CreditPriors; reason: string; entityCredit: number } | null>(null);
  const [creditError, setCreditError] = useState<string | null>(null);
  const [writeOffTarget, setWriteOffTarget] = useState<Invoice | null>(null);
  const [writeOffReason, setWriteOffReason] = useState('');
  const [writeOffAmount, setWriteOffAmount] = useState(''); // #66: partial write-off; prefilled to the full balance
  const [writeOffError, setWriteOffError] = useState<string | null>(null);
  // #67 bad-debt recovery: reinstate a written-off invoice (void its write-off → reopen to issued so a
  // payment can be recorded). No new endpoint — it voids the write-off adjustment, which already reopens.
  const [reinstateTarget, setReinstateTarget] = useState<Invoice | null>(null);
  const [reinstateReason, setReinstateReason] = useState('');
  const [reinstateError, setReinstateError] = useState<string | null>(null);
  // WS2: void an issued invoice (distinct from writing it off — a void reverses the invoice with no
  // bad-debt loss; a write-off books the loss). Distinct state from the payment void above.
  const [voidInvTarget, setVoidInvTarget] = useState<Invoice | null>(null);
  const [voidInvReason, setVoidInvReason] = useState('');
  const [voidInvError, setVoidInvError] = useState<string | null>(null);
  // 02 LOCKED s108, the same rule payError already follows: an error that renders below the fold reads
  // as "nothing happened". The void dialog never carried this ref, and 04 #109 lengthens its body with
  // a settled-fee heads-up, so the error is pinned into view whenever it appears. Declared HERE rather
  // than beside payErrorRef because the effect's dependency array is evaluated during render, so it
  // must sit after the state it names.
  const voidInvErrorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (voidInvError) voidInvErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [voidInvError]);
  // WS2: invoice-scoped "Resume services" — reactivate only the non-payment-suspended units a given
  // invoice suspended (used after writing off / clearing that invoice). Reuses the C endpoint.
  const [resumeInvTarget, setResumeInvTarget] = useState<Invoice | null>(null);
  const [resumeUnits, setResumeUnits] = useState<NonPaymentSuspendedUnit[]>([]);
  const [resumeSelected, setResumeSelected] = useState<Set<string>>(new Set());
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [refundForm, setRefundForm] = useState<{
    invoice: Invoice; amount: string; method: RefundMethod; reason: string; dateRefunded: string;
    reverseLines: boolean; lines: CreditLineRow[]; priors: CreditPriors;
    /** 04 #160 WS-C M2b: opened by the lapse dialog's paid-source branch - a backdrop click never dismisses
     *  it (the owner's s249 rule; Cancel stays the way out, and the credit is still owed and re-raises). */
    forCredits?: boolean;
  } | null>(null);
  const [refundError, setRefundError] = useState<string | null>(null);
  const [releaseHeadsUp, setReleaseHeadsUp] = useState<{ documents: WouldReleaseDocument[]; retry: () => Promise<void> } | null>(null);
  // 04 #109: the settled-linked-late-fee consent gate. Same shape as releaseHeadsUp above, including
  // the retry closure that re-submits with the confirm flag set.
  const [paidFeeHeadsUp, setPaidFeeHeadsUp] = useState<{ fees: PaidLateFeeHeadsUp[]; retry: () => Promise<void> } | null>(null);

  // The selected client's adjustments (or every client's on the All-invoices view), read for the row
  // pills, the detail modal's credit lines and the credit-note priors. 04 #93 slice (b): the client-scoped
  // Credits & refunds card that used to render this list is GONE from this page; its four actions moved
  // to the MONEY register (New client credit on its header; Void and the credit-first Apply in its
  // per-document dialog) and to the invoice detail modal below (Un-apply, beside each applied client
  // credit's applied date). The 04 #93(i) harm the card carried (document numbers clipped against
  // action pills) ends with it. Routes unchanged (02b #93 rule 2).
  const [adjustments, setAdjustments] = useState<CreditOrAdjustment[]>([]);
  // Collapsible groups: pending work open by default, settled/closed tucked away.
  const [groupOpen, setGroupOpen] = useState<Record<string, boolean>>({ drafts: true, pending: true, paid: false, closed: false });

  // The §7.11 "Add unbilled items" picker (slice 3).
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerData, setPickerData] = useState<UnbilledItemsResponse | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerSelected, setPickerSelected] = useState<Set<string>>(new Set());
  /** Gate messages for the picker button (e.g. "select a client first") — shown AT the button,
   *  not in the footer error slot (owner UX fix, s72). */
  const [pickerGateError, setPickerGateError] = useState<string | null>(null);
  /** The draft auto-created by opening the picker on a NEW invoice. If the admin then Cancels
   *  without an explicit Save/Issue, this draft is DISCARDED (deleted — which releases its pulled
   *  sources server-side) rather than silently kept (owner UX fix, s72). */
  const [autoDraftId, setAutoDraftId] = useState<string | null>(null);
  /** s72 owner UX: the transient save confirmation (SaveFlash) — in-memory only, so it shows
   *  exactly once and never survives a refresh. (The earlier glow-and-scroll variant was rolled
   *  back same-session by owner call: the list jump felt more confusing than helpful.) */
  const [flash, setFlash] = useState<string | null>(null);
  /** s72 year-selector convention: shown only when invoices span multiple display years
   *  (issueDate; December keeps counting as the current year through January). */
  const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());
  // 04 #71 entity-aware list. listScope echoes what the server applied; entityFilter narrows a
  // rolled-up business view to ONE linked account (set by the dropdown or by clicking a badge);
  // openReceivable carries the server's per-invoice AR amounts so the owed total is never derived here.
  const [listScope, setListScope] = useState<InvoiceListScope | null>(null);
  const [entityFilter, setEntityFilter] = useState('');
  const [openReceivable, setOpenReceivable] = useState<Record<string, number>>({});
  /** Slice 4: the scoped client's chain-resolved tax (server-shipped on the list response) —
   *  prefills the editor's tax controls; the description renders as the provenance hint. */
  const [resolvedTax, setResolvedTax] = useState<ResolvedTaxInfo | null>(null);
  /** 04 #169 (b): the resolver's own answer for the OPEN FORM's client ON ITS ISSUE DATE (the dry-run
   *  `mgmt-invoices?action=resolve-tax`, nothing written). The list's `resolvedTax` above is resolved as
   *  of today, so it previewed a certificate as exempting on a date past its expiry (the s241 V7b
   *  finding); the gate, the create-form prefill and the provenance hint read THIS whenever its key
   *  matches the form (the key = client | issue date), and fall back to the older sources until it
   *  answers. The date rule is never restated here (02 s97): the server's exemptionInForce decides. */
  const [previewTax, setPreviewTax] = useState<{ key: string; info: ResolvedTaxInfo } | null>(null);
  /** Slice 4: the "Compose monthly invoice" dialog. month = 'YYYY-MM'; pull seeds from the
   *  brand's composeAutoPullUnbilled setting (per-occasion override); force = the confirmed
   *  re-compose after a month_already_composed 409. */
  const [composeForm, setComposeForm] = useState<{ clientId: string; month: string; pull: boolean; force: boolean } | null>(null);
  const [composeError, setComposeError] = useState<string | null>(null);
  const [composeWarnings, setComposeWarnings] = useState<string[]>([]);

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);
  const businessById = useMemo(() => new Map(businesses.map((b) => [b.id, b])), [businesses]);
  /** 04 #60 WS2: the charge-class catalog of a client's brand (the editor resolves class rows and
   *  seeds the autoApply set from it; the server is still the authority on every save). */
  const brandForClient = (cid: string): Business | undefined => {
    const c = clientById.get(cid);
    return c ? businessById.get(c.primaryBusinessId) : undefined;
  };
  /** WS2b: the brand's classes that may be applied to a NEW draft today (effectively active). */
  const activeClassesForClient = (cid: string): ChargeClass[] => {
    const b = brandForClient(cid);
    return catalogOf(b).filter((c) => classActiveNow(c, b));
  };
  const brandForInvoice = (inv: Invoice): Business | undefined => businessById.get(inv.businessId);
  /** 04 #136 THE BRAND CLOCK (02 s177): the timezone a date for this record is taken in. The OWNING
   *  brand's when known (its own `timezone`, absent = the platform default); with no owning brand
   *  (no client picked yet, or brands still loading), the PORTAL brand's. */
  const tzOf = (b: Business | undefined): string | null => (b ? b.timezone ?? null : portalBrand?.timezone ?? null);
  /** 04 #60 WS4: the brand's classes a VOLUNTARY tip may be named through today - effectively active
   *  (04 #125 rule 10, WS-A s191: a collected-on-behalf class is offered too - the tip is then owed to
   *  that class's payee, 241000 Tips payable, rather than kept as income; the option says so). */
  const tipClassesFor = (b: Business | undefined): ChargeClass[] =>
    catalogOf(b).filter((c) => classActiveNow(c, b));
  /** The tip picker's option label: a collected-on-behalf class is marked, so "Book the tip to" never
   *  reads as naming an income account when it names a liability owed to someone else. */
  const tipClassLabel = (c: ChargeClass): string => (c.treatment === 'pass-through' ? `${c.name} (collected on behalf)` : c.name);
  /** 04 #60 WS4: whether a dialog offers the Tip field at all (the brand's `gratuityEnabled`). */
  const tipsOn = (b: Business | undefined): boolean => b?.gratuityEnabled === true;

  /** Convenience-fee rate + tax defaults for a client (per-client override wins over the brand default, §1.4). */
  const billingConfigFor = (c: Client | null | undefined): { convenienceFeeRate: number; taxEnabled: boolean; taxRate: number } => {
    const b = c ? businessById.get(c.primaryBusinessId) : undefined;
    // The convenience fee enable is brand-level (s77 #14); when off, the preview carries no fee (rate 0).
    const convenienceFeeEnabled = b?.convenienceFeeEnabled !== false;
    return {
      convenienceFeeRate: convenienceFeeEnabled ? (c?.convenienceFeeRateOverride ?? b?.convenienceFeeRate ?? 0) : 0,
      taxEnabled: c?.taxEnabledOverride ?? b?.taxEnabledDefault ?? false,
      taxRate: c?.taxRateOverride ?? b?.taxRateDefault ?? 0,
    };
  };

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [c, b] = await Promise.all([api.admin.listClients(), api.admin.listBusinesses()]);
        if (!alive) return;
        setClients(c.clients.map((r) => r.client)); // listClients returns enriched rows { client, ... }
        setBusinesses(b.businesses);
      } catch (e) {
        if (alive) setError(apiErrorMessage(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const load = async (cid: string): Promise<void> => {
    setListLoading(true);
    setError(null);
    try {
      // 04 #71: a client-scoped list asks for the ENTITY scope (the union of this client's own
      // partition and everything billed to it). The all-clients overview already spans everything.
      const res = await api.admin.listInvoices(cid || undefined, cid ? 'entity' : undefined);
      setInvoices(res.invoices);
      setListScope(res.scope ?? null);
      setOpenReceivable(res.openReceivableByInvoiceId ?? {});
      setResolvedTax(res.resolvedTax ?? null);
      setPendingNotationSet(new Set(res.pendingNotationInvoiceIds ?? []));
      // Load adjustments for the selected client, or across ALL clients on the All-invoices view, so
      // refund/credit info renders correctly everywhere (the client's available credit balance is read
      // where it is spent: the settlement dialog and the Money register).
      try {
        const adj = await api.admin.listAdjustments(cid || undefined);
        setAdjustments(adj.adjustments);
      } catch {
        setAdjustments([]);
      }
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    // 04 #71: a linked-account narrowing belongs to the client it was set on, so drop it on change.
    setEntityFilter('');
    void load(clientId);
  }, [clientId]);

  const update = (patch: Partial<FormState>): void => setForm((f) => (f ? { ...f, ...patch } : f));

  // Billing type drives line kind: a One-off invoice never recurs, so a "Recurring" line is
  // meaningless on it. Switching to One-off coerces any recurring lines back to One-time, and the
  // Line-kind selector only offers "Recurring" while the invoice is Monthly. (Automated monthly
  // billing itself arrives at Stage 5; today billingType/kind are classification only.)
  const setBillingType = (billingType: 'monthly' | 'one-off'): void =>
    setForm((f) =>
      f
        ? {
            ...f,
            billingType,
            lines: billingType === 'one-off' ? f.lines.map((l) => ({ ...l, kind: 'one-time' as LineItemKind })) : f.lines,
          }
        : f,
    );

  // Pick the client inside the create form; re-derive that client's tax default. Create only — an
  // issued/existing invoice's client is immutable (the partition key, §7.15).
  const setFormClient = (id: string): void => {
    setPickerGateError(null);
    setForm((f) => {
      if (!f) return f;
      const c = billingConfigFor(clientById.get(id));
      // Immediate simple prefill; the chain-accurate rate follows from the server below.
      // 04 #136: an untouched issue-date prefill follows the new client's brand clock (rebaseDatePrefill
      // keeps any date the admin typed), the taxTouched precedent above.
      const issueDate = rebaseDatePrefill(f.issueDate, tzOf(brandForClient(f.clientId)), tzOf(brandForClient(id)));
      return { ...f, clientId: id, issueDate, taxTouched: false, taxEnabled: c.taxEnabled, taxRate: taxRatePct(c.taxRate) };
    });
    // The chain-accurate prefill follows from the dry-run effect below (keyed on the client AND the
    // issue date), which replaces the second list fetch this used to make for the picked client.
  };

  // 04 #169 (b): ask the resolver for the open form's client on the form's ISSUE DATE, and again whenever
  // either changes. The answer feeds the registration / exemption gate (both forms), the create form's
  // untouched Charge-tax prefill and its provenance hint. Debounced so a date typed digit by digit asks
  // once; keyed so a late answer for an older (client, date) is dropped. Best-effort: a failure leaves
  // the older sources in place (the server still decides at save).
  const previewClientId = editing ? editing.clientId : form?.clientId ?? '';
  const previewIssueDate = form?.issueDate ?? '';
  useEffect(() => {
    if (!formOpen || !previewClientId || !/^\d{4}-\d{2}-\d{2}$/.test(previewIssueDate)) return;
    const key = `${previewClientId}|${previewIssueDate}`;
    let alive = true;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await api.admin.resolveInvoiceTax(previewClientId, previewIssueDate);
          if (!alive) return;
          const rt = res.resolvedTax;
          setPreviewTax({ key, info: rt });
          // The create form's untouched tax controls defer to the server (the slice-4 rule): prefill
          // them from the dated resolution, exactly as the list's today-resolution used to.
          if (!editing) {
            setForm((f) =>
              f && f.clientId === previewClientId && f.issueDate === previewIssueDate && !f.taxTouched
                ? { ...f, taxEnabled: rt.enabled, taxRate: taxRatePct(rt.rate) }
                : f,
            );
          }
        } catch {
          /* best-effort: the list resolution / the draft's stored stamp stands */
        }
      })();
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, previewClientId, previewIssueDate, editing?.id]);
  const addLine = (): void => setForm((f) => (f ? { ...f, lines: [...f.lines, emptyLine()] } : f));
  const updateLine = (idx: number, patch: Partial<LineRow>): void =>
    setForm((f) => (f ? { ...f, lines: f.lines.map((r, i) => (i === idx ? { ...r, ...patch } : r)) } : f));
  const removeLine = (idx: number): void =>
    setForm((f) => (f ? { ...f, lines: f.lines.filter((_, i) => i !== idx) } : f));
  /** 04 #60 WS2: apply a brand charge class to the open draft as its own row (at most one per
   *  class; the server enforces the same rule). */
  const applyChargeClass = (cls: ChargeClass): void =>
    setForm((f) => (f && !f.lines.some((r) => r.chargeClassId === cls.id) ? { ...f, lines: [...f.lines, chargeClassRow(cls, true)] } : f));
  /** Replace a (deactivated) class row with another active class, in place: the new row is a NEW
   *  line (no lineId echo - the server never swaps a class through an echo), so the old one is
   *  removed on save and the replacement applied. */
  const replaceChargeClass = (idx: number, cls: ChargeClass): void =>
    setForm((f) => (f ? { ...f, lines: f.lines.map((r, i) => (i === idx ? chargeClassRow(cls, true) : r)) } : f));
  /** 04 #60 WS2b: "charge it anyway" for a deactivated class on this draft; the next save stamps the
   *  per-line acknowledgement so issue never asks about it again (option 2 of the 02g set). */
  const acknowledgeChargeClass = (idx: number, ack: boolean): void =>
    setForm((f) => (f ? { ...f, lines: f.lines.map((r, i) => (i === idx ? { ...r, chargeClassAcknowledged: ack } : r)) } : f));
  /** Convert a class row into a plain ORDINARY one-time line at its current derived amount. The GL
   *  cost is stated on screen before the admin picks this: the amount books to Service Revenue,
   *  becomes discountable, and follows the line's own taxable flag. */
  const convertChargeClassRow = (idx: number, amount: number): void =>
    setForm((f) =>
      f
        ? {
            ...f,
            lines: f.lines.map((r, i) =>
              i === idx
                ? { description: r.description, qty: '1', cost: '', charge: String(round2(amount)), kind: 'one-time' as const, taxable: r.taxable, taxMode: 'rule' as const, taxCategory: 'service.professional' as const, discType: 'none' as const, discValue: '' }
                : r,
            ),
          }
        : f,
    );

  const openCreate = (): void => {
    const cfg = billingConfigFor(clientById.get(clientId));
    // Prefer the server's chain resolution for the scoped client (slice 4); fall back to the
    // simple client/brand derivation while none is loaded.
    const rt = clientId && resolvedTax ? resolvedTax : null;
    // 04 #60 WS2: seed the brand's active autoApply classes so the admin SEES them before the draft
    // exists and can remove one; the create call then sends autoApplyChargeClasses: false.
    const seeded = activeClassesForClient(clientId).filter((c) => c.autoApply).map((c) => chargeClassRow(c, true));
    setEditing(null);
    setForm({
      clientId,
      billingType: 'one-off',
      issueDate: brandLocalDate(tzOf(brandForClient(clientId))),
      paymentTerms: 'net30',
      earlyPayMode: '',
      earlyPayRatePct: '',
      earlyPayDays: '',
      lines: [emptyLine(), ...seeded],
      discountType: 'none',
      discountValue: '',
      taxEnabled: rt ? rt.enabled : cfg.taxEnabled,
      taxRate: rt ? taxRatePct(rt.rate) : taxRatePct(cfg.taxRate),
      taxTouched: false,
      notes: '',
    });
    setFormError(null);
    setPickerGateError(null);
    setAutoDraftId(null);
    setFormOpen(true);
    setOpenSeq((n) => n + 1);
  };

  useEffect(() => {
    if (formOpen && form) formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen, openSeq]);
  const openEdit = (inv: Invoice): void => {
    setEditing(inv);
    setForm({
      clientId: inv.clientId,
      billingType: inv.billingType,
      issueDate: inv.issueDate,
      paymentTerms: inv.paymentTerms,
      // 04 #69: a draft's earlyPayDiscount holds only the per-invoice override (rate 0 = none here).
      earlyPayMode: inv.earlyPayDiscount ? (inv.earlyPayDiscount.rate > 0 ? 'custom' : 'none') : '',
      earlyPayRatePct: inv.earlyPayDiscount && inv.earlyPayDiscount.rate > 0 ? pctLabel(inv.earlyPayDiscount.rate) : '',
      earlyPayDays: inv.earlyPayDiscount && inv.earlyPayDiscount.rate > 0 ? String(inv.earlyPayDiscount.days) : '',
      lines: rowsFromInvoice(inv, brandForInvoice(inv)),
      discountType: inv.discount ? inv.discount.type : 'none',
      discountValue: inv.discount ? String(inv.discount.type === 'percent' ? round2(inv.discount.value * 100) : inv.discount.value) : '',
      taxEnabled: inv.tax.enabled,
      taxRate: taxRatePct(inv.tax.rate),
      taxTouched: false,
      notes: inv.notes ?? '',
    });
    setFormError(null);
    setPickerGateError(null);
    setAutoDraftId(null);
    setFormOpen(true);
    setOpenSeq((n) => n + 1);
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setForm(null);
    setEditing(null);
    setFormError(null);
    setPickerGateError(null);
  };

  /** Cancel from the editor (s72): if the ONLY reason a draft exists is that the picker
   *  auto-saved it (opened on a NEW invoice) and the admin never explicitly saved or issued,
   *  discard it — the server-side draft delete releases its pulled sources back to unbilled. */
  const cancelForm = (): void => {
    const discard =
      autoDraftId && editing && editing.id === autoDraftId && editing.state === 'draft' ? editing : null;
    setAutoDraftId(null);
    closeForm();
    if (discard) {
      void (async () => {
        try {
          await api.admin.deleteInvoice(discard.id, discard.clientId);
        } catch {
          /* best-effort: a leftover draft is visible in Drafts and deletable by hand */
        }
        await load(clientId);
      })();
    }
  };

  /** Parse the form into the API line/discount/tax shapes, or return an error string. Tax is
   *  returned ONLY when the admin touched the tax controls (slice 4) — untouched, the save omits
   *  it and the server resolves the Fork-2/Q47 chain. */
  function buildPayload(f: FormState): { lineItems: InvoiceLineInput[]; discount: { type: DiscountType; value: number } | null; tax?: { enabled: boolean; rate: number } } | { error: string } {
    const lineItems: InvoiceLineInput[] = [];
    for (let i = 0; i < f.lines.length; i++) {
      const r = f.lines[i];
      if (r.chargeClassId) {
        // A charge-class row carries no typed money (04 #60 WS2): the server derives the amount.
        lineItems.push({
          ...(r.lineId ? { lineId: r.lineId, chargeClassAcknowledged: r.chargeClassAcknowledged === true } : {}),
          chargeClassId: r.chargeClassId,
          description: r.description.trim(),
          charge: 0,
        });
        continue;
      }
      if (r.creditReason && r.lineId) {
        // 04 #45 O5: a composed credit row is echoed by id only; the server keeps it as composed.
        lineItems.push({ lineId: r.lineId, kind: 'recurring', description: r.description, charge: 0 });
        continue;
      }
      if (!r.description.trim()) return { error: `Line ${i + 1}: a description is required.` };
      const qty = parseFloat(r.qty);
      if (!Number.isFinite(qty) || qty <= 0) return { error: `Line ${i + 1}: quantity must be a positive number.` };
      const cost = r.cost.trim() ? parseFloat(r.cost) : 0;
      if (!Number.isFinite(cost) || cost < 0) return { error: `Line ${i + 1}: cost must be a non-negative amount (or blank).` };
      const charge = r.charge.trim() ? parseFloat(r.charge) : NaN;
      if (!Number.isFinite(charge) || charge < 0) return { error: `Line ${i + 1}: a non-negative charge is required.` };
      let discount: { type: DiscountType; value: number } | null = null;
      if (r.discType !== 'none') {
        const raw = parseFloat(r.discValue);
        if (!Number.isFinite(raw) || raw < 0) return { error: `Line ${i + 1}: the line discount must be a non-negative number.` };
        if (r.discType === 'percent') {
          if (raw > 100) return { error: `Line ${i + 1}: a percent line discount cannot exceed 100%.` };
          discount = { type: 'percent', value: round2(raw) / 100 };
        } else {
          discount = { type: 'flat', value: round2(raw) };
        }
      }
      // lineId is echoed so pulled lines keep their sourceRef (the server ignores a pulled row's
      // qty/cost/kind — those are source-authoritative) and manual lines keep a stable identity.
      // 04 #160 WS-C M1 (the override contract): `taxable` rides ONLY when the checkbox was touched
      // on this edit, `useTaxRule` when "Use the rule" was clicked, and the category on every manual
      // or pulled row (the server ignores it on a composed service / package line).
      lineItems.push({
        ...(r.lineId ? { lineId: r.lineId } : {}),
        kind: r.kind,
        description: r.description.trim(),
        qty: round2(qty),
        cost: round2(cost),
        charge: round2(charge),
        ...(r.taxTouched ? { taxable: r.taxable } : {}),
        ...(r.useTaxRule ? { useTaxRule: true } : {}),
        ...(r.taxCategory ? { taxCategory: r.taxCategory } : {}),
        discount,
      });
    }

    let discount: { type: DiscountType; value: number } | null = null;
    if (f.discountType !== 'none') {
      const raw = parseFloat(f.discountValue);
      if (!Number.isFinite(raw) || raw < 0) return { error: 'The discount amount must be a non-negative number.' };
      if (f.discountType === 'percent') {
        if (raw > 100) return { error: 'A percent discount cannot exceed 100%.' };
        discount = { type: 'percent', value: round2(raw) / 100 }; // percent entered → rate (10 → 0.10)
      } else {
        discount = { type: 'flat', value: round2(raw) };
      }
    }

    if (!f.taxTouched) return { lineItems, discount }; // untouched → the server resolves the chain

    let taxRate = 0;
    if (f.taxEnabled) {
      taxRate = f.taxRate.trim() ? parseFloat(f.taxRate) : NaN;
      if (!Number.isFinite(taxRate) || taxRate < 0 || taxRate > 100) return { error: 'The tax rate must be a percentage between 0 and 100.' };
      taxRate = taxRate / 100; // percent → rate; NEVER round the rate (s75) — only the tax AMOUNT rounds
    }
    return { lineItems, discount, tax: { enabled: f.taxEnabled, rate: taxRate } };
  }

  /** Save the form. issue=false → create/save a DRAFT. issue=true (create only) → create the draft
   *  then immediately issue it; if the issue step fails the draft STAYS created and the error shows
   *  at the page level (fix it and issue from its row). */
  const submit = async (issue: boolean): Promise<void> => {
    if (!form) return;
    const targetClientId = editing ? editing.clientId : form.clientId;
    if (!targetClientId) return setFormError('Select a client before creating an invoice.');
    if (!form.issueDate) return setFormError('An issue date is required.');
    const payload = buildPayload(form);
    if ('error' in payload) return setFormError(payload.error);
    if (issue && payload.lineItems.length === 0) return setFormError('Add at least one line item before issuing.');

    const common = {
      billingType: form.billingType,
      issueDate: form.issueDate,
      paymentTerms: form.paymentTerms,
      earlyPayDiscount: builtDraftEarlyPay(form), // 04 #69: the per-invoice override (null = inherit)
      lineItems: payload.lineItems,
      discount: payload.discount,
      ...(payload.tax ? { tax: payload.tax } : {}),
      notes: form.notes.trim() ? form.notes.trim() : null,
    };

    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        const body: UpdateInvoiceRequest = common;
        const saved = (await api.admin.updateInvoice(editing.id, targetClientId, body)).invoice;
        setAutoDraftId(null); // explicitly saved — keep it
        closeForm();
        await load(clientId);
        if (issue) {
          // "Save and issue" (owner rule, s167): the draft is saved first, then the SAME issue
          // confirmation the list uses opens for it, so issuing from the editor and from the row
          // are one flow with one guard path.
          setIssueTarget(saved);
          return;
        }
        setFlash('Draft saved.');
        return;
      }
      const body: CreateInvoiceRequest = { ...common, clientId: targetClientId, autoApplyChargeClasses: false }; // the editor seeded them (04 #60 WS2)
      const createdInv = (await api.admin.createInvoice(body)).invoice;
      setAutoDraftId(null);
      if (!issue) {
        closeForm();
        await load(clientId);
        setFlash('Draft created.');
        return;
      }
      // Create-then-issue in ONE sequence with the form still open + buttons disabled (s77 #5) — no
      // intermediate close/reload, so the draft never flashes in the list and a second click can't
      // race the issue into a false "only a draft can be issued".
      try {
        await api.admin.issueInvoice(createdInv.id, createdInv.clientId);
        closeForm();
        await load(clientId);
        setFlash('Invoice created and issued.');
      } catch (e) {
        closeForm();
        await load(clientId);
        setError(`The draft was created but could not be issued: ${apiErrorMessage(e)} It is in the list below; fix it and issue it from its row.`);
      }
    } catch (e) {
      setFormError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /** Save the open form as a DRAFT (create or update) WITHOUT closing the editor, and return the
   *  stored invoice — the picker's pull is a server op, so the draft must exist and be current. */
  const ensureDraftSaved = async (): Promise<Invoice | null> => {
    if (!form) return null;
    const targetClientId = editing ? editing.clientId : form.clientId;
    if (!targetClientId) {
      setPickerGateError('Select a client before pulling unbilled items.');
      return null;
    }
    if (!form.issueDate) {
      setPickerGateError('Set an issue date before pulling unbilled items.');
      return null;
    }
    // A brand-new form starts with one empty row — drop untouched rows so "Add unbilled items"
    // works before any manual line is typed (a draft may be created with zero lines).
    const cleaned = { ...form, lines: form.lines.filter((r) => r.chargeClassId || r.description.trim() || r.charge.trim() || r.cost.trim()) };
    const payload = buildPayload(cleaned);
    if ('error' in payload) {
      setFormError(payload.error);
      return null;
    }
    const common = {
      billingType: form.billingType,
      issueDate: form.issueDate,
      paymentTerms: form.paymentTerms,
      earlyPayDiscount: builtDraftEarlyPay(form), // 04 #69: the per-invoice override (null = inherit)
      lineItems: payload.lineItems,
      discount: payload.discount,
      ...(payload.tax ? { tax: payload.tax } : {}),
      notes: form.notes.trim() ? form.notes.trim() : null,
    };
    setSaving(true);
    setFormError(null);
    try {
      const res = editing
        ? await api.admin.updateInvoice(editing.id, targetClientId, common as UpdateInvoiceRequest)
        : await api.admin.createInvoice({ ...common, clientId: targetClientId, autoApplyChargeClasses: false } as CreateInvoiceRequest);
      if (!editing) setAutoDraftId(res.invoice.id); // picker-created, discarded on Cancel unless explicitly saved
      setEditing(res.invoice);
      setForm((f) => (f ? { ...f, lines: rowsFromInvoice(res.invoice, brandForInvoice(res.invoice)) } : f)); // sync server line ids
      return res.invoice;
    } catch (e) {
      setFormError(apiErrorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const pickerKey = (it: UnbilledPickerItem): string => `${it.sourceType}:${it.sourceId}:${it.lineId ?? ''}`;

  /** Open the §7.11 picker: save the draft first, then load the client's unbilled set. */
  const openPicker = async (): Promise<void> => {
    setPickerGateError(null);
    const inv = await ensureDraftSaved();
    if (!inv) return;
    setPickerOpen(true);
    setPickerData(null);
    setPickerError(null);
    setPickerSelected(new Set());
    try {
      const res = await api.admin.listUnbilled(inv.clientId, inv.projectId ?? undefined);
      setPickerData(res);
    } catch (e) {
      setPickerError(apiErrorMessage(e));
    }
  };

  const togglePickerItem = (it: UnbilledPickerItem): void =>
    setPickerSelected((s) => {
      const next = new Set(s);
      const k = pickerKey(it);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const togglePickerSection = (items: UnbilledPickerItem[]): void =>
    setPickerSelected((s) => {
      const next = new Set(s);
      const allIn = items.every((it) => next.has(pickerKey(it)));
      for (const it of items) {
        if (allIn) next.delete(pickerKey(it));
        else next.add(pickerKey(it));
      }
      return next;
    });

  const closePicker = (): void => {
    setPickerOpen(false);
    setPickerData(null);
    setPickerError(null);
    setPickerSelected(new Set());
  };

  /** Pull the selected items onto the draft. Sources are marked billed by the server at this
   *  moment; the editor re-syncs its rows from the returned invoice. */
  const doPull = async (): Promise<void> => {
    if (!editing || !pickerData) return;
    const all = [...pickerData.time, ...pickerData.mileage, ...pickerData.expenses];
    const items: PullItemRef[] = all
      .filter((it) => pickerSelected.has(pickerKey(it)))
      .map((it) => ({ sourceType: it.sourceType, sourceId: it.sourceId, ...(it.lineId ? { lineId: it.lineId } : {}) }));
    if (items.length === 0) return setPickerError('Select at least one item to add.');
    setPickerBusy(true);
    setPickerError(null);
    try {
      const res = await api.admin.pullUnbilled(editing.id, editing.clientId, items);
      setEditing(res.invoice);
      setForm((f) => (f ? { ...f, lines: rowsFromInvoice(res.invoice, brandForInvoice(res.invoice)) } : f));
      closePicker();
      await load(clientId);
    } catch (e) {
      setPickerError(apiErrorMessage(e));
    } finally {
      setPickerBusy(false);
    }
  };

  /** Open the slice-4 compose dialog. The dialog carries its OWN client picker (s75 fix 4) so it's
   *  always available without scoping the list first; it seeds from the top scope when one is set.
   *  The auto-pull checkbox seeds from the chosen client's brand setting; the month defaults to now. */
  const openCompose = (): void => {
    const c = clientById.get(clientId);
    const b = c ? businessById.get(c.primaryBusinessId) : undefined;
    setComposeError(null);
    setComposeWarnings([]);
    setComposeForm({
      clientId,
      month: brandLocalDate(tzOf(b)).slice(0, 7),
      pull: b?.composeAutoPullUnbilled ?? false,
      force: false,
    });
  };

  /** Picking a client inside the compose dialog re-seeds the auto-pull default from THAT client's
   *  brand and clears any pending force confirmation. */
  const setComposeClient = (id: string): void => {
    const c = clientById.get(id);
    const b = c ? businessById.get(c.primaryBusinessId) : undefined;
    setComposeError(null);
    setComposeForm((f) =>
      f
        ? { ...f, clientId: id, month: rebaseDatePrefill(f.month, tzOf(brandForClient(f.clientId)), tzOf(b)), pull: b?.composeAutoPullUnbilled ?? false, force: false }
        : f,
    );
  };

  const doCompose = async (): Promise<void> => {
    if (!composeForm) return;
    if (!composeForm.clientId) return setComposeError('Pick a client.');
    if (!/^\d{4}-\d{2}$/.test(composeForm.month)) return setComposeError('Pick a month.');
    setBusy(true);
    setComposeError(null);
    try {
      const res = await api.admin.composeMonthlyInvoice({
        clientId: composeForm.clientId,
        period: composeForm.month,
        pullUnbilled: composeForm.pull,
        ...(composeForm.force ? { force: true } : {}),
      });
      const composedClientId = composeForm.clientId;
      setComposeForm(null);
      setComposeWarnings(res.warnings);
      setClientId(composedClientId);
      await load(composedClientId);
      openEdit(res.invoice);
      setFlash(
        `Monthly draft composed${res.pulledCount > 0 ? ` (${res.pulledCount} unbilled item${res.pulledCount === 1 ? '' : 's'} pulled in)` : ''}.`,
      );
    } catch (e) {
      const msg = apiErrorMessage(e);
      // 04 #160 WS-C M2b (DP-13): nothing_to_compose with credit owed carries the lapse dialog's read for
      // that month; open it in place of the error (closing it keeps carrying, exactly as before).
      const pending = pendingCreditsFromError(e);
      if (pending) {
        setComposeForm(null);
        setPendingTrigger('compose');
        setPendingCredits(pending);
        return;
      }
      // The F2 guard: surface the confirm-and-force path in place.
      if (msg.includes('already exists')) {
        setComposeForm((f) => (f ? { ...f, force: true } : f));
        setComposeError(`${msg} Confirming again will compose a second invoice for the month.`);
      } else {
        setComposeError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const pendingCreditsFromError = (err: unknown): PendingCreditsResponse | null => {
    if (err instanceof ApiError) {
      const d = err.details as { error?: { code?: string; details?: { pendingCredits?: PendingCreditsResponse } } } | null | undefined;
      if (d?.error?.code === 'nothing_to_compose' && d.error.details?.pendingCredits) return d.error.details.pendingCredits;
    }
    return null;
  };

  /** The on-demand read (the "Pending credits" pill): the dialog opens even when nothing is owed, so the
   *  client's lapsed credits can be reviewed and voided there. */
  const openPendingCredits = async (): Promise<void> => {
    if (!clientId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.admin.pendingCredits(clientId);
      setPendingTrigger('manual');
      setPendingCredits(res);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** The paid-source branch: open the refund dialog in "return part of the invoice" mode with the
   *  entitlement's lines pre-filled (list units; each capped at the line's remaining net) and a reason. */
  const openRefundPrefilled = async (inv: Invoice, prefill: Array<{ lineId: string; amount: number; coversFrom?: string; coversTo?: string }>, reason: string): Promise<void> => {
    setRefundError(null);
    const fresh = await freshCreditRows(inv);
    const lines = fresh.lines.map((r) => {
      const p = prefill.find((x) => x.lineId === r.lineId);
      return p && p.amount > 0
        ? { ...r, sel: true, amount: round2(Math.min(p.amount, r.net)).toFixed(2), ...(p.coversFrom && p.coversTo ? { coversFrom: p.coversFrom, coversTo: p.coversTo } : {}) }
        : r;
    });
    setRefundForm({
      invoice: inv,
      amount: '',
      method: 'zelle',
      reason,
      dateRefunded: brandLocalDate(tzOf(brandForInvoice(inv))),
      reverseLines: true,
      lines,
      priors: fresh.priors,
      forCredits: true,
    });
    if (!lines.some((r) => r.sel)) setRefundError('The credited line has nothing left to return (it was already credited or refunded); the credit is still owed and carries.');
  };

  const openRefundForCredits = async (credits: PendingCredit[], cid: string): Promise<void> => {
    if (credits.length === 0) return;
    try {
      const res = await api.admin.getInvoice(credits[0].source.invoiceId, cid);
      await openRefundPrefilled(res.invoice, refundPrefill(credits), `Credit for days not in service (${credits.map((c) => `${c.unit} ${c.coversFrom} to ${c.coversTo}`).join('; ')})`);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  // Deep link from "Generate invoice" on the Projects page: /admin/invoices?client=<id>&open=<invId>
  // scopes the list to the client and opens the generated draft in the editor. A §7.8 payment deep
  // link (&payments=1, from the reported-payment email/notice) instead opens that invoice's Payments
  // panel and flashes it, so the admin lands right where they confirm or reject.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const cid = p.get('client');
    const open = p.get('open');
    const wantsPayments = p.get('payments') === '1';
    const wantsDetail = p.get('detail') === '1';
    const refundParam = p.get('refund');
    if (!cid && !open) return;
    if (cid) setClientId(cid);
    // Consume-once (s72, owner-refined): strip params from the address bar — the client filter lives on
    // in state for this visit, and a browser refresh lands on the plain list.
    const url = new URL(window.location.href);
    url.searchParams.delete('open');
    url.searchParams.delete('client');
    url.searchParams.delete('payments');
    url.searchParams.delete('detail');
    url.searchParams.delete('refund');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    if (!open || !cid) return;
    if (refundParam) {
      // 04 #160 WS-C M2b: the lapse dialog's paid-source branch from another page - defer to the effect
      // below (waits for the list to load), then open the refund dialog pre-filled.
      const ymd = /^\d{4}-\d{2}-\d{2}$/;
      type RefundPrefillLine = { lineId: string; amount: number; coversFrom?: string; coversTo?: string };
      const lines: RefundPrefillLine[] = [];
      for (const part of refundParam.split(',')) {
        const [lineId, amount, from, to] = part.split(':');
        if (!lineId || amount === undefined) continue;
        const entry: RefundPrefillLine = { lineId: decodeURIComponent(lineId), amount: round2(parseFloat(amount) || 0) };
        if (from && to && ymd.test(from) && ymd.test(to) && from <= to) {
          entry.coversFrom = from;
          entry.coversTo = to;
        }
        if (entry.amount > 0) lines.push(entry);
      }
      if (lines.length > 0) setDeepLinkRefundFor({ invoiceId: open, lines });
      return;
    }
    if (wantsPayments) {
      // Issued invoice with a reported payment — defer to the effect below (waits for the list to load).
      setDeepLinkPaymentsFor(open);
      return;
    }
    if (wantsDetail) {
      // 04 #93 (b): an adjustment row on the Money register links to the invoice it names; open its detail.
      setDeepLinkDetailFor(open);
      return;
    }
    void (async () => {
      try {
        const res = await api.admin.getInvoice(open, cid);
        if (res.invoice.state === 'draft') openEdit(res.invoice);
      } catch {
        /* a stale deep link just lands on the list */
      }
    })();
    // Re-run on every navigation (location.key changes even for same-path links), so a deep link from
    // the notification center works while already on /admin/invoices — not just on a fresh page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // §7.8: once the (deep-linked) invoice is in the loaded list, open its Payments panel, expand the
  // Awaiting-payment group, scroll to it, and flash it briefly.
  useEffect(() => {
    if (!deepLinkPaymentsFor) return;
    const inv = invoices.find((i) => i.id === deepLinkPaymentsFor);
    if (!inv) return; // not loaded yet (list still fetching, or a stale/other-client id)
    setGroupOpen((m) => ({ ...m, pending: true, paid: true }));
    setPaymentsOpen((m) => ({ ...m, [inv.id]: true }));
    void refreshPayments(inv).catch(() => undefined);
    setFlashInvoiceId(inv.id);
    setDeepLinkPaymentsFor(null);
    const t1 = setTimeout(() => document.getElementById(`inv-${inv.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80);
    const t2 = setTimeout(() => setFlashInvoiceId(null), 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkPaymentsFor, invoices]);

  // 04 #160 WS-C M2b: once the deep-linked (paid) invoice is in the loaded list, open the refund dialog
  // pre-filled with the credited lines, expand its group and flash the card behind the dialog.
  useEffect(() => {
    if (!deepLinkRefundFor) return;
    const inv = invoices.find((i) => i.id === deepLinkRefundFor.invoiceId);
    if (!inv) return; // not loaded yet (list still fetching, or a stale/other-client id)
    setDeepLinkRefundFor(null);
    setGroupOpen((m) => ({ ...m, pending: true, paid: true }));
    setFlashInvoiceId(inv.id);
    void openRefundPrefilled(inv, deepLinkRefundFor.lines, 'Credit for days not in service (lapse dialog)');
    const t2 = setTimeout(() => setFlashInvoiceId(null), 2600);
    return () => clearTimeout(t2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkRefundFor, invoices]);

  // 04 #93 (b): once the deep-linked invoice is in the loaded list, open its detail modal (where an
  // applied client credit's Un-apply lives), expand its group and flash the card behind the modal.
  useEffect(() => {
    if (!deepLinkDetailFor) return;
    const inv = invoices.find((i) => i.id === deepLinkDetailFor);
    if (!inv) return; // not loaded yet (list still fetching, or a stale/other-client id)
    setGroupOpen((m) => ({ ...m, pending: true, paid: true, closed: true }));
    setDetailTarget(inv);
    setFlashInvoiceId(inv.id);
    setDeepLinkDetailFor(null);
    const t2 = setTimeout(() => setFlashInvoiceId(null), 2600);
    return () => clearTimeout(t2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkDetailFor, invoices]);

  const doIssue = async (): Promise<void> => {
    if (!issueTarget) return;
    setBusy(true);
    try {
      await api.admin.issueInvoice(issueTarget.id, issueTarget.clientId);
      setIssueTarget(null);
      await load(clientId);
    } catch (e) {
      setError(apiErrorMessage(e));
      setIssueTarget(null);
    } finally {
      setBusy(false);
    }
  };

  /** Open the payment dialog (prefilled to the full remaining balance; editable). */
  const openPay = (inv: Invoice): void => {
    const remaining = nonCardRemaining(inv);
    setPayError(null);
    setPayForm({
      invoice: inv,
      amount: remaining ? String(remaining) : '',
      method: 'zelle', // default; still selectable (owner rule)
      dateReceived: brandLocalDate(tzOf(brandForInvoice(inv))), // autofilled to today in the invoice's brand (owner rule; 02 brand clock)
      receiptNumber: '',
      memo: '',
      // s77 #6: when the amount overpays, converting the excess to a client credit is the default
      // (the QB/Xero "unapplied cash becomes a credit" norm); ignored server-side for exact/partials.
      overpaymentToCredit: true,
      credits: [],
      creditsLoading: true,
      cashEdited: false,
      tip: '',
      tipChargeClassId: '',
      earlyPay: null,
      earlyPayLoading: hasEarlyPayTerms(inv),
      applyEarlyPay: false,
    });
    // 04 #93 (c): fetch the BILLING ENTITY's issued credits with remaining balance (02d lock 7:
    // eligibility keys on the invoice's frozen billedToClientId, NEVER the page's selected client).
    // Best-effort: a fetch hiccup hides the section and the plain payment path still works.
    void (async () => {
      const entityId = inv.billedToClientId ?? inv.clientId;
      try {
        const res = await api.admin.listAdjustments(entityId);
        const credits: PayCredit[] = res.adjustments
          .filter((a) => a.kind === 'client-credit' && a.status === 'issued' && round2(a.amount - (a.amountApplied ?? 0)) > 0)
          .sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0))
          .map((a) => ({
            creditId: a.id,
            documentNumber: a.documentNumber ?? null,
            remaining: round2(a.amount - (a.amountApplied ?? 0)),
            amount: '',
          }));
        setPayForm((f) => (f && f.invoice.id === inv.id ? { ...f, credits, creditsLoading: false } : f));
      } catch {
        setPayForm((f) => (f && f.invoice.id === inv.id ? { ...f, credits: [], creditsLoading: false } : f));
      }
    })();
  };

  /** 04 #93 (c): the total the settlement dialog is funding from credit right now. */
  const payCreditTotal = (f: NonNullable<typeof payForm>): number =>
    round2(f.credits.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0));
  /** 04 #60 WS4: the voluntary tip typed into the single dialog, as a number (0 when blank or junk). */
  const payTip = (f: NonNullable<typeof payForm>): number => round2(parseFloat(f.tip) || 0);
  /** 04 #60 WS4: the cash that faces the invoice - the amount RECEIVED less the tip (the cross dialog's
   *  convention, ruled for both dialogs at s175). */
  const payApplied = (f: NonNullable<typeof payForm>): number => round2((parseFloat(f.amount) || 0) - payTip(f));
  /** 04 #60 WS4: the cash that would settle the invoice after any credits being applied - what the
   *  running line hints at when the applied figure lands short. */
  /** 04 #69: the GROSS settlement relief (net + its tax, 02 invariant 1) the TICKED, ELIGIBLE early-pay
   *  discount would carry, else 0. Server-quoted; never derived here. */
  const payDiscountGross = (f: NonNullable<typeof payForm>): number =>
    f.applyEarlyPay && f.earlyPay?.eligible ? round2(f.earlyPay.discountGross) : 0;
  /** What the invoice settles at once a ticked early-pay discount is taken off (credits not yet netted). */
  const payTarget = (f: NonNullable<typeof payForm>): number => round2(Math.max(0, nonCardRemaining(f.invoice) - payDiscountGross(f)));
  const payCashToSettle = (f: NonNullable<typeof payForm>): number => round2(payTarget(f) - payCreditTotal(f));

  /** 04 #60 WS4 (owner follow-up, s175): typing a tip RAISES the amount received by the tip while the
   *  admin has not hand-edited the amount, so the prefilled balance keeps reaching the invoice and the
   *  tip reads as "on top" - the same live-adjust the credit rows do (04 #93 c). A hand-edited amount is
   *  never touched; the notice under the field then says what reaches the invoice. */
  const setPayTip = (v: string): void => {
    setPayError(null);
    setPayForm((f) => {
      if (!f) return f;
      const next = { ...f, tip: v };
      if (!next.cashEdited) next.amount = String(round2(Math.max(0, payCashToSettle(next)) + payTip(next)));
      return next;
    });
  };

  /** 04 #93 (c): edit one credit row; unless the admin has hand-edited the cash amount, cash
   *  live-reduces so credit + cash = the invoice's non-card remaining. */
  const setPayCreditAmount = (creditId: string, v: string): void => {
    setPayError(null);
    setPayForm((f) => {
      if (!f) return f;
      const credits = f.credits.map((c) => (c.creditId === creditId ? { ...c, amount: v } : c));
      const next = { ...f, credits };
      if (!next.cashEdited) {
        // 04 #60 WS4: the amount received keeps the tip on top of the cash that faces the invoice;
        // 04 #69: and a ticked early-pay discount stays taken off it.
        next.amount = String(round2(Math.max(0, payCashToSettle(next)) + payTip(next)));
      }
      return next;
    });
  };

  /** 04 #69 rule 6: the admin ticks or unticks the offered discount; an untouched amount follows
   *  (ticked = the pay-in-full-with-discount figure plus any tip; unticked = the full balance). */
  const setPayApplyEarlyPay = (checked: boolean): void => {
    setPayError(null);
    setPayForm((f) => {
      if (!f) return f;
      const next = { ...f, applyEarlyPay: checked && f.earlyPay?.eligible === true };
      if (!next.cashEdited) next.amount = String(round2(Math.max(0, payCashToSettle(next)) + payTip(next)));
      return next;
    });
  };

  // 04 #69: the single dialog's quote. ONE server authority (GET ?action=early-pay-quote), asked once
  // on open with no amount (the server answers for the pay-in-full-with-discount figure) and again
  // whenever the date or the cash toward the invoice changes (02 s97: nothing is restated here). The
  // FIRST eligible answer prefills an untouched amount; later answers only restate the outcome and
  // untick a discount that stopped being available. Stable by construction: the server's
  // payInFullAmount does not depend on the cash, so a prefilled amount re-quotes to itself.
  // 04 #93 (c) + s211: the amount received IS the cash (the credit rows live-reduce it; the submit sends
  // it as the cash leg unchanged), so the quote asks with that cash as entered and names the credits
  // separately — the server then judges against remaining − credits, as recordSettlement does. The
  // s201 form subtracted the credits from the cash a second time and never told the server about them,
  // so a credit edit re-quoted requires-full-payment and unticked a discount the server would grant.
  const payQuoteSeq = useRef(0);
  const payQuoteKey = payForm && hasEarlyPayTerms(payForm.invoice)
    ? `${payForm.invoice.id}|${payForm.dateReceived}|${payForm.earlyPay === null ? 'first' : `${payApplied(payForm)}|${payCreditTotal(payForm)}`}`
    : null;
  useEffect(() => {
    if (!payQuoteKey || !payForm) return;
    const seq = ++payQuoteSeq.current;
    const inv = payForm.invoice;
    const first = payForm.earlyPay === null;
    const cash = round2(Math.max(0, payApplied(payForm)));
    const credits = payCreditTotal(payForm);
    const opts = {
      dateReceived: payForm.dateReceived || undefined,
      ...(first ? {} : { amount: cash }),
      ...(credits > 0 ? { credits } : {}),
    };
    const t = setTimeout(() => {
      api.admin.earlyPayQuote(inv.id, inv.clientId, opts).then(
        (res) => {
          if (seq !== payQuoteSeq.current) return;
          setPayForm((f) => {
            if (!f || f.invoice.id !== inv.id) return f;
            const q = res.quote;
            const next = { ...f, earlyPay: q, earlyPayLoading: false };
            if (first) {
              next.applyEarlyPay = q.eligible;
              if (q.eligible && !f.cashEdited && q.payInFullAmount != null) next.amount = String(round2(q.payInFullAmount + payTip(f)));
            } else if (!q.eligible) {
              next.applyEarlyPay = false;
            }
            return next;
          });
        },
        () => {
          if (seq !== payQuoteSeq.current) return;
          setPayForm((f) => (f && f.invoice.id === inv.id ? { ...f, earlyPayLoading: false } : f));
        },
      );
    }, first ? 0 : 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payQuoteKey]);

  const refreshPayments = async (inv: Invoice): Promise<void> => {
    const res = await api.admin.listPayments(inv.clientId, inv.id);
    setPaymentsByInvoice((m) => ({ ...m, [inv.id]: res.payments }));
    mergeAllocationCtx(res.allocationContext);
    if (res.earlyPayOutcomes) setEarlyPayOutcomes((m) => ({ ...m, ...res.earlyPayOutcomes }));
  };

  /** 04 #72 overlay B: fold a response's allocation context into the accumulated map. Additive by
   *  design — a payment's context never changes, so a later fetch can only confirm it. */
  const mergeAllocationCtx = (ctx?: PaymentAllocationContext): void => {
    if (!ctx || Object.keys(ctx).length === 0) return;
    setAllocationCtx((m) => ({ ...m, ...ctx }));
  };

  /** MIRROR of api/src/lib/invoices.ts#paymentContributionTo — the dollars a payment applied to ONE
   *  invoice. The server stays authoritative for settlement; this only decides what a row PRINTS, so
   *  that a $70 receipt never reads as $70 against the $40 invoice it contributed $10 to (04 #72(d)).
   *  Keep in step with the server helper if its precedence ever changes. */
  const contributionTo = (p: Payment, invoiceId: string): number => {
    if (p.allocations && p.allocations.length > 0) {
      const a = p.allocations.find((x) => x.invoiceId === invoiceId);
      return a ? round2(a.amount) : 0;
    }
    // 04 #60 WS4: a voluntary tip settles nothing, exactly as the credited excess.
    return p.invoiceId === invoiceId ? round2(p.amount - (p.creditedExcess ?? 0) - (p.tipAmount ?? 0)) : 0;
  };

  /** The one-line explanation of why a row's printed amount differs from the cash received, or null
   *  when they agree (the ordinary case, which gets no extra text at all). Symmetric with the two
   *  contextual suffixes invoice-pdf.ts prints on the client's copy. */
  const paymentRowContext = (p: Payment): string | null => {
    const spread = p.allocations?.length ?? 0;
    // 04 #60 WS4: a voluntary tip is NOT folded into this clause; paymentTipRows states it and the
    // total received as their own labelled lines (owner follow-up s175: never blended into context).
    if (spread > 1) return `of ${fmtMoney(p.amount)} received, applied to ${spread} invoices`;
    const excess = round2(p.creditedExcess ?? 0);
    if (excess > 0) {
      // `creditedExcess` is stamped under BOTH leftover dispositions (#64 fork 3), so it does not
      // by itself mean "became a credit" — read the disposition links. Prod: AIT-PMT-2026-0071
      // carries creditedExcess 0.50 REFUNDED as AIT-RFD-2026-0022, not credited.
      const disposition = p.overpaymentRefundId
        ? `${fmtMoney(excess)} refunded`
        : p.overpaymentCreditId
          ? `${fmtMoney(excess)} to account credit`
          : `${fmtMoney(excess)} unapplied`;
      return `of ${fmtMoney(p.amount)} received, ${disposition}`;
    }
    return null;
  };

  /** 04 #60 WS4: under a tipped receipt, the Tip and the Total received as two aligned, labelled
   *  rows (for the surfaces whose rows put the amount in a right-hand column). The applied figure
   *  above them stays what the receipt did to THIS invoice (02 s138). Null when there is no tip. */
  const paymentTipRows = (p: Payment): JSX.Element | null => {
    const tip = round2(p.tipAmount ?? 0);
    if (tip <= 0) return null;
    return (
      <>
        <div className="flex justify-between gap-3 text-[var(--color-muted)]"><span className="pl-3">Tip</span><span className="shrink-0">{fmtMoney(tip)}</span></div>
        <div className="flex justify-between gap-3 font-medium"><span className="pl-3">Total received</span><span className="shrink-0">{fmtMoney(p.amount)}</span></div>
      </>
    );
  };

  /** A payment's allocations as DISPLAY rows: the server-enriched context when we have it, otherwise
   *  the raw allocations so the dialog still enumerates (ids + amounts) rather than showing nothing. */
  const allocationRows = (p: Payment): PaymentAllocationView[] => {
    const ctx = allocationCtx[p.id];
    if (ctx && ctx.length > 0) return ctx;
    return (p.allocations ?? []).map((a) => ({
      invoiceId: a.invoiceId,
      clientId: null,
      documentNumber: null,
      clientName: null,
      amount: a.amount,
    }));
  };

  /** 04 #72(c): open the preview for ONE allocated invoice, showing its contents AND every payment
   *  that touches it, without leaving the dialog it was opened from. Both reads are existing admin
   *  endpoints; the allocation row carries the invoice's OWN partition, which is what made this
   *  possible without a new route (a cross-partition allocation is not in this page's loaded list).
   *  `from` decides only what the footer offers as the way back (04 #70 gave it a second caller). */
  const openAllocPreview = (a: PaymentAllocationView, from: 'void' | 'receipt'): void => {
    if (!a.clientId) return;
    setAllocPreviewFrom(from);
    setAllocPreview(a);
    setAllocPreviewData(null);
    setAllocPreviewError(null);
    void (async () => {
      try {
        const [inv, pay] = await Promise.all([
          api.admin.getInvoice(a.invoiceId, a.clientId as string),
          api.admin.listPayments(a.clientId as string, a.invoiceId),
        ]);
        setAllocPreviewData({ invoice: inv.invoice, payments: pay.payments });
        mergeAllocationCtx(pay.allocationContext);
      } catch (e) {
        setAllocPreviewError(apiErrorMessage(e));
      }
    })();
  };

  /** 04 #70: open the RECEIPT for one payment, from its number on any payment row. The receipt is the
   *  hub (the s93 locked decision): it names every invoice the money was applied to, each opening the
   *  #72(c) preview, so an admin can start from a PMT number and reach all of it without knowing which
   *  invoice anchors the record.
   *
   *  The payment is RE-READ from the server on open rather than taken from the clicked row. The row is
   *  page state that a mutation elsewhere may have aged, and this dialog is where an admin reads what
   *  a receipt did with real money, so it shows server truth or it shows a spinner. The read also
   *  supplies allocation context for a SINGLE-invoice payment (getPayment enriches with includeSingle),
   *  which the per-invoice list deliberately does not, and without it a single-payment receipt could
   *  name only a bare invoice id and could not be clicked through. */
  const openReceipt = (p: Payment): void => {
    setReceiptTarget(p);
    setReceiptFresh(null);
    setReceiptError(null);
    void (async () => {
      try {
        const res = await api.admin.getPayment(p.id, p.clientId);
        setReceiptFresh(res.payment);
        mergeAllocationCtx(res.allocationContext);
      } catch (e) {
        setReceiptError(apiErrorMessage(e));
      }
    })();
  };

  const closeReceipt = (): void => {
    setReceiptTarget(null);
    setReceiptFresh(null);
    setReceiptError(null);
  };

  /** Every invoice a payment mutation touched. A #64 multi-invoice record/void returns `invoices`
   *  (all of them); the single-invoice paths return only `invoice`. */
  const affectedInvoiceIds = (res: { invoice: Invoice; invoices?: Invoice[] }): string[] =>
    res.invoices && res.invoices.length > 0 ? res.invoices.map((i) => i.id) : [res.invoice.id];

  /** 04 #77 (root cause code-confirmed s89) — the ONE place the payments cache is patched after a
   *  mutation. Two rules were being broken by hand at five separate call sites:
   *   • patch EVERY invoice the mutation touched, not just the anchor. A #64 multi-invoice payment
   *     names several invoices; the non-anchor entries kept their stale `status:'received'` object and
   *     rendered a live row with an armed Void button after the payment had already been voided.
   *   • NEVER CREATE a cache entry for an invoice whose panel was never opened. Writing `[]` (or a
   *     one-element list) fabricates a "loaded" state, and the refetch guards in togglePayments /
   *     openDetail then never fetch the real list — producing "No payments recorded yet." on an
   *     invoice with a non-zero amountPaid, until a hard reload.
   *  ABSENT and EMPTY are therefore different states here: absent = never loaded (refetch on open);
   *  empty = loaded and genuinely has no payments. */
  const patchPaymentCache = (invoiceIds: string[], payment: Payment, mode: 'replace' | 'insert'): void => {
    setPaymentsByInvoice((m) => {
      const next = { ...m };
      for (const id of invoiceIds) {
        const cached = next[id];
        if (cached === undefined) continue; // never loaded — leave absent so the next open refetches
        next[id] =
          mode === 'insert'
            ? [payment, ...cached.filter((p) => p.id !== payment.id)]
            : cached.map((p) => (p.id === payment.id ? payment : p));
      }
      return next;
    });
  };

  /** Open the issued-invoice detail view; pull its payments if not already cached (s77 #16).
   *  Guard is `=== undefined` (absent), never a truthiness test: an empty array is a LOADED list. */
  const openDetail = (inv: Invoice): void => {
    setDetailTarget(inv);
    if (paymentsByInvoice[inv.id] === undefined) void refreshPayments(inv).catch((e) => setError(apiErrorMessage(e)));
  };

  /** Per-line deep link to the time/mileage/expense record a pulled line came from (§7.11 sourceRef).
   *  Manual / service / package lines have no source and get nothing. Scopes the target page to the
   *  client and passes ?focus so it can scroll to + highlight the entry. */
  const sourceLinkFor = (line: InvoiceLineItem, inv: Invoice): JSX.Element | null => {
    const s = line.sourceRef;
    if (!s || (s.sourceType !== 'time' && s.sourceType !== 'mileage' && s.sourceType !== 'expense')) return null;
    const path = s.sourceType === 'time' ? '/admin/time' : s.sourceType === 'mileage' ? '/admin/mileage' : '/admin/expenses';
    const label = s.sourceType === 'time' ? 'time entry' : s.sourceType === 'mileage' ? 'mileage entry' : 'expense';
    const focusId = s.lineId ?? s.sourceId;
    const qs = new URLSearchParams({ focus: focusId, client: inv.clientId });
    return (
      <>
        {' · '}
        <button type="button" className="underline hover:text-[var(--color-text)]" onClick={() => { setDetailTarget(null); navigate(`${path}?${qs.toString()}`); }}>
          view {label} ↗
        </button>
      </>
    );
  };

  const togglePayments = (inv: Invoice): void => {
    const open = !paymentsOpen[inv.id];
    setPaymentsOpen((m) => ({ ...m, [inv.id]: open }));
    // `=== undefined` (absent), never a truthiness test — an empty array is a LOADED list, not a miss.
    if (open && paymentsByInvoice[inv.id] === undefined) {
      void refreshPayments(inv).catch((e) => setError(apiErrorMessage(e)));
    }
  };

  const doRecordPayment = async (confirmRelease = false): Promise<void> => {
    if (!payForm) return;
    const invoiceRemaining = nonCardRemaining(payForm.invoice);
    // 04 #60 WS4: a voluntary tip, validated here so the admin never submits into a server 400. The
    // server re-validates every rule (brand switch, class, strictly below the amount received).
    // `received` is the total that arrived, tip included (the dialog's "Amount received"); the tip comes
    // out of it and only the remainder faces the invoice - the same convention as the cross dialog.
    const tipRaw = payForm.tip.trim();
    const tipAmount = tipRaw ? round2(parseFloat(tipRaw)) : 0;
    if (tipRaw && !(Number.isFinite(tipAmount) && tipAmount > 0)) return setPayError('The tip must be a positive amount, or left blank.');
    const tipFields = tipAmount > 0 ? { tipAmount, tipChargeClassId: payForm.tipChargeClassId || null } : {};
    const received = round2(parseFloat(payForm.amount) || 0);
    if (tipAmount > 0 && tipAmount >= received)
      return setPayError(`The tip (${fmtMoney(tipAmount)}) must be less than the amount received (${fmtMoney(received)}); the rest goes to the invoice.`);
    // 04 #69 rule 6: the discount goes out ONLY as an explicit flag the admin left ticked; the server
    // re-quotes against one starting state and refuses a stale flag (409 early_pay_not_eligible).
    const earlyPayFields = payForm.applyEarlyPay && payForm.earlyPay?.eligible ? { applyEarlyPayDiscount: true as const } : {};
    const discountGross = payDiscountGross(payForm);
    const earlyPayFlash = (notes: CreditOrAdjustment[] | undefined): void => {
      if (!notes || notes.length === 0) return;
      const total = round2(notes.reduce((t, n) => t + n.amount, 0));
      setFlash(`Payment recorded with an early-pay discount of ${fmtMoney(total)} (${notes.map((n) => n.documentNumber ?? n.id).join(', ')}).`);
    };
    // 04 #93 (c): any credit amount entered routes to the COMPOSED settlement op (credit legs
    // first, then cash; one server action, fail-fast). No credit amounts = today's path, verbatim.
    const creditSlices = payForm.credits
      .map((c) => ({ creditId: c.creditId, number: c.documentNumber, remaining: c.remaining, amount: round2(parseFloat(c.amount) || 0) }))
      .filter((c) => c.amount > 0);
    if (creditSlices.length > 0) {
      const over = creditSlices.find((c) => c.amount > c.remaining + 0.001);
      if (over) return setPayError(`Applying ${fmtMoney(over.amount)} from ${over.number ?? 'a credit'} exceeds its remaining ${fmtMoney(over.remaining)}.`);
      const creditTotal = round2(creditSlices.reduce((s, c) => s + c.amount, 0));
      if (creditTotal > invoiceRemaining + 0.001)
        return setPayError(`The credits to apply (${fmtMoney(creditTotal)}) exceed the invoice's remaining balance (${fmtMoney(invoiceRemaining)}).`);
      // The cash leg is what faces the invoice: the amount received less the tip. The server mints the
      // payment at cash + tip with the tip stamped, so the receipt still records the full cash received.
      const cash = round2(received - tipAmount);
      // 04 #69: a ticked discount lowers the cap by its gross relief, exactly as the server caps it.
      const cashCap = round2(invoiceRemaining - creditTotal - discountGross);
      if (cash > cashCap + 0.001)
        return setPayError(`The cash toward the invoice (${fmtMoney(cash)}${tipAmount > 0 ? ' after the tip' : ''}) exceeds what remains after the credits${discountGross > 0 ? ' and the early-pay discount' : ''} (${fmtMoney(cashCap)}). Lower the amount or the credit; to keep an overpayment as new account credit, clear the credit fields and use the overpayment option.`);
      if (cash > 0 && !payForm.dateReceived) return setPayError('A payment date is required.');
      const body: RecordSettlementRequest = {
        invoiceId: payForm.invoice.id,
        clientId: payForm.invoice.clientId,
        amount: cash,
        method: payForm.method,
        dateReceived: payForm.dateReceived || undefined,
        receiptNumber: payForm.receiptNumber.trim() ? payForm.receiptNumber.trim() : null,
        memo: payForm.memo.trim() ? payForm.memo.trim() : null,
        creditApplications: creditSlices.map((c) => ({ creditId: c.creditId, amount: c.amount })),
        ...tipFields,
        ...earlyPayFields,
        ...(confirmRelease ? { confirmRelease: true } : {}),
      };
      setBusy(true);
      setPayError(null);
      try {
        const res = await api.admin.recordSettlement(body);
        setPayForm(null);
        setReleaseHeadsUp(null);
        if (res.payment) patchPaymentCache([res.invoice.id], res.payment, 'insert');
        await load(clientId);
        if (!res.payment) setFlash('Invoice settled by credit.');
        else earlyPayFlash(res.earlyPayDiscounts);
      } catch (e) {
        const docs = releaseDocsFromError(e);
        if (docs) setReleaseHeadsUp({ documents: docs, retry: () => doRecordPayment(true) });
        else setPayError(apiErrorMessage(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!Number.isFinite(parseFloat(payForm.amount)) || received <= 0) return setPayError('A positive payment amount is required.');
    if (!payForm.dateReceived) return setPayError('A payment date is required.');
    // s77 #6: an overpayment must land somewhere — either convert the excess to a credit (the default)
    // or lower the amount to the balance. Catch it here so the admin never submits into a server error.
    // 04 #60 WS4: judged in the applied unit (received − tip); the tip is not an overpayment.
    // 04 #69: judged against the balance LESS a ticked discount's gross relief (the server's own guard).
    const remaining = payTarget(payForm);
    const applied = round2(received - tipAmount);
    if (applied > remaining && !payForm.overpaymentToCredit)
      return setPayError(
        `That is more than the ${fmtMoney(remaining)} balance${tipAmount > 0 ? ' after the tip' : ''}${discountGross > 0 ? ' after the early-pay discount' : ''}. Lower the amount, or keep "convert the extra to account credit" checked.`,
      );
    const body: RecordPaymentRequest = {
      invoiceId: payForm.invoice.id,
      clientId: payForm.invoice.clientId,
      amount: received, // the FULL cash received, tip included (s77 #1); the server applies amount − tip
      method: payForm.method,
      dateReceived: payForm.dateReceived,
      receiptNumber: payForm.receiptNumber.trim() ? payForm.receiptNumber.trim() : null,
      memo: payForm.memo.trim() ? payForm.memo.trim() : null,
      overpaymentToCredit: payForm.overpaymentToCredit,
      ...tipFields,
      ...earlyPayFields,
    };
    setBusy(true);
    setPayError(null);
    try {
      const res = await api.admin.recordPayment(body);
      setPayForm(null);
      patchPaymentCache(affectedInvoiceIds(res), res.payment, 'insert');
      mergeAllocationCtx(res.allocationContext);
      await load(clientId);
      earlyPayFlash(res.earlyPayDiscounts);
    } catch (e) {
      setPayError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doVoidPayment = async (): Promise<void> => {
    if (!voidTarget) return;
    if (!voidReason.trim()) return setVoidError('A void reason is required.');
    setVoidError(null);
    setBusy(true);
    try {
      const res = await api.admin.voidPayment(voidTarget.id, voidTarget.clientId, voidReason.trim(), voidReturned);
      setVoidTarget(null);
      setVoidReason('');
      setVoidReturned(false);
      // 04 #77: patch every invoice this void touched (res.invoices), not just the anchor.
      patchPaymentCache(affectedInvoiceIds(res), res.payment, 'replace');
      mergeAllocationCtx(res.allocationContext);
      await load(clientId);
      // 04 #69 rule 18: the void cascaded to the early-pay credit note(s) the payment earned; say so.
      const also: AlsoVoidedAdjustment[] = res.alsoVoided ?? [];
      if (also.length > 0) {
        setFlash(`Payment voided. Its early-pay discount ${also.length === 1 ? 'was' : 'credit notes were'} also voided: ${also.map((a) => `${a.documentNumber ?? a.id} (${fmtMoney(a.amount)})`).join(', ')}. The invoice${also.length === 1 ? '' : 's'} reopen${also.length === 1 ? 's' : ''} for the cash and the discount.`);
      }
    } catch (e) {
      // 04 #75 (scope corrected s89 — this is the LAST dialog still doing it wrong; the invoice and
      // adjustment void dialogs already render inline). A rejected void KEEPS the dialog open, shows
      // the server's guidance inline, and retains the typed reason so the admin can act on it.
      setVoidError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // ── #64 "Record a payment across invoices" ──────────────────────────────────────────────────────
  /** Fetch the entity's open invoices (oldest-first, past-due flagged) into the cross-payment rows; if an
   *  amount is already entered, prefill oldest-first. */
  const loadAllocatable = async (entityId: string, dateReceived?: string): Promise<void> => {
    setCrossLoading(true);
    setCrossError(null);
    try {
      // 04 #69 rule 16: each row's early-pay quote is evaluated on the dialog's date received.
      const res = await api.admin.allocatableInvoices(entityId, dateReceived);
      setCrossForm((f) => {
        if (!f) return f;
        const rows: CrossRow[] = res.invoices.map((i) => ({
          invoiceId: i.id,
          clientId: i.clientId,
          documentNumber: i.documentNumber,
          issueDate: i.issueDate,
          dueDate: i.dueDate,
          remaining: i.remaining,
          pastDue: i.pastDue,
          amount: '',
          // 04 #69: offered pre-ticked where eligible (rule 6: still an explicit per-row choice).
          ...(i.earlyPay ? { earlyPay: i.earlyPay } : {}),
          applyEarlyPay: i.earlyPay?.eligible === true,
        }));
        const received = round2(parseFloat(f.amountReceived) || 0);
        const applied = round2(Math.max(0, received - crossTipOf(f))); // 04 #60 WS4: the tip never reaches a row
        return { ...f, entityId, entityName: res.entityName, rows: applied > 0 ? crossPrefill(rows, applied) : rows };
      });
    } catch (e) {
      setCrossError(apiErrorMessage(e));
      setCrossForm((f) => (f ? { ...f, rows: [] } : f));
    } finally {
      setCrossLoading(false);
    }
  };

  /** Open the cross-payment dialog. Prefills the entity to the current client filter when one is picked. */
  const openCrossPay = (): void => {
    setCrossError(null);
    const c = clientId ? clientById.get(clientId) : undefined;
    const preId = c && !c.isInternal ? clientId : '';
    const preDate = brandLocalDate(tzOf(brandForClient(preId)));
    setCrossForm({
      entityId: preId,
      entityName: c ? clientAdminName(c) : null,
      method: 'zelle',
      amountReceived: '',
      dateReceived: preDate,
      receiptNumber: '',
      memo: '',
      leftoverDisposition: 'credit',
      leftoverRefundMethod: 'cash',
      rows: [],
      tip: '',
      tipChargeClassId: '',
    });
    if (preId) void loadAllocatable(preId, preDate);
  };

  /** Overlay 3a: EVERY edit to the cross-payment form clears the standing validation error first, so a
   *  message like "Enter the amount received." cannot linger after the admin has supplied it. Route all
   *  field edits through this rather than calling setCrossForm directly. */
  const editCrossForm = (mut: (f: NonNullable<typeof crossForm>) => NonNullable<typeof crossForm>): void => {
    setCrossError(null);
    setCrossForm((f) => (f ? mut(f) : f));
  };

  const onCrossEntity = (entityId: string): void => {
    const c = clientById.get(entityId);
    // 04 #136: an untouched date-received prefill follows the picked entity's brand clock.
    const nextDate = crossForm ? rebaseDatePrefill(crossForm.dateReceived, tzOf(brandForClient(crossForm.entityId)), tzOf(brandForClient(entityId))) : undefined;
    editCrossForm((f) => ({
      ...f,
      entityId,
      entityName: c ? clientAdminName(c) : null,
      dateReceived: nextDate ?? f.dateReceived,
      rows: [],
    }));
    if (entityId) void loadAllocatable(entityId, nextDate);
  };

  /** 04 #69 rule 16: the rows' early-pay quotes are evaluated on the date received, so a date edit
   *  reloads them (the server re-quotes every row on the new date) and the prefill follows. */
  const onCrossDate = (v: string): void => {
    editCrossForm((f) => ({ ...f, dateReceived: v }));
    if (crossForm?.entityId && v) void loadAllocatable(crossForm.entityId, v);
  };

  /** 04 #69 rule 6: tick or untick one row's offered discount; the oldest-first prefill re-runs so the
   *  row's amount follows its target (the discounted figure, or the full remaining balance). */
  const onCrossToggleEarlyPay = (invoiceId: string, checked: boolean): void => {
    editCrossForm((f) => {
      const rows = f.rows.map((r) => (r.invoiceId === invoiceId ? { ...r, applyEarlyPay: checked && r.earlyPay?.eligible === true } : r));
      const received = round2(parseFloat(f.amountReceived) || 0);
      const applied = round2(Math.max(0, received - crossTipOf(f)));
      return { ...f, rows: applied > 0 ? crossPrefill(rows, applied) : rows };
    });
  };

  // 04 #69: keep every quoted row's verdict current with its typed amount. A row whose amount differs
  // from the cash its quote answered for is re-asked (debounced); a blank amount keeps its load-time
  // quote (the pay-in-full-with-discount figure is exactly what the prefill will put there). A row
  // the server now calls ineligible is unticked; a row is never auto-ticked (rule 6).
  const crossQuoteKey = crossForm ? `${crossForm.dateReceived}|${crossForm.rows.map((r) => `${r.invoiceId}:${r.amount}`).join(',')}` : null;
  useEffect(() => {
    if (!crossForm || !crossQuoteKey) return;
    const date = crossForm.dateReceived;
    const stale = crossForm.rows.filter((r) => r.earlyPay && r.amount.trim() !== '' && round2(parseFloat(r.amount) || 0) !== round2(r.earlyPay.cash));
    if (stale.length === 0) return;
    const t = setTimeout(() => {
      for (const row of stale) {
        const amount = round2(Math.max(0, parseFloat(row.amount) || 0));
        api.admin.earlyPayQuote(row.invoiceId, row.clientId, { dateReceived: date || undefined, amount }).then(
          (res) =>
            setCrossForm((f) =>
              f && f.dateReceived === date
                ? {
                    ...f,
                    rows: f.rows.map((r) =>
                      r.invoiceId === row.invoiceId && round2(parseFloat(r.amount) || 0) === amount
                        ? { ...r, earlyPay: res.quote, applyEarlyPay: r.applyEarlyPay && res.quote.eligible }
                        : r,
                    ),
                  }
                : f,
            ),
          () => undefined,
        );
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crossQuoteKey]);

  const onCrossReceived = (v: string): void => {
    editCrossForm((f) => {
      const received = round2(parseFloat(v) || 0);
      const applied = round2(Math.max(0, received - crossTipOf(f)));
      const rows = applied > 0 ? crossPrefill(f.rows, applied) : f.rows.map((r) => ({ ...r, amount: '' }));
      return { ...f, amountReceived: v, rows };
    });
  };

  /** 04 #60 WS4: editing the tip re-prefills the rows from received − tip, exactly as editing the
   *  amount received does (the rows always describe what faces the invoices). */
  const onCrossTip = (v: string): void => {
    editCrossForm((f) => {
      const received = round2(parseFloat(f.amountReceived) || 0);
      const applied = round2(Math.max(0, received - (round2(parseFloat(v) || 0))));
      const rows = received > 0 ? crossPrefill(f.rows, applied) : f.rows;
      return { ...f, tip: v, rows };
    });
  };

  const setCrossRowAmount = (invoiceId: string, v: string): void => {
    editCrossForm((f) => ({ ...f, rows: f.rows.map((r) => (r.invoiceId === invoiceId ? { ...r, amount: v } : r)) }));
  };

  const doCrossPay = async (): Promise<void> => {
    if (!crossForm) return;
    if (!crossForm.entityId) return setCrossError('Select a billing entity.');
    const received = round2(parseFloat(crossForm.amountReceived) || 0);
    if (!(received > 0)) return setCrossError('Enter the amount received.');
    if (!crossForm.dateReceived) return setCrossError('A payment date is required.');
    // 04 #60 WS4: the tip is part of the amount received and none of the allocations.
    const tipRaw = crossForm.tip.trim();
    const tipAmount = tipRaw ? round2(parseFloat(tipRaw)) : 0;
    if (tipRaw && !(Number.isFinite(tipAmount) && tipAmount > 0)) return setCrossError('The tip must be a positive amount, or left blank.');
    if (tipAmount >= received) return setCrossError(`The tip (${fmtMoney(tipAmount)}) must be less than the amount received (${fmtMoney(received)}).`);
    const applied = round2(received - tipAmount);
    const allocations = crossForm.rows
      .map((r) => ({ invoiceId: r.invoiceId, amount: round2(parseFloat(r.amount) || 0), remaining: r.remaining, number: r.documentNumber }))
      .filter((a) => a.amount > 0);
    if (allocations.length === 0) return setCrossError('Allocate the payment to at least one invoice.');
    const over = allocations.find((a) => a.amount > a.remaining + 0.001);
    if (over) return setCrossError(`Allocation for ${over.number ?? 'an invoice'} exceeds its remaining balance (${fmtMoney(over.remaining)}).`);
    const allocated = round2(allocations.reduce((s, a) => s + a.amount, 0));
    if (applied + 0.001 < allocated)
      return setCrossError(`The amount received${tipAmount > 0 ? ' after the tip' : ''} (${fmtMoney(applied)}) is less than the total allocated (${fmtMoney(allocated)}).`);
    const leftover = round2(applied - allocated);
    if (leftover > 0 && crossForm.leftoverDisposition !== 'credit' && crossForm.leftoverDisposition !== 'refund')
      return setCrossError('Choose how to record the leftover: an on-account credit or a refund.');
    const body: RecordPaymentRequest = {
      billedToClientId: crossForm.entityId,
      allocations: allocations.map((a) => ({ invoiceId: a.invoiceId, amount: a.amount })),
      amount: received,
      method: crossForm.method,
      dateReceived: crossForm.dateReceived,
      receiptNumber: crossForm.receiptNumber.trim() ? crossForm.receiptNumber.trim() : null,
      memo: crossForm.memo.trim() ? crossForm.memo.trim() : null,
      ...(tipAmount > 0 ? { tipAmount, tipChargeClassId: crossForm.tipChargeClassId || null } : {}),
      // 04 #69 rule 16: one explicit flag per ALLOCATED row the admin left ticked (the server re-quotes
      // each against one starting state and refuses a stale one).
      ...(() => {
        const ids = crossForm.rows
          .filter((r) => r.applyEarlyPay && r.earlyPay?.eligible && round2(parseFloat(r.amount) || 0) > 0)
          .map((r) => r.invoiceId);
        return ids.length > 0 ? { earlyPayDiscountInvoiceIds: ids } : {};
      })(),
      ...(leftover > 0
        ? {
            leftoverDisposition: crossForm.leftoverDisposition,
            ...(crossForm.leftoverDisposition === 'refund' ? { leftoverRefundMethod: crossForm.leftoverRefundMethod } : {}),
          }
        : {}),
    };
    setBusy(true);
    setCrossError(null);
    try {
      const res = await api.admin.recordPayment(body);
      // Reflect EVERY affected invoice (response .invoices) in the current list, and cache the new
      // payment on the primary invoice.
      const affected = res.invoices ?? [res.invoice];
      const byId = new Map(affected.map((i) => [i.id, i] as const));
      setInvoices((list) => list.map((i) => byId.get(i.id) ?? i));
      patchPaymentCache(affected.map((i) => i.id), res.payment, 'insert');
      mergeAllocationCtx(res.allocationContext);
      setCrossForm(null);
      const epNotes = res.earlyPayDiscounts ?? [];
      const epClause = epNotes.length > 0 ? `, with ${epNotes.length === 1 ? 'an early-pay discount' : 'early-pay discounts'} of ${fmtMoney(round2(epNotes.reduce((t, n) => t + n.amount, 0)))} (${epNotes.map((n) => n.documentNumber ?? n.id).join(', ')})` : '';
      setFlash(`Payment of ${fmtMoney(received)} recorded across ${allocations.length} invoice${allocations.length === 1 ? '' : 's'}${tipAmount > 0 ? `, including a ${fmtMoney(tipAmount)} tip` : ''}${epClause}.`);
      await load(clientId);
    } catch (e) {
      setCrossError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // §7.8: confirm a client-reported (pending-confirmation) payment → received + settle; or mark it not
  // received → failed + client emailed. Both return the updated payment + invoice (mirror doVoidPayment).
  const doConfirmNotation = async (p: Payment, body: ConfirmPaymentRequest = {}): Promise<void> => {
    setNotationBusyId(p.id);
    setBusy(true);
    try {
      const res = await api.admin.confirmPayment(p.id, p.clientId, body);
      patchPaymentCache(affectedInvoiceIds(res), res.payment, 'replace');
      mergeAllocationCtx(res.allocationContext);
      setNotationConfirm(null);
      await load(clientId);
      // 04 #69 rule 17: the confirm minted the discount it stated; name the credit note.
      const notes = res.earlyPayDiscounts ?? [];
      if (notes.length > 0) setFlash(`Payment confirmed with an early-pay discount of ${fmtMoney(round2(notes.reduce((t, n) => t + n.amount, 0)))} (${notes.map((n) => n.documentNumber ?? n.id).join(', ')}).`);
    } catch (e) {
      if (notationConfirm && notationConfirm.payment.id === p.id) setNotationError(apiErrorMessage(e));
      else setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
      setNotationBusyId(null);
    }
  };

  /** 04 #69 rule 17 (owner ruling s201): an invoice WITHOUT early-pay terms keeps the one-click
   *  Confirm; one WITH terms opens the confirm dialog that states the resulting state (the server's
   *  sentence), pre-ticks the discount when eligible, and lets the admin set the date received. */
  const startConfirmNotation = (p: Payment, inv: Invoice): void => {
    const outcome = earlyPayOutcomes[p.id];
    if (!outcome || !outcome.terms || !hasEarlyPayTerms(inv)) {
      void doConfirmNotation(p);
      return;
    }
    setNotationError(null);
    setNotationConfirm({
      payment: p,
      invoice: inv,
      dateReceived: p.dateReceived ?? outcome.dateReceived ?? brandLocalDate(tzOf(brandForInvoice(inv))),
      receiptNumber: p.receiptNumber ?? '',
      quote: outcome,
      quoteLoading: false,
      applyEarlyPay: outcome.eligible,
    });
  };

  /** The confirm dialog's date edit: re-quote on the new date against the IMMUTABLE reported amount. */
  const setNotationDate = (v: string): void => {
    setNotationError(null);
    setNotationConfirm((n) => (n ? { ...n, dateReceived: v, quoteLoading: !!v } : n));
    const n = notationConfirm;
    if (!n || !v) return;
    const seq = ++notationQuoteSeq.current;
    api.admin.earlyPayQuote(n.invoice.id, n.invoice.clientId, { dateReceived: v, amount: round2(n.payment.amount) }).then(
      (res) => {
        if (seq !== notationQuoteSeq.current) return;
        setNotationConfirm((cur) => (cur && cur.payment.id === n.payment.id ? { ...cur, quote: res.quote, quoteLoading: false, applyEarlyPay: cur.applyEarlyPay && res.quote.eligible } : cur));
      },
      (e) => {
        if (seq !== notationQuoteSeq.current) return;
        setNotationConfirm((cur) => (cur ? { ...cur, quoteLoading: false } : cur));
        setNotationError(apiErrorMessage(e));
      },
    );
  };

  const doRejectNotation = async (p: Payment): Promise<void> => {
    setNotationBusyId(p.id);
    setBusy(true);
    try {
      const res = await api.admin.rejectPayment(p.id, p.clientId);
      patchPaymentCache(affectedInvoiceIds(res), res.payment, 'replace');
      mergeAllocationCtx(res.allocationContext);
      await load(clientId);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
      setNotationBusyId(null);
    }
  };

  // ── Credits / refunds / write-offs (slice 7) ─────────────────────────────────
  /** Pull the would_release_documents doc list off a 409, or null if that's not the error. */
  const releaseDocsFromError = (err: unknown): WouldReleaseDocument[] | null => {
    if (err instanceof ApiError) {
      const d = err.details as
        | { error?: { code?: string; details?: { documents?: WouldReleaseDocument[] } } }
        | null
        | undefined;
      if (d?.error?.code === 'would_release_documents') return d.error.details?.documents ?? [];
    }
    return null;
  };

  /** 04 #109: pull the already-settled linked late fees off a 409, or null if that is not the error. */
  const paidLateFeesFromError = (err: unknown): PaidLateFeeHeadsUp[] | null => {
    if (err instanceof ApiError) {
      const d = err.details as
        | { error?: { code?: string; details?: { paidLateFees?: PaidLateFeeHeadsUp[] } } }
        | null
        | undefined;
      if (d?.error?.code === 'late_fee_already_settled') return d.error.details?.paidLateFees ?? [];
    }
    return null;
  };

  /** The invoice's surviving line-crediting notes (credit notes AND early-pay CRNs, both counted by the server),
   *  from a given adjustments list (pass the fresh invoice-scoped list so the dialog matches the server exactly):
   *  the per-line list already credited plus each note's booked legs (02 invariant 3 s207, the 3c anchors). */
  const creditRowsAndPriorsFrom = (inv: Invoice, adjList: CreditOrAdjustment[]): { lines: CreditLineRow[]; priors: CreditPriors } => {
    // 04 #160 WS-C M2a (02i T1.6): the loaded invoice list supplies the composed credit lines already
    // raised against this invoice's lines (the server is the authority; see credit-math.ts).
    const priors = creditPriorsFrom(inv, adjList, invoices);
    const lines = inv.lineItems
      .map((l) => {
        const originalNet = round2(Math.max(0, round2(l.charge * l.qty) - (l.discount?.amount ?? 0)));
        const credited = round2((priors.byLine[l.lineId] ?? 0) + (priors.composerByLine?.[l.lineId] ?? 0));
        const net = round2(Math.max(0, originalNet - credited)); // remaining creditable
        const balanceFactor = invoiceLineBalanceFactor(inv, l);
        return { lineId: l.lineId, description: l.description, net, originalNet, credited, taxable: l.taxable !== false, taxShare: invoiceLineTaxShare(l), partlyTaxable: l.chargeClass?.taxMode === 'apportioned', balanceFactor, discounted: !l.chargeClassId && !l.chargeClass && (inv.discount?.amount ?? 0) > 0, sel: false, amount: '' };
      })
      .filter((r) => r.net > 0); // fully-credited lines drop off — nothing left to credit
    return { lines, priors };
  };

  /** Fetch the invoice's own adjustments fresh (same query the server credit-note guard uses), so the
   *  dialog's remaining-per-line and 3c anchors are never stale. Falls back to the loaded list on error. */
  const freshCreditRows = async (inv: Invoice): Promise<{ lines: CreditLineRow[]; priors: CreditPriors }> => {
    try {
      const res = await api.admin.listAdjustments(inv.clientId, inv.id);
      return creditRowsAndPriorsFrom(inv, res.adjustments);
    } catch {
      return creditRowsAndPriorsFrom(inv, adjustments);
    }
  };

  const openCredit = async (inv: Invoice): Promise<void> => {
    setCreditError(null);
    // 02d ruling 13 use-time guard: the ENTITY's available credit (billedToClientId, never the
    // page's selected client). Best-effort: a fetch hiccup just skips the banner.
    let entityCredit = 0;
    try {
      const res = await api.admin.listAdjustments(inv.billedToClientId ?? inv.clientId);
      entityCredit = res.clientCreditBalance;
    } catch {
      entityCredit = 0;
    }
    const fresh = await freshCreditRows(inv);
    setCreditForm({ invoice: inv, reason: '', lines: fresh.lines, priors: fresh.priors, entityCredit });
  };

  const doCreateCredit = async (confirmRelease = false): Promise<void> => {
    if (!creditForm) return;
    const selected = creditForm.lines.filter((r) => r.sel);
    if (selected.length === 0) return setCreditError('Select at least one line to credit.');
    for (const r of selected) {
      const amt = round2(parseFloat(r.amount) || 0);
      if (!(amt > 0)) return setCreditError(`Enter how much of "${r.description}" to credit.`);
      if (amt > r.net + 1e-9) return setCreditError(`The credit for "${r.description}" can't exceed its remaining ${fmtMoney(r.net)}.`);
    }
    if (!creditForm.reason.trim()) return setCreditError('A reason is required.');
    // The pre-check mirrors the server's over_credit guard (comp.total > balance): the note's TOTAL (revenue after
    // the invoice discount + tax), never the list sum - under 04 #149 R2 the list can exceed the balance while the
    // note fits (0028 C2, s209: list 53.00 vs balance 47.19, note 47.19).
    const noteTotal = creditNotePreview(creditForm.invoice, selectedCreditLines(selected), creditForm.priors).total;
    const balance = nonCardRemaining(creditForm.invoice);
    if (noteTotal > balance + 1e-9)
      return setCreditError(`That credit (${fmtMoney(noteTotal)}) is more than the invoice's outstanding balance (${fmtMoney(balance)}). Lower the amounts, or use a refund to return money beyond the balance.`);
    const lines = selected.map((r) => ({ lineId: r.lineId, amount: round2(parseFloat(r.amount) || 0) }));
    const body: CreateAdjustmentRequest = {
      kind: 'credit-note',
      clientId: creditForm.invoice.clientId,
      invoiceId: creditForm.invoice.id,
      lines,
      reason: creditForm.reason.trim(),
      confirmRelease,
    };
    setBusy(true);
    setCreditError(null);
    try {
      await api.admin.createAdjustment(body);
      setCreditForm(null);
      setReleaseHeadsUp(null);
      await load(clientId);
      setFlash('Credit note issued.');
    } catch (e) {
      const docs = releaseDocsFromError(e);
      if (docs) setReleaseHeadsUp({ documents: docs, retry: () => doCreateCredit(true) });
      else setCreditError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const resumeKey = (u: NonPaymentSuspendedUnit): string => (u.kind === 'service' ? `svc:${u.serviceId}` : `pkg:${u.packageId}`);
  const openResumeServices = async (inv: Invoice): Promise<void> => {
    setResumeInvTarget(inv);
    setResumeUnits([]);
    setResumeSelected(new Set());
    setResumeError(null);
    setResumeBusy(true);
    try {
      const res = await api.admin.listNonPaymentSuspended(inv.clientId, inv.id);
      setResumeUnits(res.units);
      const coveredS = new Set(res.invoiceCoveredServiceIds ?? []);
      const coveredP = new Set(res.invoiceCoveredPackageIds ?? []);
      const pre = new Set<string>();
      for (const u of res.units) {
        if (u.kind === 'service' && u.serviceId && coveredS.has(u.serviceId)) pre.add(resumeKey(u));
        if (u.kind === 'package' && u.packageId && coveredP.has(u.packageId)) pre.add(resumeKey(u));
      }
      setResumeSelected(pre);
    } catch (e) {
      setResumeError(apiErrorMessage(e));
    } finally {
      setResumeBusy(false);
    }
  };
  const toggleResume = (key: string): void =>
    setResumeSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const doResumeServices = async (): Promise<void> => {
    if (!resumeInvTarget) return;
    const serviceIds: string[] = [];
    const packageIds: string[] = [];
    for (const u of resumeUnits) {
      if (!resumeSelected.has(resumeKey(u))) continue;
      if (u.kind === 'service' && u.serviceId) serviceIds.push(u.serviceId);
      if (u.kind === 'package' && u.packageId) packageIds.push(u.packageId);
    }
    if (serviceIds.length === 0 && packageIds.length === 0) {
      setResumeError('Select at least one service to resume.');
      return;
    }
    setResumeBusy(true);
    setResumeError(null);
    try {
      await api.admin.resumeNonPaymentServices(resumeInvTarget.clientId, { serviceIds, packageIds });
      setResumeInvTarget(null);
      await load(clientId);
      setFlash('Services resumed.');
    } catch (e) {
      setResumeError(apiErrorMessage(e));
    } finally {
      setResumeBusy(false);
    }
  };

  const doVoidInvoice = async (confirmPaidLateFee = false): Promise<void> => {
    if (!voidInvTarget) return;
    if (!voidInvReason.trim()) return setVoidInvError('A reason is required.');
    setBusy(true);
    setVoidInvError(null);
    try {
      const res = await api.admin.voidInvoice(voidInvTarget.id, voidInvTarget.clientId, voidInvReason.trim(), confirmPaidLateFee);
      setVoidInvTarget(null);
      setVoidInvReason('');
      setPaidFeeHeadsUp(null);
      await load(clientId);
      const also = res.alsoVoided ?? [];
      const failed = res.cascadeVoidFailed ?? [];
      setFlash(
        failed.length
          ? `Invoice voided, but voiding its linked late-fee ${failed.length === 1 ? 'invoice' : 'invoices'} failed: ${failed.join(', ')}. Void ${failed.length === 1 ? 'it' : 'them'} manually.`
          : also.length
            ? `Invoice voided. Also voided its linked late-fee ${also.length === 1 ? 'invoice' : 'invoices'}: ${also.map((a) => a.documentNumber ?? a.invoiceId).join(', ')}.`
            : 'Invoice voided.',
      );
    } catch (e) {
      // 04 #109: an already-settled linked late fee is a CONSENT gate, not a refusal, so route the 409
      // into the heads-up dialog with a retry that carries the confirm flag. Any other error renders
      // inline in the void dialog as before.
      const fees = paidLateFeesFromError(e);
      if (fees) setPaidFeeHeadsUp({ fees, retry: () => doVoidInvoice(true) });
      else setVoidInvError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doWriteOff = async (): Promise<void> => {
    if (!writeOffTarget) return;
    if (!writeOffReason.trim()) return setWriteOffError('A reason is required.');
    const remaining = nonCardRemaining(writeOffTarget);
    const amount = round2(Number(writeOffAmount));
    if (!Number.isFinite(amount) || amount <= 0) return setWriteOffError('Enter an amount to write off.');
    if (amount > remaining + 1e-9) return setWriteOffError(`The write-off can't exceed the ${fmtMoney(remaining)} balance.`);
    const body: CreateAdjustmentRequest = {
      kind: 'write-off',
      clientId: writeOffTarget.clientId,
      invoiceId: writeOffTarget.id,
      reason: writeOffReason.trim(),
      amount,
    };
    setBusy(true);
    setWriteOffError(null);
    try {
      const res = await api.admin.createAdjustment(body);
      const partial = amount < remaining - 1e-9;
      setWriteOffTarget(null);
      setWriteOffReason('');
      setWriteOffAmount('');
      await load(clientId);
      const also = res.alsoWrittenOff ?? [];
      setFlash(
        partial
          ? `Wrote off ${fmtMoney(amount)}; the invoice stays open for the remaining ${fmtMoney(round2(remaining - amount))}.`
          : also.length
            ? `Invoice written off. Also wrote off its linked late-fee ${also.length === 1 ? 'invoice' : 'invoices'}: ${also.map((a) => a.documentNumber ?? a.invoiceId).join(', ')}.`
            : 'Invoice written off.',
      );
    } catch (e) {
      setWriteOffError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** #67 bad-debt recovery: reinstate a written-off invoice by voiding its (active) write-off adjustment,
   *  which reopens the invoice to 'issued' with the written-off balance restored, ready for a payment.
   *  Fetches the invoice's adjustments FRESH (the client-level `adjustments` state is empty on the
   *  All-invoices view), mirroring the View modal. */
  const doReinstate = async (): Promise<void> => {
    if (!reinstateTarget) return;
    if (!reinstateReason.trim()) return setReinstateError('A reason is required.');
    setBusy(true);
    setReinstateError(null);
    try {
      const res = await api.admin.listAdjustments(reinstateTarget.clientId, reinstateTarget.id);
      // An invoice can carry MORE than one write-off (a partial allowance then a later full write-off).
      // Reinstating means the debt is collectible again, so void EVERY active write-off to restore the
      // full balance — voiding just one would leave the invoice partly written off.
      const wos = res.adjustments.filter((a) => a.kind === 'write-off' && a.status !== 'voided');
      if (wos.length === 0) {
        setReinstateError('No active write-off was found for this invoice. Reload and try again.');
        return;
      }
      for (const wo of wos) {
        // The write-off lives in its invoice's partition by construction (it was just fetched with
        // that partition key), so resolve from the adjustment then the invoice, never the page-level
        // selected client, which under the 04 #71 entity scope can be the paying business instead.
        await api.admin.voidAdjustment(wo.id, wo.clientId ?? reinstateTarget.clientId, reinstateReason.trim());
      }
      setReinstateTarget(null);
      setReinstateReason('');
      await load(clientId);
      setFlash('Invoice reinstated and reopened for payment. Record the payment when it arrives.');
    } catch (e) {
      setReinstateError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** Net cash refunded against an invoice (non-void refunds linked to it). */
  const refundedForInvoice = (inv: Invoice): number =>
    round2(
      adjustments
        .filter((a) => a.invoiceId === inv.id && a.kind === 'refund' && a.status !== 'voided')
        .reduce((s, a) => s + a.amount, 0),
    );

  /** Overpayment credit that arose ON this invoice (linked via sourceInvoiceId). */
  const overpaidForInvoice = (inv: Invoice): number =>
    round2(
      adjustments
        .filter((a) => a.sourceInvoiceId === inv.id && a.kind === 'client-credit' && a.status !== 'voided')
        .reduce((s, a) => s + a.amount, 0),
    );

  /** Cash refundable on an invoice: net received (amountPaid) minus prior non-void refunds against it.
   *  The server is the authority; this drives the dialog hint + a pre-submit guard. */
  const refundableFor = (inv: Invoice): number => round2(Math.max(0, (inv.amountPaid ?? 0) - refundedForInvoice(inv)));

  /** A reversed LIST amount's balance relief on a line (revenue after the invoice discount + its tax), mirrored
   *  from the server through the shared-ui helper (04 #149 commit 2, 02 invariant 3 s207) - the per-line hint
   *  and cap unit; the dialog totals come from creditNotePreview (cumulative, rounded once). */
  const lineGross = (net: number, row: CreditLineRow): number => round2(net * row.balanceFactor);
  /** The per-row hint: the line's list net, what earlier notes took, and what crediting its remainder relieves
   *  (after the invoice discount's share on an ordinary line of a discounted invoice, plus the line's tax). */
  const creditRowHint = (inv: Invoice, r: CreditLineRow, verb: 'credited' | 'refunded'): ReactNode => {
    const taxed = r.taxShare > 0 && inv.tax.enabled;
    const parts = [r.discounted ? 'less the invoice discount' : null, taxed ? 'plus tax' : null].filter(Boolean);
    const tail = parts.length > 0 ? ` ${parts.join(', ')} → ${fmtMoney(lineGross(r.net, r))} ${verb}${r.partlyTaxable ? ' (partly taxable)' : ''}` : ' · non-taxable';
    return (
      <>Net {fmtMoney(r.originalNet)}{r.credited > 0 ? <span className="text-[var(--tint-red-text)]"> (-{fmtMoney(r.credited)})</span> : null}{tail}</>
    );
  };
  /** The selected lines as creditNotePreview inputs. */
  const selectedCreditLines = (lines: CreditLineRow[]): { lineId: string; amount: number }[] =>
    lines.filter((l) => l.sel).map((l) => ({ lineId: l.lineId, amount: parseFloat(l.amount) || 0 }));
  /** Total cash returned by a "return part of the invoice" refund = the paired credit note's total (revenue net of
   *  the invoice discount + tax), so the refund and its credit note always agree (s77; the server mints the note
   *  from the same lines). */
  const returnGrossTotal = (form: { invoice: Invoice; lines: CreditLineRow[]; priors: CreditPriors }): number =>
    creditNotePreview(form.invoice, selectedCreditLines(form.lines), form.priors).total;
  /** Max PRE-TAX list net still refundable on a line, so the total return (revenue + tax) stays within the
   *  cash REFUNDABLE (amountPaid less prior refunds) - mirrors the credit dialog's per-line cap. */
  const refundLineMaxNet = (form: { invoice: Invoice; lines: CreditLineRow[] }, i: number): number => {
    const inv = form.invoice;
    const refundable = refundableFor(inv);
    const row = form.lines[i];
    const otherConsumed = form.lines.reduce(
      (s, x, j) => (j !== i && x.sel ? s + (parseFloat(x.amount) || 0) * x.balanceFactor : s),
      0,
    );
    const room = Math.max(0, refundable - otherConsumed);
    return round2(Math.min(row.net, room / row.balanceFactor));
  };

  const openRefund = async (inv: Invoice): Promise<void> => {
    setRefundError(null);
    setRefundForm({
      invoice: inv,
      amount: '',
      method: 'zelle',
      reason: '',
      dateRefunded: brandLocalDate(tzOf(brandForInvoice(inv))),
      reverseLines: false, // explicit mode chosen in the dialog (cash-only vs return part of the invoice)
      ...(await freshCreditRows(inv)),
    });
  };

  const doRefund = async (confirmRelease = false): Promise<void> => {
    if (!refundForm) return;
    if (!refundForm.reason.trim()) return setRefundError('A reason is required.');
    const qualified = refundForm.reverseLines;
    // Validate the reversed lines FIRST (each capped at its own pre-tax net) so the specific,
    // actionable error wins over the invoice-level refundable cap.
    if (qualified) {
      const selected = refundForm.lines.filter((x) => x.sel);
      if (selected.length === 0) return setRefundError('Select at least one line to return, or choose "Cash refund only".');
      for (const r of selected) {
        const net = round2(parseFloat(r.amount) || 0);
        if (!(net > 0)) return setRefundError(`Enter how much of "${r.description}" to return.`);
        if (net > r.net + 1e-9)
          return setRefundError(`The amount to return for "${r.description}" can't exceed that line's ${fmtMoney(r.net)} before tax. Tax is added automatically.`);
      }
    }
    // Return mode: the cash returned is DERIVED from the reversed lines (net + tax), so the refund and
    // its credit note always agree. Cash mode: the amount typed.
    const amount = qualified ? returnGrossTotal(refundForm) : round2(parseFloat(refundForm.amount) || 0);
    if (!(amount > 0)) return setRefundError(qualified ? 'Select at least one line to return, and enter how much of each.' : 'A positive refund amount is required.');
    const refundable = refundableFor(refundForm.invoice);
    if (amount > refundable + 1e-9)
      return setRefundError(`That is more than the ${fmtMoney(refundable)} refundable on this invoice (the cash received, less prior refunds).`);
    const lines = qualified
      ? refundForm.lines.filter((r) => r.sel).map((r) => ({ lineId: r.lineId, amount: round2(parseFloat(r.amount) || 0), ...(r.coversFrom && r.coversTo ? { coversFrom: r.coversFrom, coversTo: r.coversTo } : {}) }))
      : null;
    const body: CreateAdjustmentRequest = {
      kind: 'refund',
      clientId: refundForm.invoice.clientId,
      amount,
      method: refundForm.method,
      reason: refundForm.reason.trim(),
      dateRefunded: refundForm.dateRefunded,
      invoiceId: refundForm.invoice.id,
      lines,
      createLinkedCreditNote: qualified, // "return part of the invoice" always issues the linked CN
      confirmRelease,
    };
    setBusy(true);
    setRefundError(null);
    try {
      await api.admin.createAdjustment(body);
      setRefundForm(null);
      setReleaseHeadsUp(null);
      await load(clientId);
      setFlash('Refund recorded.');
    } catch (e) {
      const docs = releaseDocsFromError(e);
      if (docs) setReleaseHeadsUp({ documents: docs, retry: () => doRefund(true) });
      else setRefundError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  /** The Cosmos partition an adjustment lives in.
   *
   *  An adjustment is always stamped with its partition at mint (admin-credits.ts; since 04 #73
   *  lock 7 that is the invoice's BILLING ENTITY, while legacy docs carry the invoice's own
   *  clientId), so resolve from the adjustment itself, then from the invoice it names, and
   *  otherwise give up. It deliberately
   *  NEVER falls back to the page-level selected client: under the 04 #71 entity scope the selected
   *  client is the paying BUSINESS while a rolled-up invoice and its adjustments live in the ISSUING
   *  person's partition, so a page-level fallback would route a financial write to the wrong
   *  partition. Returning null makes the caller surface an error instead of guessing, which is the
   *  same fail-loud posture the server takes on an unknown scope. */
  const adjustmentClientId = (adj: CreditOrAdjustment, invoiceHint?: string | null): string | null => {
    if (adj.clientId) return adj.clientId;
    const target = invoiceHint ?? adj.appliedToInvoiceId ?? adj.invoiceId;
    if (!target) return null;
    return invoices.find((i) => i.id === target)?.clientId ?? null;
  };

  const UNRESOLVED_PARTITION =
    'Could not work out which client this adjustment belongs to. Reload the page and try again.';

  /** Un-apply a client credit from ONE invoice it is applied to (s77 #9): reopens that invoice if a
   *  balance returns, frees the credit. 04 #93 (b): lives in the invoice detail modal (02d #93 ruling 10),
   *  so it names the invoice IN VIEW, never the credit's appliedToInvoiceId anchor (a multi-applied credit's
   *  anchor is only its newest application; the server resolves the entry across all three key generations). */
  const doUnapply = async (adj: CreditOrAdjustment, invoiceId: string): Promise<void> => {
    const partition = adjustmentClientId(adj, invoiceId);
    if (!partition) return setError(UNRESOLVED_PARTITION);
    setBusy(true);
    setError(null);
    try {
      const res = await api.admin.unapplyCredit(adj.id, partition, invoiceId);
      await load(clientId);
      // The modal stays open on the recomputed invoice so the admin sees the line go and the balance return.
      if (res.invoice) setDetailTarget(res.invoice);
      setFlash(`${adj.documentNumber ?? 'Credit'} un-applied from ${res.invoice?.documentNumber ?? 'the invoice'}.`);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.admin.deleteInvoice(deleteTarget.id, deleteTarget.clientId);
      setDeleteTarget(null);
      await load(clientId);
    } catch (e) {
      setError(apiErrorMessage(e));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-[var(--color-muted)]">Loading…</div>;

  const client = clientId ? clientById.get(clientId) ?? null : null;

  // Live preview totals for the open form.
  const formClient = editing ? clientById.get(editing.clientId) : form ? clientById.get(form.clientId) : undefined;
  const cfg = billingConfigFor(formClient);
  // 04 #160 WS-C M1: the registration gate + exemption the server resolved for this client. 04 #169 (b):
  // the dry-run resolution ON THE FORM'S ISSUE DATE wins whenever it has answered for the current
  // (client, date); until then the draft's own stamp (editing) or the list's today-resolution (creating)
  // stands, as before. Absent on a draft saved before M1 = no gate known (the next save stamps it).
  const previewKey = form ? `${editing ? editing.clientId : form.clientId}|${form.issueDate}` : '';
  const datedTax: ResolvedTaxInfo | null = previewTax && previewKey && previewTax.key === previewKey ? previewTax.info : null;
  const gateOf = (t: { registered?: boolean; exemption?: unknown } | null | undefined): 'not-registered' | 'exempt-client' | null =>
    t ? (t.registered === false ? 'not-registered' : t.exemption ? 'exempt-client' : null) : null;
  const taxGate: 'not-registered' | 'exempt-client' | null = datedTax
    ? gateOf(datedTax)
    : editing
      ? gateOf(editing.tax)
      : resolvedTax && form?.clientId
        ? gateOf(resolvedTax)
        : null;
  const formBrandName = (editing ? brandForInvoice(editing) : form ? brandForClient(form.clientId) : undefined)?.name ?? 'The brand';
  const preview = form
    ? previewTotals(
        form.lines.map((r) => ({
          charge: parseFloat(r.charge) || 0,
          qty: parseFloat(r.qty) || 0,
          taxable: r.taxable,
          discount:
            r.discType === 'none'
              ? null
              : { type: r.discType, value: r.discType === 'percent' ? (parseFloat(r.discValue) || 0) / 100 : parseFloat(r.discValue) || 0 },
          chargeClass: r.chargeClass ? { rateType: r.chargeClass.rateType, value: r.chargeClass.value, taxMode: r.chargeClass.taxMode, percentBasis: r.chargeClass.percentBasis } : null,
          credit: !!r.creditReason,
          ...(r.creditReason && typeof r.creditTaxAmount === 'number' ? { creditTaxAmount: r.creditTaxAmount } : {}),
          // A touched checkbox or a manual row previews as the admin's boolean; a rule-decided row as
          // its stored fraction at its stored rate (a row the server has not resolved yet - new, or
          // its category just changed - previews as 0 until the save resolves it); a gated class row
          // taxes nothing; a CREDIT row (04 #160 WS-C M2a) previews its source's frozen fraction at its
          // source's rate, never gated (its tax is fixed, 02i T1.3).
          tax: r.chargeClassId
            ? { gated: taxGate !== null || r.taxableFraction === 0 }
            : r.creditReason
              ? { fraction: r.taxableFraction ?? 0, rate: r.taxRate }
              : r.taxTouched || r.taxMode === 'override'
                ? { fraction: taxGate ? 0 : r.taxable ? 1 : 0 }
                : { fraction: taxGate ? 0 : r.taxableFraction ?? 0, rate: r.taxRate },
        })),
        form.discountType === 'none'
          ? null
          : { type: form.discountType, value: form.discountType === 'percent' ? (parseFloat(form.discountValue) || 0) / 100 : parseFloat(form.discountValue) || 0 },
        { enabled: form.taxEnabled, rate: form.taxEnabled ? (parseFloat(form.taxRate) || 0) / 100 : 0, registered: taxGate !== 'not-registered' },
        cfg.convenienceFeeRate,
      )
    : null;

  const renderPayments = (inv: Invoice): JSX.Element | null => {
    if (!paymentsOpen[inv.id]) return null;
    const rows = paymentsByInvoice[inv.id];
    if (rows === undefined) return <div className="text-xs text-[var(--color-muted)]">Loading payments…</div>;
    if (rows.length === 0) return <div className="text-xs text-[var(--color-muted)]">No payments recorded yet.</div>;
    return (
      <div className="space-y-1 rounded-md bg-[rgb(var(--edge-rgb)/0.03)] p-2">
        {rows.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
            <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5">
              {/* 04 #77 (2): a voided/failed payment stays VISIBLE (void-not-delete) but must read
                  UNMISTAKABLY as not-money. The badge pill sits OUTSIDE the struck span on purpose:
                  a struck-through "Voided" label is the one word on the row that must stay legible.
                  Pattern copied from the adjustments row. */}
              {p.status === 'voided' ? (
                <span className="rounded bg-[rgb(var(--edge-rgb)/0.06)] px-1.5 py-0.5 font-medium text-[var(--color-muted)]">Voided</span>
              ) : p.status === 'failed' ? (
                <span className="rounded bg-[rgb(var(--edge-rgb)/0.06)] px-1.5 py-0.5 font-medium text-[var(--color-muted)]">Not received</span>
              ) : null}
              <span className={`flex flex-wrap items-center gap-x-3 gap-y-0.5 ${p.status === 'voided' || p.status === 'failed' ? 'text-[var(--color-muted)] line-through' : ''}`}>
                {/* 04 #70: the PMT number is the way IN to the receipt. Every status is clickable,
                    including a pending notation (which has no number yet, §7.8 assigns it at confirm)
                    and a voided one, because "what did this money touch" is exactly the question asked
                    about a record that looks wrong. */}
                <button
                  type="button"
                  className="font-semibold text-[var(--color-text)] underline decoration-dotted underline-offset-2 hover:decoration-solid"
                  onClick={() => openReceipt(p)}
                >
                  {p.documentNumber ?? 'Reported'}
                </button>
                {/* 04 #72(d): the amount applied to THE INVOICE IN VIEW, never the receipt total. A
                    $70 receipt spread over two invoices used to print $70 against the $40 one. */}
                <span>{fmtMoney(contributionTo(p, inv.id))}</span>
                <span>{METHOD_LABEL[p.method] ?? p.method}</span>
                <span>{displayDate(p.dateReceived)}</span>
                {p.receiptNumber ? <span>Ref {p.receiptNumber}</span> : null}
                {p.memo ? <span className="text-[var(--color-muted)]">{p.memo}</span> : null}
              </span>
              {/* 04 #72(a): the receipt total stays available alongside, so nothing is hidden. */}
              {paymentRowContext(p) ? (
                <span className="basis-full text-[var(--color-muted)]">{paymentRowContext(p)}</span>
              ) : null}
              {/* 04 #60 WS4: a tipped receipt names its tip and its total received on their own line. */}
              {(p.tipAmount ?? 0) > 0 ? (
                <span className="basis-full">
                  <span className="text-[var(--color-muted)]">Tip {fmtMoney(round2(p.tipAmount ?? 0))}</span>
                  <span className="text-[var(--color-muted)]"> · </span>
                  <span className="font-medium">Total received {fmtMoney(p.amount)}</span>
                </span>
              ) : null}
              {/* 04 #69 rule 17: a pending client notation states the SERVER's resulting state, never
                  arithmetic done here; the confirm dialog pre-ticks from the same quote. */}
              {p.status === 'pending-confirmation' && earlyPayOutcomes[p.id]?.terms ? (
                <span className={`basis-full ${earlyPayOutcomes[p.id]?.eligible ? 'text-[var(--tint-green-text)]' : 'text-[var(--color-muted)]'}`}>
                  {earlyPayOutcomes[p.id]?.statement}
                </span>
              ) : null}
              {/* 04 #69: a received payment that earned a discount names its credit note(s). */}
              {p.status === 'received' && (p.earlyPayDiscountAdjustmentIds?.length ?? 0) > 0 ? (
                <span className="basis-full text-[var(--color-muted)]">
                  Early-pay discount applied with this payment ({p.earlyPayDiscountAdjustmentIds!.length === 1 ? 'credit note' : `${p.earlyPayDiscountAdjustmentIds!.length} credit notes`} on the Money page).
                </span>
              ) : null}
            </div>
            {/* 04 #93 (b) (owner, s215): the void stamp takes its OWN line (basis-full) rather than
                competing with the number for the row's width, which is what clipped long numbers and
                reasons on this panel. */}
            {p.status === 'voided' ? (
              <span className="basis-full text-[var(--color-muted)]">
                {p.voidedAt ? `Voided ${displayDate(p.voidedAt.slice(0, 10))}` : 'Voided'}
                {p.voidReason ? `: ${p.voidReason}` : ''}
              </span>
            ) : p.status === 'failed' ? (
              <span className="basis-full text-[var(--color-muted)]">
                {p.voidedAt ? `Not received, marked ${displayDate(p.voidedAt.slice(0, 10))}` : 'Not received'}
                {p.voidReason ? `: ${p.voidReason}` : ''}
              </span>
            ) : p.status === 'pending-confirmation' ? (
              <div className="flex items-center gap-2">
                <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-[var(--tint-warn-text)]">Reported by client</span>
                {notationBusyId === p.id ? (
                  <span className="text-[var(--color-muted)]">Working…</span>
                ) : (
                  <>
                    <PillButton tone="green" onClick={() => startConfirmNotation(p, inv)}>Confirm</PillButton>
                    <PillButton tone="red" onClick={() => void doRejectNotation(p)}>Not received</PillButton>
                  </>
                )}
              </div>
            ) : (
              <PillButton tone="red" onClick={() => { setVoidReason(''); setVoidReturned(false); setVoidError(null); setVoidTarget(p); }}>Void</PillButton>
            )}
          </div>
        ))}
      </div>
    );
  };

  /** One picker row: checkbox + the §7.11 clarity context (date, description, detail, project, cost → charge). */
  const renderPickerRow = (it: UnbilledPickerItem): JSX.Element => {
    const k = pickerKey(it);
    return (
      <label key={k} className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1 text-xs hover:bg-[rgb(var(--edge-rgb)/0.03)]">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={pickerSelected.has(k)}
          onChange={() => togglePickerItem(it)}
        />
        <span className="flex-1">
          <span className="font-medium text-[var(--color-text)]">{it.description}</span>
          <span className="text-[var(--color-muted)]">
            {' '}· {displayDate(it.date)}
            {it.detail ? ` · ${it.detail}` : ''}
            {it.projectName ? ` · project: ${it.projectName}` : ''}
          </span>
        </span>
        <span className="whitespace-nowrap text-[var(--color-muted)]">
          cost {fmtMoney(it.cost)} <span className="text-[var(--color-text)]">→ {fmtMoney(it.charge)}</span>
        </span>
      </label>
    );
  };

  const renderPickerSection = (title: string, items: UnbilledPickerItem[]): JSX.Element | null => {
    if (items.length === 0) return null;
    const allIn = items.every((it) => pickerSelected.has(pickerKey(it)));
    return (
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-[var(--color-text)]">{title}</span>
          <button type="button" className="text-xs text-[var(--color-muted)] underline" onClick={() => togglePickerSection(items)}>
            {allIn ? 'Clear all' : 'Select all'}
          </button>
        </div>
        {items.map(renderPickerRow)}
      </div>
    );
  };

  /** Expense lines GROUPED BY VENDOR (§7.11 — "grouped by vendor" is the spec's picker rule). */
  const renderPickerExpenses = (items: UnbilledPickerItem[]): JSX.Element | null => {
    if (items.length === 0) return null;
    const byVendor = new Map<string, UnbilledPickerItem[]>();
    for (const it of items) {
      const key = it.vendorName ?? 'No vendor';
      const g = byVendor.get(key);
      if (g) g.push(it);
      else byVendor.set(key, [it]);
    }
    return (
      <div className="space-y-2">
        {[...byVendor.entries()].map(([vendor, rows]) => renderPickerSection(`Expenses · ${vendor}`, rows))}
      </div>
    );
  };

  /**
   * 04 #110 - WHO IS BILLED, AND WHO INCURRED THE CHARGES, on every card in BOTH
   * the all-invoices and client-scoped modes.
   *
   * THE INTENT, stated plainly so it survives any later refactor: an invoice always
   * has TWO parties, and they differ whenever a linked person's bill is owed by
   * their business.
   *   inv.clientId         = the partition owner = who it was ISSUED to
   *                        = WHO INCURRED THE CHARGES
   *   inv.billedToClientId = the immutable billing authority stamped at ISSUE
   *                        = WHO IS BILLED (02 RECEIVABLE ATTRIBUTION, locked s96)
   *
   * What this replaces: a muted client span that rendered in the all-invoices mode
   * ONLY, and named the ISSUING client, so on the one screen used to scan every
   * client the party that actually owes the money was never stated. The blue
   * "Issued to" / "Billed to" pills do state it, but both are gated on a selected
   * client (see rolledUp / billedElsewhere), so neither renders in the all mode.
   *
   * The pills stay, and the division of labour is deliberate: the pills are
   * NAVIGATION (they set the entity filter or switch client), this line is the
   * FACT. It renders unconditionally, because identity that appears only when some
   * other chip happens to be present is exactly the conditional-appearance defect
   * 04 #104 exists to remove.
   *
   * A DRAFT genuinely has no billing entity yet: billedToClientId is null until
   * ISSUE and immutable after (02b slice 2, locked s64). The line says so rather
   * than printing one name and letting it read as settled.
   */
  const renderInvoiceParties = (inv: Invoice): JSX.Element => {
    const incurredBy = clientAdminLabel(clientById.get(inv.clientId), inv.clientId);
    const strong = 'font-medium text-[var(--color-text)]';
    if (!inv.billedToClientId)
      return (
        <div className="text-xs text-[var(--color-muted)]">
          For <span className={strong}>{incurredBy}</span> · billing entity set at issue
        </div>
      );
    const billedTo = clientAdminLabel(clientById.get(inv.billedToClientId), inv.billedToClientId);
    return (
      <div className="text-xs text-[var(--color-muted)]">
        Billed to <span className={strong}>{billedTo}</span>
        {inv.billedToClientId !== inv.clientId ? (
          <> · incurred by <span className={strong}>{incurredBy}</span></>
        ) : null}
      </div>
    );
  };

  const renderInvoice = (inv: Invoice): JSX.Element => {
    const countdown = inv.state === 'issued' ? dueCountdown(inv.dueDate, tzOf(brandForInvoice(inv))) : null;
    const hasPending = pendingNotationSet.has(inv.id);
    return (
      <div
        key={inv.id}
        id={`inv-${inv.id}`}
        className={`rounded-xl transition ${flashInvoiceId === inv.id ? 'ring-2 ring-amber-500 ring-offset-2' : hasPending ? 'ring-1 ring-amber-400' : ''}`}
      >
      <Card>
        <div className="space-y-2 p-4">
          {/* 04 #104: the action pills used to share a justify-between row with this one, so a
              SHORT chip row kept them inline right while a LONG one wrapped them below - one
              flex-wrap, two appearances on the same page. They now always occupy their own row
              below, and this row carries badges only. */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-[var(--color-text)]">{inv.documentNumber ?? 'Draft'}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATE_TONE[inv.state]}`}>{STATE_LABEL[inv.state]}</span>
            {(() => {
              const r = refundedForInvoice(inv);
              if (r <= 0) return null;
              const full = (inv.amountPaid ?? 0) > 0 && r >= round2(inv.amountPaid ?? 0) - 0.001;
              return <span className="rounded-full bg-[var(--tint-red-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-red-text)]">{full ? 'Refunded' : 'Partially refunded'}</span>;
            })()}
            {overpaidForInvoice(inv) > 0 ? (
              <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-warn-text)]">Overpaid</span>
            ) : null}
            {countdown ? (
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${countdown.overdue ? 'bg-[var(--tint-red-bg)] text-[var(--tint-red-text)]' : 'bg-[var(--tint-warn-bg)] text-[var(--tint-warn-text)]'}`}>
                {countdown.label}
              </span>
            ) : null}
            {inv.billingType === 'monthly' && inv.coversPeriodStart ? (
              <span className="rounded-full bg-[rgb(var(--edge-rgb)/0.04)] px-2 py-0.5 text-xs italic text-[var(--color-muted)]">{coverageLabel(inv)}</span>
            ) : null}
            {rolledUp(inv) ? (
              <PillButton
                tone="blue"
                size="xs"
                title="Issued to this linked account, billed to the client in view. Click to show only their invoices."
                onClick={() => setEntityFilter((f) => (f === inv.clientId ? '' : inv.clientId))}
              >
                Issued to {clientAdminLabel(clientById.get(inv.clientId), inv.clientId)}
              </PillButton>
            ) : null}
            {billedElsewhere(inv) ? (
              <PillButton
                tone="blue"
                size="xs"
                title="This client received the invoice; the linked business owes it. Click to open that business's invoices."
                onClick={() => setClientId(inv.billedToClientId as string)}
              >
                Billed to {clientAdminLabel(clientById.get(inv.billedToClientId as string), inv.billedToClientId as string)}
              </PillButton>
            ) : null}
            {/* 02d #39 lock 7: a status marker is a BADGE, never a PillButton - and it belongs
                with the other badges. It sat inside the action row until 04 #104 separated them. */}
            {hasPending ? (
              <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-warn-text)]">Payment reported</span>
            ) : null}
          </div>
          {renderInvoiceParties(inv)}
          <div className="flex flex-wrap items-center gap-2">
            {inv.state === 'draft' ? (
              <>
                <PillButton onClick={() => openEdit(inv)}>Edit</PillButton>
                <PillButton tone="green" onClick={() => setIssueTarget(inv)}>Issue</PillButton>
                <PillButton tone="red" onClick={() => setDeleteTarget(inv)}>Delete</PillButton>
              </>
            ) : inv.state === 'issued' ? (
              <>
                <PillButton tone="green" onClick={() => openPay(inv)}>Record payment</PillButton>
                <PillButton onClick={() => openCredit(inv)}>Credit note</PillButton>
                <PillButton tone="red" onClick={() => { setVoidInvTarget(inv); setVoidInvReason(''); setVoidInvError(null); }}>Void</PillButton>
                <PillButton tone="red" onClick={() => { setWriteOffTarget(inv); setWriteOffReason(''); setWriteOffAmount(String(nonCardRemaining(inv))); setWriteOffError(null); }}>Write off</PillButton>
              </>
            ) : inv.state === 'paid' ? (
              <PillButton onClick={() => openRefund(inv)}>Refund</PillButton>
            ) : null}
            {inv.state !== 'draft' ? (
              <>
                <PillButton onClick={() => openDetail(inv)}>View</PillButton>
                <PillButton onClick={() => window.open(api.admin.invoiceFileUrl(inv.id, inv.clientId), '_blank', 'noopener')}>PDF</PillButton>
                <PillButton onClick={() => togglePayments(inv)}>{paymentsOpen[inv.id] ? 'Hide payments' : 'Payments'}</PillButton>
                {inv.state === 'written-off' ? (
                  <>
                    <PillButton tone="green" onClick={() => { setReinstateTarget(inv); setReinstateReason(''); setReinstateError(null); }}>Reinstate</PillButton>
                    <PillButton tone="green" onClick={() => void openResumeServices(inv)}>Resume services</PillButton>
                  </>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="text-xs text-[var(--color-muted)]">
            {inv.billingType === 'monthly' ? 'Monthly' : 'One-off'} · {inv.lineItems.length} {inv.lineItems.length === 1 ? 'line' : 'lines'}
            {' · '}issued {displayDate(inv.state === 'draft' ? null : inv.issueDate)}
            {' · '}due {displayDate(inv.state === 'draft' ? null : inv.dueDate)}
            {inv.state === 'issued' ? <> {' · '}{termsCountdown(inv, tzOf(brandForInvoice(inv)))}</> : null}
            {/* 04 #69 rule 13: the frozen terms, as data; available or ended on the BRAND's calendar (rule 4'). */}
            {inv.state === 'issued' && hasEarlyPayTerms(inv) ? (
              <> {' · '}Early pay {pctLabel(inv.earlyPayDiscount!.rate)}% through {displayDate(inv.earlyPayDiscount!.dueBy ?? null)}{brandLocalDate(tzOf(brandForInvoice(inv))) <= (inv.earlyPayDiscount!.dueBy ?? '') ? '' : ' (ended)'}</>
            ) : inv.state === 'paid' && hasEarlyPayTerms(inv) ? (
              <> {' · '}Early-pay terms {pctLabel(inv.earlyPayDiscount!.rate)}% through {displayDate(inv.earlyPayDiscount!.dueBy ?? null)}</>
            ) : null}
          </div>
          {(() => {
            // Both card/non-card totals only while there's a balance to pay (§7.4 — the client hasn't
            // chosen a method yet). Once settled, show just the total that paid it. Convenience fee suppressed
            // entirely when the brand's fee is off (s77 #14).
            const convenienceFeeOn = inv.convenienceFee?.enabled !== false && round2(inv.totalWithConvenienceFee) !== round2(inv.totalWithoutConvenienceFee);
            const settled = inv.state === 'paid';
            const refunded = refundedForInvoice(inv);
            const overpaid = overpaidForInvoice(inv);
            const num = (label: string, val: number, neg = false): JSX.Element => (
              <span>{label} <span className="font-semibold text-[var(--color-text)]">{neg ? '-' : ''}{fmtMoney(val)}</span></span>
            );
            return (
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-muted)]">
                {num('Subtotal', inv.subtotal)}
                {inv.discount ? num('Discount', inv.discount.amount, true) : null}
                {inv.tax.enabled ? num('Tax', inv.tax.amount) : null}
                {!settled && convenienceFeeOn ? (
                  <>
                    {num('With convenience fee', inv.totalWithConvenienceFee)}
                    {num('Without', inv.totalWithoutConvenienceFee)}
                  </>
                ) : (
                  num('Total', inv.totalWithoutConvenienceFee)
                )}
                {inv.state !== 'draft' ? (
                  <>
                    {inv.amountPaid > 0 ? num('Paid', inv.amountPaid) : null}
                    {appliedCreditsTotal(inv) > 0 ? num('Credits', appliedCreditsTotal(inv), true) : null}
                    {inv.balanceDue > 0 ? num('Balance', inv.balanceDue) : null}
                    {overpaid > 0 ? (
                      <span className="text-[var(--ink-brand)]">Overpaid <span className="font-semibold">{fmtMoney(overpaid)}</span> → credit</span>
                    ) : null}
                    {refunded > 0 ? num('Refunded', refunded) : null}
                  </>
                ) : null}
              </div>
            );
          })()}
          {renderPayments(inv)}
          {inv.notes ? <div className="text-xs text-[var(--color-muted)]">Note: {inv.notes}</div> : null}
        </div>
      </Card>
      </div>
    );
  };

  /** A collapsible invoice group (owner rule: pending on top, completed under, each expandable). */
  const renderGroup = (key: string, title: string, rows: Invoice[]): JSX.Element | null => {
    if (rows.length === 0) return null;
    const open = groupOpen[key] ?? false;
    return (
      <div className="space-y-2">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left text-sm font-semibold text-[var(--color-text)] hover:bg-[rgb(var(--edge-rgb)/0.03)]"
          onClick={() => setGroupOpen((m) => ({ ...m, [key]: !open }))}
        >
          <span className="text-xs">{open ? '▾' : '▸'}</span>
          <span>{title}</span>
          <span className="rounded-full bg-[rgb(var(--edge-rgb)/0.06)] px-2 py-0.5 text-xs font-medium text-[var(--color-muted)]">{rows.length}</span>
        </button>
        {open ? <div className="space-y-3">{rows.map(renderInvoice)}</div> : null}
      </div>
    );
  };

  const invoiceYears = collectDisplayYears(invoices.map((i) => i.issueDate));
  const effectiveYear = invoiceYears.includes(yearFilter) ? yearFilter : invoiceYears[0] ?? yearFilter;
  const yearScoped = invoiceYears.length > 1 ? invoices.filter((i) => inDisplayYear(i.issueDate, effectiveYear)) : invoices;

  // 04 #71. A ROLLED-UP row is one whose own partition is not the selected client: it was issued to a
  // linked person and is billed to this business. Every such row necessarily carries
  // billedToClientId = the selected client, and because that stamp is frozen at issue it can only
  // exist inside that person's link window, so narrowing to a person needs no date logic at all.
  const rolledUp = (inv: Invoice): boolean => !!clientId && inv.clientId !== clientId;
  // The reverse marker: this client's own invoice that somebody else owes.
  const billedElsewhere = (inv: Invoice): boolean =>
    !!clientId && inv.clientId === clientId && !!inv.billedToClientId && inv.billedToClientId !== clientId;
  const linkedAccountIds = [...new Set(yearScoped.filter(rolledUp).map((i) => i.clientId))].sort((a, b) =>
    clientAdminLabel(clientById.get(a), a).localeCompare(clientAdminLabel(clientById.get(b), b)),
  );
  const scoped = entityFilter ? yearScoped.filter((i) => i.clientId === entityFilter) : yearScoped;
  // Owed = the sum of the SERVER's per-invoice AR amounts. No accounting math here by design: the
  // figure has to agree with the AR aging report, which derives it from the shared lib, so this only
  // ever adds up numbers the server already computed.
  //
  // A receivable belongs to whoever is BILLED for it, NOT to whoever the row is listed under. On a
  // linked person's view the union deliberately shows invoices they only RECEIVED, and summing every
  // visible row charged the same debt to two entities at once. So the total keys on the frozen
  // billedToClientId exactly as AR aging groups, and anything billed elsewhere gets its own line rather
  // than vanishing from a screen that still shows the rows.
  const hasReceivable = (i: Invoice): boolean => (openReceivable[i.id] ?? 0) > 0;
  const owedHere = scoped.filter((i) => hasReceivable(i) && i.billedToClientId === clientId);
  const owedElsewhere = scoped.filter((i) => hasReceivable(i) && i.billedToClientId !== clientId);
  const sumReceivable = (rows: Invoice[]): number =>
    round2(rows.reduce((sum, i) => sum + (openReceivable[i.id] ?? 0), 0));
  const owedTotal = sumReceivable(owedHere);
  const owedCount = owedHere.length;
  const elsewhereTotal = sumReceivable(owedElsewhere);
  const elsewherePayers = [...new Set(owedElsewhere.map((i) => i.billedToClientId).filter((x): x is string => !!x))];
  const pendingInScope = scoped.filter((i) => pendingNotationSet.has(i.id)).length;

  const drafts = scoped.filter((i) => i.state === 'draft');
  const pending = scoped.filter((i) => i.state === 'issued');
  const completed = scoped.filter((i) => i.state === 'paid');
  const closed = scoped.filter((i) => i.state === 'void' || i.state === 'written-off');

  // #64 cross-payment live totals. 04 #60 WS4: the tip comes off the received figure before the
  // leftover is judged, because it settles nothing and is disposed of by nothing (it is income).
  const crossReceived = crossForm ? round2(parseFloat(crossForm.amountReceived) || 0) : 0;
  const crossTip = crossForm ? crossTipOf(crossForm) : 0;
  const crossAllocated = crossForm ? round2(crossForm.rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0)) : 0;
  const crossLeftover = round2(Math.max(0, crossReceived - crossTip - crossAllocated));

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-[var(--color-text)]">Invoices</h1>
        <div className="flex gap-2">
          {clientId ? <Button variant="secondary" onClick={() => void openPendingCredits()} disabled={busy}>Pending credits</Button> : null}
          <Button variant="secondary" onClick={openCrossPay}>Record across invoices</Button>
          <Button variant="secondary" onClick={openCompose}>Compose monthly invoice</Button>
          <Button onClick={openCreate}>New invoice</Button>
        </div>
      </div>

      <div className={`grid gap-4 ${clientId ? 'md:grid-cols-2' : ''}`}>
      <Card className="relative z-20">
        <div className="flex flex-wrap items-end gap-3 p-4">
          <Field label="Client" tip="Leave on 'All invoices' to browse every client's invoices. Pick a client to see theirs and to create one. An invoice's client cannot change after creation.">
            <select className={`${inputClass} md:min-w-[260px]`} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">All invoices</option>
              {clients.filter((c) => !c.isInternal).map((c) => (
                <option key={c.id} value={c.id}>{clientAdminName(c)}</option>
              ))}
            </select>
          </Field>
          {linkedAccountIds.length > 0 ? (
            <Field label="Linked account" tip="This client's linked people who have invoices billed to it. Narrowing to one shows only their business-billed invoices, which by definition are the ones issued while they were linked.">
              <select className={inputClass} value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}>
                <option value="">All linked accounts</option>
                <option value={clientId}>{client ? clientAdminName(client) : 'This client'} only</option>
                {linkedAccountIds.map((id) => (
                  <option key={id} value={id}>{clientAdminLabel(clientById.get(id), id)}</option>
                ))}
              </select>
            </Field>
          ) : null}
          {invoiceYears.length > 1 ? (
            <Field label="Year" tip="Shown because your invoices span multiple years. Through January, December invoices also appear under the new year; the prior year always shows its full January to December.">
              <select className={inputClass} value={String(effectiveYear)} onChange={(e) => setYearFilter(Number(e.target.value))}>
                {invoiceYears.map((y) => (
                  <option key={y} value={String(y)}>{displayYearLabel(y)}</option>
                ))}
              </select>
            </Field>
          ) : null}
        </div>
        {clientId ? (
          <div className="border-t border-[rgb(var(--edge-rgb)/0.06)] px-4 py-3">
            <p className="text-sm">
              <span className="text-[var(--color-muted)]">
                Owed{entityFilter && entityFilter !== clientId
                  ? ` for ${clientAdminLabel(clientById.get(entityFilter), entityFilter)}'s invoices`
                  : ''}
              </span>{' '}
              <span className="font-semibold text-[var(--color-text)]">{fmtMoney(owedTotal)}</span>{' '}
              <span className="text-xs text-[var(--color-muted)]">
                across {owedCount} open {owedCount === 1 ? 'invoice' : 'invoices'}
              </span>
            </p>
            <p className="text-xs text-[var(--color-muted)]">
              The receivable excluding the convenience fee, the same basis as the AR aging report, so it
              does not add up to the card-inclusive balances on the rows.
            </p>
            {owedElsewhere.length > 0 ? (
              <p className="text-xs text-[var(--color-muted)]">
                A further {fmtMoney(elsewhereTotal)} across {owedElsewhere.length}{' '}
                {owedElsewhere.length === 1 ? 'invoice' : 'invoices'} shown here is billed to{' '}
                {elsewherePayers.length === 1
                  ? clientAdminLabel(clientById.get(elsewherePayers[0]), elsewherePayers[0])
                  : 'another entity'}
                , so it is not counted above.
              </p>
            ) : null}
            {listScope === 'partition' ? (
              <p className="text-xs text-[var(--tint-warn-text)]">
                Showing only invoices issued to this client. The server did not apply the linked-account
                scope, so anything billed to this client from a linked account is missing from this list
                and from the total. Reload; if it persists, the API deploy is behind the portal.
              </p>
            ) : null}
            {pendingInScope > 0 ? (
              <p className="text-xs text-[var(--tint-warn-text)]">
                {pendingInScope} {pendingInScope === 1 ? 'invoice has' : 'invoices have'} a client-reported
                payment awaiting confirmation. Not counted above until you confirm it.
              </p>
            ) : null}
          </div>
        ) : null}
      </Card>
      </div>

      {error ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-3 text-sm text-[var(--tint-red-text)]">{error}</div> : null}
      <SaveFlash message={flash} onClear={() => setFlash(null)} />
      {composeWarnings.length > 0 ? (
        <div className="space-y-1 rounded-md bg-[var(--tint-warn-bg-soft)] p-3 text-sm text-[var(--tint-warn-text)]">
          <p className="font-medium">Review before issuing:</p>
          {composeWarnings.map((w, i) => (
            <p key={i}>{w}</p>
          ))}
          <button type="button" className="text-xs underline" onClick={() => setComposeWarnings([])}>Dismiss</button>
        </div>
      ) : null}

      {formOpen && form ? (
        <div ref={formCardRef}>
        <Card>
          <div className="space-y-3 p-4">
            <h2 className="text-base font-semibold text-[var(--color-text)]">
              {editing ? `Edit draft${editing.documentNumber ? ` ${editing.documentNumber}` : ''}` : 'New invoice'}
              {editing && editing.billingType === 'monthly' && editing.coversPeriodStart ? (
                <span className="ml-2 text-sm font-normal italic text-[var(--color-muted)]">{coverageLabel(editing)}</span>
              ) : null}
            </h2>

            {editing ? (
              <Field label="Client" tip="An invoice's client is fixed once created (§7.15).">
                <div className="px-3 py-2 text-sm text-[var(--color-text)]">{clientAdminLabel(clientById.get(editing.clientId), editing.clientId)}</div>
              </Field>
            ) : (
              <Field label="Client" required tip="Who this invoice bills. Fixed once created. The house (internal) client is never billed.">
                <select className={inputClass} value={form.clientId} onChange={(e) => setFormClient(e.target.value)}>
                  <option value="">Select a client…</option>
                  {clients.filter((c) => !c.isInternal).map((c) => (
                    <option key={c.id} value={c.id}>{clientAdminName(c)}</option>
                  ))}
                </select>
              </Field>
            )}

            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Billing type" tip="Invoice-level: 'Monthly' means this bill belongs to the client's monthly cycle and may carry Recurring lines; 'One-off' is a standalone invoice (all lines are one-time). Automated monthly billing runs from Stage 5; for now this is a label.">
                <select className={inputClass} value={form.billingType} onChange={(e) => setBillingType(e.target.value as 'monthly' | 'one-off')}>
                  <option value="one-off">One-off</option>
                  <option value="monthly">Monthly</option>
                </select>
              </Field>
              <Field label="Issue date" required tip="The invoice's issue date. Its number and due date are set from this on issue.">
                <input type="date" className={inputClass} value={form.issueDate} onChange={(e) => update({ issueDate: e.target.value })} />
              </Field>
              <Field label="Payment terms" tip="Overridable per invoice (§7.3). Due date = issue date + terms.">
                <select className={inputClass} value={form.paymentTerms} onChange={(e) => update({ paymentTerms: e.target.value as PaymentTerms })}>
                  {TERMS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </Field>
              {/* 04 #69 (spec 7.24): the per-invoice early-pay override. Resolved (invoice, else client,
                  else brand) and frozen with its deadline when the invoice is issued. */}
              <Field label="Early-pay discount" tip="Overridable per invoice (spec 7.24). Inherit uses the client's override, else the brand default in Settings. The terms and the deadline are set on the invoice when it is issued and never change after.">
                <select
                  className={inputClass}
                  value={form.earlyPayMode}
                  onChange={(e) => update({ earlyPayMode: e.target.value as FormState['earlyPayMode'] })}
                >
                  <option value="">Inherit (client, else brand)</option>
                  <option value="custom">Set for this invoice</option>
                  <option value="none">No discount on this invoice</option>
                </select>
              </Field>
            </div>
            {form.earlyPayMode === 'custom' ? (
              <div className="grid gap-3 md:grid-cols-3">
                <Field label="Percent off" tip="Figured on the ordinary lines after any invoice discount; never on tax, the convenience fee or charge-class lines.">
                  <input className={inputClass} inputMode="decimal" placeholder="0 = no discount" value={form.earlyPayRatePct} onChange={(e) => update({ earlyPayRatePct: e.target.value })} />
                </Field>
                <Field label="Days from the invoice date" tip="Whole calendar days in the brand's time zone, counted from the issue date, last day included. 0 = only a payment received on the invoice date qualifies.">
                  <input className={inputClass} inputMode="numeric" placeholder="10" value={form.earlyPayDays} onChange={(e) => update({ earlyPayDays: e.target.value })} />
                </Field>
              </div>
            ) : null}
            <p className="text-xs text-[var(--color-muted)]">Due {displayDate(previewDueDate(form.issueDate, form.paymentTerms))} once issued.</p>

            {/* Line items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-sm font-medium text-[var(--color-text)]">
                  Line items
                  <InfoTip text="Charge is per unit (line total = charge × quantity); cost is internal and never shown to the client. 'Add unbilled items' pulls the client's unbilled billable time, mileage, and expense lines (§7.11); pulling marks the source billed immediately; a pulled line's cost and quantity are locked from the source, while its description and charge stay editable." />
                </div>
                <div className="flex flex-col items-end gap-1">
                  {pickerGateError ? <span className="text-xs text-[var(--tint-red-text)]">{pickerGateError}</span> : null}
                  <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => void openPicker()} disabled={saving || pickerOpen}>Add unbilled items</Button>
                    <Button variant="secondary" onClick={addLine} disabled={saving}>Add line</Button>
                  </div>
                </div>
              </div>
              {form.lines.map((row, idx) => row.chargeClassId ? (
                /* 04 #60 WS2: a CHARGE-CLASS row - money is derived, never typed. The amount shown
                   is the live preview mirror's figure; the server recomputes it on every save. */
                <div key={row.lineId ?? `cls-${row.chargeClassId}`} className="grid gap-2 md:grid-cols-[2fr,0.6fr,0.8fr,0.8fr,1fr,auto] md:items-end">
                  <Field label={`Description (charge: ${row.chargeClass?.name ?? 'unknown class'})`} required>
                    <input className={inputClass} value={row.description} onChange={(e) => updateLine(idx, { description: e.target.value })} maxLength={500} />
                  </Field>
                  <Field label="Qty"><div className="px-3 py-2 text-sm text-[var(--color-muted)]">1</div></Field>
                  <Field label="Cost ($)"><div className="px-3 py-2 text-sm text-[var(--color-muted)]">-</div></Field>
                  <Field label="Charge ($)" tip="Computed from this charge class's rate and the invoice's other lines; it updates as you edit and is never typed.">
                    <div className="px-3 py-2 text-sm">{fmtMoney(preview?.lineAmounts[idx] ?? 0)}</div>
                  </Field>
                  <Field label="Kind"><div className="px-3 py-2 text-sm text-[var(--color-muted)]">Charge</div></Field>
                  <PillButton tone="red" onClick={() => { setReplaceIdx(null); removeLine(idx); }}>Remove</PillButton>
                  <div className="flex flex-col gap-1 md:col-span-6">
                    {row.chargeClass ? (
                      <p className="text-xs text-[var(--color-muted)]">{chargeClassHint(row.chargeClass)}</p>
                    ) : (
                      <p className="text-xs text-[var(--tint-red-text)]">This brand no longer defines this charge class. Remove the line before saving.</p>
                    )}
                    {row.chargeClass && !row.chargeClass.active && row.chargeClassAcknowledged ? (
                      <div className="rounded-md bg-[var(--tint-warn-bg-soft)] p-2 text-xs text-[var(--tint-warn-text)]">
                        <p>
                          This charge class was deactivated{row.chargeClass.deactivatesAt ? ` on ${formatIsoDay(row.chargeClass.deactivatesAt)}` : ''} and you chose to charge it anyway:
                          it is charged as it stands and issue will not ask again.
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <PillButton tone="neutral" onClick={() => acknowledgeChargeClass(idx, false)}>Reconsider</PillButton>
                        </div>
                      </div>
                    ) : null}
                    {row.chargeClass && !row.chargeClass.active && !row.chargeClassAcknowledged ? (
                      <div className="rounded-md bg-[var(--tint-warn-bg-soft)] p-2 text-xs text-[var(--tint-warn-text)]">
                        <p className="font-medium">This charge class was deactivated{row.chargeClass.deactivatesAt ? ` on ${formatIsoDay(row.chargeClass.deactivatesAt)}` : ''} after this draft was created, so the invoice cannot be issued with it until you decide what to do.</p>
                        <p className="mt-1">Charging it anyway keeps the class exactly as it stands (deactivation never changes values) and stops issue asking about it. Reactivating it puts it back on every future invoice for this brand.</p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <PillButton tone="red" onClick={() => { setReplaceIdx(null); removeLine(idx); }}>Remove from this draft</PillButton>
                          <PillButton tone="amber" onClick={() => { setReplaceIdx(null); acknowledgeChargeClass(idx, true); }}>Charge it anyway</PillButton>
                          <PillButton tone="neutral" onClick={() => setReplaceIdx(replaceIdx === idx ? null : idx)}>Replace with another class</PillButton>
                          <PillButton tone="neutral" onClick={() => navigate('/admin/settings')}>Reactivate in Settings</PillButton>
                          <PillButton tone="amber" onClick={() => { setReplaceIdx(null); convertChargeClassRow(idx, preview?.lineAmounts[idx] ?? 0); }}>Convert to a plain line</PillButton>
                        </div>
                        <p className="mt-1 text-[11px]">Converting keeps the amount as an ordinary line: it books to Service Revenue instead of the class account, becomes discountable, and is taxed by the line's own flag.</p>
                        {replaceIdx === idx ? (() => {
                          const cid = editing ? editing.clientId : form.clientId;
                          const options = activeClassesForClient(cid).filter((c) => !form.lines.some((r) => r.chargeClassId === c.id));
                          return (
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {options.length > 0 ? (
                                <select className={`${inputClass} w-auto`} value="" onChange={(e) => { const cls = options.find((c) => c.id === e.target.value); if (cls) { replaceChargeClass(idx, cls); setReplaceIdx(null); } }} aria-label="Replacement charge class">
                                  <option value="">Choose a replacement...</option>
                                  {options.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.rateType === 'percent' ? `${round2(c.value * 100)}%` : fmtMoney(c.value)})</option>)}
                                </select>
                              ) : (
                                <span>No other active class fits yet.</span>
                              )}
                              <PillButton tone="neutral" onClick={() => navigate('/admin/settings')}>Create a new class in Settings</PillButton>
                              <PillButton tone="neutral" onClick={() => setReplaceIdx(null)}>Back</PillButton>
                            </div>
                          );
                        })() : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : row.creditReason ? (
                /* 04 #45 O5: a composed CREDIT row - read-only, its negative face. Removing it defers the
                   credit: the next compose re-derives the entitlement from the issued lines. */
                <div key={row.lineId ?? `credit-${idx}`} className="grid gap-2 md:grid-cols-[2fr,0.6fr,0.8fr,0.8fr,1fr,auto] md:items-end">
                  <Field label="Description (credit)">
                    <div className="px-3 py-2 text-sm">{row.description}</div>
                  </Field>
                  <Field label="Qty"><div className="px-3 py-2 text-sm text-[var(--color-muted)]">1</div></Field>
                  <Field label="Cost ($)"><div className="px-3 py-2 text-sm text-[var(--color-muted)]">-</div></Field>
                  <Field label="Charge ($)" tip="Composed from the issued invoice that billed these days (its net x the credited days / its covered days), less any credit already issued for them. Never typed.">
                    <div className="px-3 py-2 text-sm">{fmtMoney(parseFloat(row.charge) || 0)}</div>
                  </Field>
                  <Field label="Kind"><div className="px-3 py-2 text-sm text-[var(--color-muted)]">Credit</div></Field>
                  <PillButton tone="red" onClick={() => removeLine(idx)}>Remove</PillButton>
                  <p className="text-xs text-[var(--color-muted)] md:col-span-6">
                    {row.creditReason === 'catalog-disabled' ? 'Days the service or package was globally disabled' : row.creditReason === 'suspended-time' ? 'Days the account was suspended for non-payment (credited by brand setting)' : 'Days after a package switch or adoption replaced this unit'}
                    {row.coversFrom && row.coversTo ? ` (${formatIsoDay(row.coversFrom)} to ${formatIsoDay(row.coversTo)})` : ''}.{typeof row.creditTaxAmount === 'number' && row.creditTaxAmount !== 0 ? ` Includes ${fmtMoney(row.creditTaxAmount)} of the tax charged on the credited days, reversed at the rate it was charged at.` : ''} Removing this line defers the credit to the next composed invoice; it is never lost.
                  </p>
                </div>
              ) : (
                <div key={row.lineId ?? `new-${idx}`} className="grid gap-2 md:grid-cols-[2fr,0.6fr,0.8fr,0.8fr,1fr,auto] md:items-end">
                  <Field label={row.pulled ? `Description (pulled · ${row.sourceLabel})` : 'Description'} required>
                    <input className={inputClass} value={row.description} onChange={(e) => updateLine(idx, { description: e.target.value })} maxLength={500} />
                  </Field>
                  <Field label="Qty" required={!row.pulled} tip={row.pulled ? 'Locked: a pulled item is one unit of its source charge.' : undefined}>
                    {row.pulled ? (
                      <div className="px-3 py-2 text-sm text-[var(--color-muted)]">1</div>
                    ) : (
                      <input className={inputClass} inputMode="decimal" value={row.qty} onChange={(e) => updateLine(idx, { qty: e.target.value })} />
                    )}
                  </Field>
                  <Field label="Cost ($)" tip={row.pulled ? 'Read-only from the source record (§7.11). The cost lives once, on the time/mileage/expense entry.' : 'Internal only, never shown to the client.'}>
                    {row.pulled ? (
                      <div className="px-3 py-2 text-sm text-[var(--color-muted)]">{row.cost || '0'}</div>
                    ) : (
                      <input className={inputClass} inputMode="decimal" value={row.cost} onChange={(e) => updateLine(idx, { cost: e.target.value })} />
                    )}
                  </Field>
                  <Field label="Charge ($)" required tip={row.pulled ? 'Prefilled from the source snapshot; adjustable for this invoice only. The source keeps its own numbers.' : undefined}>
                    <input className={inputClass} inputMode="decimal" value={row.charge} onChange={(e) => updateLine(idx, { charge: e.target.value })} />
                  </Field>
                  <Field label="Kind" tip={row.pulled ? 'A pulled charge is one-time by nature.' : "Line-level: 'One-time' appears once; 'Recurring' carries onto future monthly invoices (offered only on a Monthly invoice, since a One-off never recurs). Recurring automation arrives at Stage 5."}>
                    {row.pulled ? (
                      <div className="px-3 py-2 text-sm text-[var(--color-muted)]">One-time</div>
                    ) : (
                      <select className={inputClass} value={row.kind} onChange={(e) => updateLine(idx, { kind: e.target.value as LineItemKind })}>
                        <option value="one-time">One-time</option>
                        {form.billingType === 'monthly' ? <option value="recurring">Recurring</option> : null}
                      </select>
                    )}
                  </Field>
                  <PillButton tone="red" onClick={() => removeLine(idx)}>Remove</PillButton>
                  {/* Slice 4: per-line tax exclusion (04 A9) + per-line discount — billing
                      adjustments, allowed on pulled lines too (never written back to a source).
                      04 #160 WS-C M1: the tax category (manual + pulled rows), the resolution the server
                      stored, the checkbox as an OVERRIDE, and "Use the rule" to drop one. */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 md:col-span-6">
                    {row.taxCategory !== undefined ? (
                      <label className="flex items-center gap-2 text-xs">
                        <span>Tax category</span>
                        <select
                          className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-xs"
                          value={row.taxCategory}
                          onChange={(e) => { const v = e.target.value; if (isCatalogTaxCategory(v)) updateLine(idx, { taxCategory: v, taxableFraction: undefined, taxRuleId: undefined, taxExemptReason: undefined }); }}
                        >
                          {TAX_CATEGORY_OPTIONS.map(([v, label]) => (
                            <option key={v} value={v}>{label}</option>
                          ))}
                        </select>
                        <InfoTip text="What this line is for sales tax. The client's state decides whether, and how much of, this category is taxable on the issue date; the line is resolved again on every save and once more at issue. A composed service line takes its catalog service's category." />
                      </label>
                    ) : row.taxCategoryFixed ? (
                      <span className="text-xs text-[var(--color-muted)]">Tax category: {row.taxCategoryFixed} (from the catalog)</span>
                    ) : null}
                    {form.taxEnabled ? (() => {
                      const st = lineTaxStatus(row, taxGate, formBrandName, form.taxRate);
                      return (
                        <span className="flex flex-wrap items-center gap-2 text-xs">
                          {st.checkbox ? (
                            <label className="flex items-center gap-2">
                              <input type="checkbox" checked={!row.taxable} onChange={(e) => updateLine(idx, { taxable: !e.target.checked, taxTouched: true, useTaxRule: false })} />
                              <span>Exclude tax</span>
                            </label>
                          ) : null}
                          <span className="text-[var(--color-muted)]">{st.text}</span>
                          {st.useRule ? (
                            <PillButton onClick={() => updateLine(idx, { useTaxRule: true, taxTouched: false, taxMode: 'rule', taxableFraction: undefined, taxRuleId: undefined, taxExemptReason: undefined })}>Use the rule</PillButton>
                          ) : null}
                          <InfoTip text="Each line is taxed by the client's state rule for its category, resolved on the issue date. Ticking Exclude tax (or un-ticking it) makes the line a manual override that the rules never touch again; Use the rule hands it back." />
                        </span>
                      );
                    })() : null}
                    <label className="flex items-center gap-2 text-xs">
                      <span>Line discount</span>
                      <select
                        className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-xs"
                        value={row.discType}
                        onChange={(e) => updateLine(idx, { discType: e.target.value as 'none' | DiscountType })}
                      >
                        <option value="none">None</option>
                        <option value="flat">Flat ($)</option>
                        <option value="percent">Percent (%)</option>
                      </select>
                      {row.discType !== 'none' ? (
                        <input
                          className="w-20 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] px-2 py-1 text-xs"
                          inputMode="decimal"
                          value={row.discValue}
                          onChange={(e) => updateLine(idx, { discValue: e.target.value })}
                          placeholder={row.discType === 'percent' ? '%' : '$'}
                        />
                      ) : null}
                      <InfoTip text="Reduces this line only, before the invoice-level discount and tax." />
                    </label>
                  </div>
                </div>
              ))}
              {form.lines.some((r) => r.pulled) ? (
                <p className="text-xs text-[var(--color-muted)]">
                  Removing a pulled line returns its source to the unbilled pool when you save. Deleting the whole draft releases them all.
                </p>
              ) : null}
              {form.lines.length === 0 ? (
                <p className="text-xs text-[var(--color-muted)]">No lines yet. Add at least one before issuing.</p>
              ) : null}
            </div>

            {/* Discount + tax */}
            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-3">
                <Field label="Discount" tip="Invoice-level discount (§7.12), applied to the subtotal before tax.">
                  <select className={inputClass} value={form.discountType} onChange={(e) => update({ discountType: e.target.value as 'none' | DiscountType })}>
                    <option value="none">None</option>
                    <option value="flat">Flat ($)</option>
                    <option value="percent">Percent (%)</option>
                  </select>
                </Field>
                {/* 04 #60 WS2: per-invoice application of a brand charge class (spec 7.23). Lists the
                    ACTIVE classes not yet on this draft; deactivated ones never appear here. */}
                {(() => {
                  const cid = editing ? editing.clientId : form.clientId;
                  const available = activeClassesForClient(cid).filter((c) => !form.lines.some((r) => r.chargeClassId === c.id));
                  if (available.length === 0) return null;
                  return (
                    <Field label="Charge class" tip="Adds one of this brand's charge classes (a service charge, program fee, shipping) as its own line. The amount is computed from the class's rate; it can be removed from this invoice before issue.">
                      <select
                        className={inputClass}
                        value=""
                        disabled={saving}
                        onChange={(e) => { const cls = available.find((c) => c.id === e.target.value); if (cls) applyChargeClass(cls); }}
                        aria-label="Add charge class"
                      >
                        <option value="">Add charge class…</option>
                        {available.map((c) => (
                          <option key={c.id} value={c.id}>{c.name}{c.autoApply ? '' : ' (manual)'}</option>
                        ))}
                      </select>
                    </Field>
                  );
                })()}
              </div>
              {form.discountType !== 'none' ? (
                <Field label={form.discountType === 'percent' ? 'Discount %' : 'Discount $'} required>
                  <input className={inputClass} inputMode="decimal" value={form.discountValue} onChange={(e) => update({ discountValue: e.target.value })} />
                </Field>
              ) : <div />}
              <Field label="Tax" tip="Off by default (§7.7). The rate resolves automatically: this invoice's override, then the client's override, then the client's address jurisdiction, then the brand's backup jurisdiction. Touching these controls makes it an explicit override for this invoice; leave them alone and the server resolution applies. Goods are taxable; services and labor are not, and each line can be excluded below.">
                <div className="space-y-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.taxEnabled} onChange={(e) => update({ taxEnabled: e.target.checked, taxTouched: true })} />
                    <span>Charge tax</span>
                    {form.taxEnabled ? (
                      <input className={`${inputClass} max-w-[6rem]`} inputMode="decimal" value={form.taxRate} onChange={(e) => update({ taxRate: e.target.value, taxTouched: true })} placeholder="%" />
                    ) : null}
                  </label>
                  {form.taxTouched ? (
                    <p className="text-xs text-[var(--color-muted)]">Overridden for this invoice.</p>
                  ) : editing && editing.tax.source ? (
                    <p className="text-xs text-[var(--color-muted)]">{editing.tax.source === 'invoice-override' ? 'Rate set on this invoice.' : `Rate from ${TAX_SOURCE_LABEL[editing.tax.source]}.`}</p>
                  ) : !editing && (datedTax ?? resolvedTax) && form.clientId ? (
                    <p className="text-xs text-[var(--color-muted)]">{(datedTax ?? resolvedTax)?.description}.</p>
                  ) : null}
                  {/* 04 #160 WS-C M1 (02i §8 DP-9 / DP-10): the invoice-level gate the server resolved. */}
                  {form.taxEnabled && taxGate === 'not-registered' ? (
                    <p className="text-xs text-[var(--tint-warn-text)]">No sales tax will be collected: {formBrandName} is not registered to collect at this client's address (Settings, Tax registrations). The invoice prints: "No sales tax was collected on this invoice. {formBrandName} is not registered to collect sales tax in this state; you may owe use tax to your state."</p>
                  ) : form.taxEnabled && taxGate === 'exempt-client' ? (
                    <p className="text-xs text-[var(--color-muted)]">No sales tax will be collected: the client holds a tax exemption certificate in effect on the issue date.</p>
                  ) : null}
                </div>
              </Field>
            </div>

            <Field label="Notes" tip="Optional note shown on the invoice.">
              <textarea className={`${inputClass} min-h-[64px]`} value={form.notes} onChange={(e) => update({ notes: e.target.value })} maxLength={2000} />
            </Field>

            {/* Live totals preview */}
            {preview ? (
              <div className="rounded-md bg-[rgb(var(--edge-rgb)/0.03)] p-3 text-sm">
                {preview.lineDiscountTotal > 0 ? (
                  <div className="flex justify-between"><span className="text-[var(--color-muted)]">Line discounts</span><span>-{fmtMoney(preview.lineDiscountTotal)}</span></div>
                ) : null}
                {preview.chargeSubtotal > 0 || form.lines.some((r) => r.chargeClassId) ? (
                  <>
                    <div className="flex justify-between"><span className="text-[var(--color-muted)]">Ordinary lines</span><span>{fmtMoney(preview.ordinarySubtotal)}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--color-muted)]">Charges</span><span>{fmtMoney(preview.chargeSubtotal)}</span></div>
                  </>
                ) : null}
                <div className="flex justify-between"><span className="text-[var(--color-muted)]">Subtotal</span><span>{fmtMoney(preview.subtotal)}</span></div>
                {preview.discountAmount > 0 ? (
                  <div className="flex justify-between"><span className="text-[var(--color-muted)]">Discount</span><span>-{fmtMoney(preview.discountAmount)}</span></div>
                ) : null}
                {form.taxEnabled ? (
                  <div className="flex justify-between"><span className="text-[var(--color-muted)]">Tax</span><span>{fmtMoney(preview.taxAmount)}</span></div>
                ) : null}
                {preview.feeAmount > 0 ? (
                  <>
                    <div className="flex justify-between"><span className="text-[var(--color-muted)]">Convenience fee</span><span>{fmtMoney(preview.feeAmount)}</span></div>
                    <div className="mt-1 flex justify-between border-t border-[rgb(var(--edge-rgb)/0.10)] pt-1 font-semibold">
                      <span>Total (w/convenience fee)</span><span>{fmtMoney(preview.totalWithConvenienceFee)}</span>
                    </div>
                    <div className="flex justify-between text-[var(--color-muted)]">
                      <span>Total by Cash or Zelle *</span><span>{fmtMoney(preview.totalWithoutConvenienceFee)}</span>
                    </div>
                    <div className="text-[11px] text-[var(--color-muted)]">* Check or money order are accepted at this same total.</div>
                  </>
                ) : (
                  <div className="mt-1 flex justify-between border-t border-[rgb(var(--edge-rgb)/0.10)] pt-1 font-semibold">
                    <span>Total</span><span>{fmtMoney(preview.totalWithoutConvenienceFee)}</span>
                  </div>
                )}
              </div>
            ) : null}

            {formError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{formError}</div> : null}
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void submit(false)} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save draft' : 'Create draft'}</Button>
              <Button variant="secondary" onClick={() => void submit(true)} disabled={saving}>{editing ? 'Save and issue' : 'Issue now'}</Button>
              <Button variant="ghost" onClick={cancelForm} disabled={saving}>Cancel</Button>
            </div>
          </div>
        </Card>
        </div>
      ) : null}

      {listLoading ? (
        <div className="p-4 text-sm text-[var(--color-muted)]">Loading invoices…</div>
      ) : invoices.length === 0 ? (
        <div className="rounded-md border border-dashed border-[rgb(var(--edge-rgb)/0.10)] p-6 text-center text-sm text-[var(--color-muted)]">
          {clientId ? (
            <div className="flex flex-wrap items-center justify-center gap-2">
              <span>No invoices yet{client ? ` for ${clientAdminName(client)}` : ''}. Create a</span>
              <Button onClick={openCreate}>New invoice</Button>
            </div>
          ) : (
            'No invoices across any client. Choose a client above to create one.'
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {!clientId ? (
            <p className="text-xs text-[var(--color-muted)]">Showing all invoices across every client. Choose a client to scope the list and add an invoice.</p>
          ) : null}
          {renderGroup('drafts', 'Drafts', drafts)}
          {renderGroup('pending', 'Awaiting payment', pending)}
          {renderGroup('paid', 'Paid', completed)}
          {renderGroup('closed', 'Void & written off', closed)}
        </div>
      )}

      {issueTarget ? (
        <ConfirmDialog
          open={true}
          title="Issue this invoice?"
          confirmLabel="Issue"
          busy={busy}
          onConfirm={doIssue}
          onCancel={() => setIssueTarget(null)}
        >
          <p>
            Issuing assigns a permanent invoice number and sends the client a notification. Once issued, the
            invoice is locked. Corrections happen by voiding or a credit note (later), not by editing. Continue?
          </p>
        </ConfirmDialog>
      ) : null}

      {payForm ? (
        <ConfirmDialog
          open={true}
          title="Record a payment"
          confirmLabel="Record payment"
          busy={busy}
          onConfirm={() => doRecordPayment()}
          onCancel={() => setPayForm(null)}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Recording against {payForm.invoice.documentNumber ?? 'this invoice'}. The remaining balance is <span className="font-semibold">{fmtMoney(nonCardRemaining(payForm.invoice))}</span> by Zelle, check, or money order. A partial payment keeps the invoice open until the balance reaches 0.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Amount received" required tip={tipsOn(brandForInvoice(payForm.invoice)) ? 'The total that arrived, tip included. A tip entered below is taken out of it; the rest is applied to the invoice.' : 'The total that arrived. It is applied to the invoice.'}>
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={payForm.amount}
                  onChange={(e) => setPayForm((f) => (f ? { ...f, amount: e.target.value, cashEdited: true } : f))}
                />
              </Field>
              <Field label="Method" required>
                <select
                  className={inputClass}
                  value={payForm.method}
                  onChange={(e) => setPayForm((f) => (f ? { ...f, method: e.target.value as PaymentMethod } : f))}
                >
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Date received" required>
                <input
                  type="date"
                  className={inputClass}
                  value={payForm.dateReceived}
                  onChange={(e) => setPayForm((f) => (f ? { ...f, dateReceived: e.target.value } : f))}
                />
              </Field>
              <Field label="Receipt / reference #" tip="Optional: the Zelle confirmation or check number.">
                <input
                  className={inputClass}
                  value={payForm.receiptNumber}
                  maxLength={100}
                  onChange={(e) => setPayForm((f) => (f ? { ...f, receiptNumber: e.target.value } : f))}
                />
              </Field>
            </div>
            <Field label="Memo" tip="Optional note kept on the payment record.">
              <input
                className={inputClass}
                value={payForm.memo}
                maxLength={500}
                onChange={(e) => setPayForm((f) => (f ? { ...f, memo: e.target.value } : f))}
              />
            </Field>
            {/* 04 #69 (spec 7.24): the early-pay discount, OFFERED and explicitly confirmed (rule 6), every
                figure from the server quote (02 s97), re-quoted on each edit of the date or the cash. */}
            {hasEarlyPayTerms(payForm.invoice) ? (
              payForm.earlyPay === null ? (
                <p className="text-xs text-[var(--color-muted)]">Checking the early-pay discount...</p>
              ) : (
                <div className={`space-y-1 rounded-md border p-2 text-sm ${payForm.earlyPay.eligible ? 'border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)]' : 'border-[rgb(var(--edge-rgb)/0.10)]'}`}>
                  <div className="text-xs text-[var(--color-muted)]">
                    Early-pay terms: {pctLabel(payForm.invoice.earlyPayDiscount!.rate)}% within {payForm.invoice.earlyPayDiscount!.days} day{payForm.invoice.earlyPayDiscount!.days === 1 ? '' : 's'} of the invoice date, through {displayDate(payForm.invoice.earlyPayDiscount!.dueBy ?? null)}
                    {payForm.invoice.earlyPayDiscount!.allowPartial ? ', partial payments earn a pro-rata share' : ', payment in full only'}.
                    {payForm.earlyPayLoading ? ' Re-checking...' : ''}
                  </div>
                  {payForm.earlyPay.eligible ? (
                    <label className="flex items-start gap-2">
                      <input type="checkbox" className="mt-0.5" checked={payForm.applyEarlyPay} onChange={(e) => setPayApplyEarlyPay(e.target.checked)} />
                      <span>
                        Apply the early-pay discount of <span className="font-semibold">{fmtMoney(payForm.earlyPay.discountGross)}</span>
                        {payForm.earlyPay.taxReversed > 0 ? ` (${fmtMoney(payForm.earlyPay.discountNet)} plus ${fmtMoney(payForm.earlyPay.taxReversed)} of its sales tax)` : ''}
                        {' '}as a credit note against the invoice.
                        <span className="block text-xs text-[var(--color-muted)]">{payForm.earlyPay.statement}.{payForm.earlyPay.payInFullAmount != null ? ` Pays in full at ${fmtMoney(payForm.earlyPay.payInFullAmount)}.` : ''}</span>
                      </span>
                    </label>
                  ) : (
                    <p className="text-xs text-[var(--color-muted)]">{payForm.earlyPay.statement}.{payForm.earlyPay.outcome === 'requires-full-payment' && payForm.earlyPay.payInFullAmount != null ? ` The discount needs ${fmtMoney(payForm.earlyPay.payInFullAmount)} received by ${displayDate(payForm.earlyPay.terms?.dueBy ?? null)}.` : ''}</p>
                  )}
                </div>
              )
            ) : null}
            {/* 04 #60 WS4: the voluntary tip, offered only when the brand accepts tips (spec 7.23). It is
                recorded on the payment only: the invoice, its totals and its balance never move. */}
            {tipsOn(brandForInvoice(payForm.invoice)) ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Tip" tip="Optional voluntary tip inside the amount received. It is taken out before the rest is applied to the invoice. Recorded on the payment only: it never changes the invoice, is never taxed, and is reversed only by voiding the payment.">
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={payForm.tip}
                    onChange={(e) => setPayTip(e.target.value)}
                  />
                </Field>
                {tipClassesFor(brandForInvoice(payForm.invoice)).length > 0 ? (
                  <Field label="Book the tip to" tip="Where the tip is booked: the default Tip income account, or one of this brand's active charge classes (its income account; a class collected on behalf of someone makes the tip owed to that payee instead of income). This only names the tip; the invoice's own charge-class lines are unaffected.">
                    <select
                      className={inputClass}
                      value={payForm.tipChargeClassId}
                      onChange={(e) => setPayForm((f) => (f ? { ...f, tipChargeClassId: e.target.value } : f))}
                    >
                      <option value="">Tip income (default)</option>
                      {tipClassesFor(brandForInvoice(payForm.invoice)).map((c) => <option key={c.id} value={c.id}>{tipClassLabel(c)}</option>)}
                    </select>
                  </Field>
                ) : null}
                {/* What faces the invoice once the tip is out. A hand-edited amount that lands short of
                    what would settle the invoice gets a NOTICE with a one-click fix, not a muted hint:
                    the admin may have typed the balance and then the tip, and recording as-is leaves the
                    invoice open (the owner's s175 finding). */}
                {payTip(payForm) > 0 ? (
                  payApplied(payForm) <= 0 ? (
                    <p className="text-sm text-[var(--tint-red-text)] sm:col-span-2">The tip must be less than the amount received.</p>
                  ) : payApplied(payForm) + 0.001 < payCashToSettle(payForm) ? (
                    <div className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] p-2 text-sm text-[var(--tint-warn-text)] sm:col-span-2">
                      <p>
                        After the {fmtMoney(payTip(payForm))} tip, only <span className="font-semibold">{fmtMoney(payApplied(payForm))}</span> of the {fmtMoney(round2(parseFloat(payForm.amount) || 0))} received reaches the invoice, leaving <span className="font-semibold">{fmtMoney(round2(payCashToSettle(payForm) - payApplied(payForm)))}</span> unpaid. Record as-is for a partial payment, or:
                      </p>
                      <button
                        type="button"
                        className="mt-1 font-medium underline decoration-dotted underline-offset-2 hover:decoration-solid"
                        onClick={() => setPayForm((f) => (f ? { ...f, amount: String(round2(payCashToSettle(f) + payTip(f))), cashEdited: true } : f))}
                      >
                        The client sent the balance plus the tip: set the amount received to {fmtMoney(round2(payCashToSettle(payForm) + payTip(payForm)))}
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm sm:col-span-2">
                      Applied to the invoice <span className="font-semibold">{fmtMoney(payApplied(payForm))}</span>; {fmtMoney(payTip(payForm))} tip.
                    </p>
                  )
                ) : null}
              </div>
            ) : null}
            {payForm.creditsLoading ? (
              <p className="text-xs text-[var(--color-muted)]">Checking for available account credit...</p>
            ) : payForm.credits.length > 0 ? (
              <div className="space-y-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-2">
                <p className="text-sm">
                  Available account credit <span className="font-semibold">{fmtMoney(round2(payForm.credits.reduce((s, c) => s + c.remaining, 0)))}</span>.
                  Enter how much of each credit funds this payment; the cash amount adjusts to cover the rest.
                </p>
                {payForm.credits.map((c) => (
                  <div key={c.creditId} className="space-y-1 text-sm">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium whitespace-nowrap">{c.documentNumber ?? c.creditId}</span>
                      <span className="text-[var(--color-muted)]">{fmtMoney(c.remaining)} remaining</span>
                    </div>
                    <input
                      className={inputClass}
                      inputMode="decimal"
                      placeholder="0.00"
                      value={c.amount}
                      onChange={(e) => setPayCreditAmount(c.creditId, e.target.value)}
                    />
                  </div>
                ))}
                {payCreditTotal(payForm) > 0 ? (
                  <p className="text-xs text-[var(--color-muted)]">
                    Credit {fmtMoney(payCreditTotal(payForm))} + cash {fmtMoney(round2(parseFloat(payForm.amount) || 0))} of the {fmtMoney(nonCardRemaining(payForm.invoice))} balance. A credit-funded payment can't overpay; to keep extra cash as new account credit, clear the credit fields first.
                  </p>
                ) : null}
              </div>
            ) : null}
            {payCreditTotal(payForm) <= 0 && Number.isFinite(parseFloat(payForm.amount)) && payApplied(payForm) > payTarget(payForm) ? (
              <label className="flex items-start gap-2 rounded-md border border-[var(--tint-red-border)] bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={payForm.overpaymentToCredit}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    // Owner fix (s108): unchecking LOWERS the amount to the balance immediately,
                    // as the copy promises, instead of arming a submit-time error. 04 #60 WS4: the
                    // amount received keeps the tip on top of the balance.
                    setPayForm((f) =>
                      f
                        ? checked
                          ? { ...f, overpaymentToCredit: true }
                          : { ...f, overpaymentToCredit: false, amount: String(round2(payTarget(f) + payTip(f))), cashEdited: true }
                        : f,
                    );
                  }}
                />
                <span>
                  This is <span className="font-semibold">{fmtMoney(round2(payApplied(payForm) - payTarget(payForm)))}</span> more than the {fmtMoney(payTarget(payForm))} balance{payTip(payForm) > 0 ? ' after the tip' : ''}{payDiscountGross(payForm) > 0 ? ' after the early-pay discount' : ''}. Convert the extra to a client credit they can use on a future invoice. Uncheck to lower the amount to the balance instead.
                </span>
              </label>
            ) : null}
            <p className="text-xs text-[var(--color-muted)]">
              Recording assigns a permanent payment number. A recorded payment can't be edited; correct a
              mistake by voiding it and recording again.
            </p>
            {payError ? <div ref={payErrorRef} className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{payError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {/* 04 #69 rule 17: confirming a client-reported payment on an invoice that carries early-pay terms
          states the resulting state (the server's sentence) and offers the discount explicitly; the
          reported amount is immutable, only the date received and the reference can be set here. */}
      {notationConfirm ? (
        <ConfirmDialog
          open={true}
          title="Confirm the reported payment"
          confirmLabel="Confirm received"
          busy={busy || notationConfirm.quoteLoading}
          onConfirm={() => {
            if (!notationConfirm.dateReceived) { setNotationError('A date received is required.'); return; }
            void doConfirmNotation(notationConfirm.payment, {
              dateReceived: notationConfirm.dateReceived,
              receiptNumber: notationConfirm.receiptNumber.trim() ? notationConfirm.receiptNumber.trim() : null,
              ...(notationConfirm.applyEarlyPay && notationConfirm.quote.eligible ? { applyEarlyPayDiscount: true } : {}),
            });
          }}
          onCancel={() => { setNotationConfirm(null); setNotationError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              {clientAdminLabel(clientById.get(notationConfirm.payment.clientId), notationConfirm.payment.clientId)} reported paying{' '}
              <span className="font-semibold">{fmtMoney(notationConfirm.payment.amount)}</span> by {METHOD_LABEL[notationConfirm.payment.method] ?? notationConfirm.payment.method} against{' '}
              {notationConfirm.invoice.documentNumber ?? 'this invoice'}. The reported amount cannot be changed; set the date the money actually arrived.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Date received" required tip="The day the money arrived, in the brand's time zone. The early-pay window is judged on it, so backdate it for a check that arrived in time.">
                <input type="date" className={inputClass} value={notationConfirm.dateReceived} onChange={(e) => setNotationDate(e.target.value)} />
              </Field>
              <Field label="Receipt / reference #" tip="Optional: the Zelle confirmation or check number.">
                <input className={inputClass} maxLength={100} value={notationConfirm.receiptNumber} onChange={(e) => setNotationConfirm((n) => (n ? { ...n, receiptNumber: e.target.value } : n))} />
              </Field>
            </div>
            <div className={`space-y-1 rounded-md border p-2 text-sm ${notationConfirm.quote.eligible ? 'border-[var(--tint-green-border)] bg-[var(--tint-green-bg-soft)]' : 'border-[rgb(var(--edge-rgb)/0.10)]'}`}>
              <div className="text-xs text-[var(--color-muted)]">
                Early-pay terms: {pctLabel(notationConfirm.invoice.earlyPayDiscount!.rate)}% through {displayDate(notationConfirm.invoice.earlyPayDiscount!.dueBy ?? null)}.{notationConfirm.quoteLoading ? ' Re-checking...' : ''}
              </div>
              {notationConfirm.quote.eligible ? (
                <label className="flex items-start gap-2">
                  <input type="checkbox" className="mt-0.5" checked={notationConfirm.applyEarlyPay} onChange={(e) => setNotationConfirm((n) => (n ? { ...n, applyEarlyPay: e.target.checked && n.quote.eligible } : n))} />
                  <span>
                    Apply the early-pay discount of <span className="font-semibold">{fmtMoney(notationConfirm.quote.discountGross)}</span> as a credit note.
                    <span className="block text-xs text-[var(--color-muted)]">{notationConfirm.quote.statement}</span>
                  </span>
                </label>
              ) : (
                <p className="text-xs">{notationConfirm.quote.statement}</p>
              )}
              {!notationConfirm.applyEarlyPay && notationConfirm.quote.eligible ? (
                <p className="text-xs text-[var(--color-muted)]">Without the discount this payment leaves {fmtMoney(round2(Math.max(0, notationConfirm.quote.remaining - notationConfirm.quote.cash)))} open.</p>
              ) : null}
            </div>
            <p className="text-xs text-[var(--color-muted)]">Confirming assigns the permanent payment number and settles the invoice as stated. Marking it not received instead is on the invoice card.</p>
            {notationError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{notationError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {crossForm ? (
        <ConfirmDialog
          open={true}
          title="Record a payment across invoices"
          confirmLabel="Record payment"
          busy={busy}
          onConfirm={doCrossPay}
          onCancel={() => { setCrossForm(null); setCrossError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              One non-card payment (Zelle, check, money order, or cash) applied across a billing entity's open
              invoices, oldest first. Any amount over the allocated total is recorded as an on-account credit or a refund.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Billing entity" required>
                <select className={inputClass} value={crossForm.entityId} onChange={(e) => onCrossEntity(e.target.value)}>
                  <option value="">Select an entity…</option>
                  {clients.filter((c) => !c.isInternal).map((c) => (
                    <option key={c.id} value={c.id}>{clientAdminName(c)}</option>
                  ))}
                </select>
              </Field>
              <Field label="Method" required>
                <select
                  className={inputClass}
                  value={crossForm.method}
                  onChange={(e) => editCrossForm((f) => ({ ...f, method: e.target.value as PaymentMethod }))}
                >
                  {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Amount received" required>
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={crossForm.amountReceived}
                  onChange={(e) => onCrossReceived(e.target.value)}
                />
              </Field>
              <Field label="Date received" required>
                <input
                  type="date"
                  className={inputClass}
                  value={crossForm.dateReceived}
                  onChange={(e) => onCrossDate(e.target.value)}
                />
              </Field>
              <Field label="Receipt / reference #" tip="Optional: the Zelle confirmation, check, or money-order number.">
                <input
                  className={inputClass}
                  maxLength={100}
                  value={crossForm.receiptNumber}
                  onChange={(e) => editCrossForm((f) => ({ ...f, receiptNumber: e.target.value }))}
                />
              </Field>
              <Field label="Memo" tip="Optional note kept on the payment record.">
                <input
                  className={inputClass}
                  maxLength={500}
                  value={crossForm.memo}
                  onChange={(e) => editCrossForm((f) => ({ ...f, memo: e.target.value }))}
                />
              </Field>
              {/* 04 #60 WS4: the voluntary tip, offered when the paying entity's brand accepts tips. */}
              {crossForm.entityId && tipsOn(brandForClient(crossForm.entityId)) ? (
                <>
                  <Field label="Tip" tip="Optional voluntary tip inside the amount received. It is taken off before the invoices are allocated and is recorded on the payment only.">
                    <input
                      className={inputClass}
                      inputMode="decimal"
                      placeholder="0.00"
                      value={crossForm.tip}
                      onChange={(e) => onCrossTip(e.target.value)}
                    />
                  </Field>
                  {tipClassesFor(brandForClient(crossForm.entityId)).length > 0 ? (
                    <Field label="Book the tip to" tip="Where the tip is booked: the default Tip income account, or one of this brand's active charge classes (its income account; a class collected on behalf of someone makes the tip owed to that payee instead of income). This only names the tip; the invoices' own charge-class lines are unaffected.">
                      <select
                        className={inputClass}
                        value={crossForm.tipChargeClassId}
                        onChange={(e) => editCrossForm((f) => ({ ...f, tipChargeClassId: e.target.value }))}
                      >
                        <option value="">Tip income (default)</option>
                        {tipClassesFor(brandForClient(crossForm.entityId)).map((c) => <option key={c.id} value={c.id}>{tipClassLabel(c)}</option>)}
                      </select>
                    </Field>
                  ) : null}
                </>
              ) : null}
            </div>

            {crossLoading ? (
              <p className="text-sm text-[var(--color-muted)]">Loading open invoices…</p>
            ) : !crossForm.entityId ? (
              <p className="text-sm text-[var(--color-muted)]">Select a billing entity to see its open invoices.</p>
            ) : crossForm.rows.length === 0 ? (
              <p className="text-sm text-[var(--color-muted)]">No open invoices to allocate for this entity.</p>
            ) : (
              <div className="space-y-2">
                <div className="text-xs font-medium text-[var(--color-muted)]">Open invoices (oldest first). Allocate the payment across them.</div>
                <div className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-[var(--color-border)] p-2">
                  {crossForm.rows.map((r) => (
                    <div key={r.invoiceId} className="grid grid-cols-[1fr_auto] items-center gap-2 text-sm">
                      <div className="min-w-0">
                        <span className={r.pastDue ? 'font-medium text-[var(--tint-red-text)]' : 'font-medium'}>{r.documentNumber ?? r.invoiceId}</span>
                        <span className="text-xs text-[var(--color-muted)]">
                          {' · '}{clientAdminLabel(clientById.get(r.clientId), r.clientId)}
                          {' · issued '}{displayDate(r.issueDate)}{' · due '}
                          <span className={r.pastDue ? 'text-[var(--tint-red-text)]' : ''}>{displayDate(r.dueDate)}{r.pastDue ? ' (past due)' : ''}</span>
                          {' · '}{fmtMoney(r.remaining)}{' due'}
                        </span>
                        {/* 04 #69 rule 16: the row's offered discount, from its server quote on this date. */}
                        {r.earlyPay?.terms ? (
                          r.earlyPay.eligible ? (
                            <label className="mt-0.5 flex items-start gap-2 text-xs text-[var(--tint-green-text)]">
                              <input type="checkbox" className="mt-0.5" checked={r.applyEarlyPay} onChange={(e) => onCrossToggleEarlyPay(r.invoiceId, e.target.checked)} />
                              <span>
                                Early-pay discount {fmtMoney(r.earlyPay.discountGross)}{r.earlyPay.payInFullAmount != null ? `, pays in full at ${fmtMoney(r.earlyPay.payInFullAmount)}` : ''} (through {displayDate(r.earlyPay.terms.dueBy)}).
                              </span>
                            </label>
                          ) : (
                            <span className="mt-0.5 block text-xs text-[var(--color-muted)]">{r.earlyPay.statement}</span>
                          )
                        ) : null}
                      </div>
                      <input
                        className={`${inputClass} w-28 text-right`}
                        inputMode="decimal"
                        value={r.amount}
                        onChange={(e) => setCrossRowAmount(r.invoiceId, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 text-sm">
                  <span>Allocated: <span className="font-semibold">{fmtMoney(crossAllocated)}</span></span>
                  <span>Received: <span className="font-semibold">{fmtMoney(crossReceived)}</span></span>
                  {crossTip > 0 ? <span>Tip: <span className="font-semibold">{fmtMoney(crossTip)}</span></span> : null}
                  <span className={crossLeftover > 0 ? 'text-[var(--tint-warn-text)]' : ''}>Leftover: <span className="font-semibold">{fmtMoney(crossLeftover)}</span></span>
                </div>
              </div>
            )}

            {crossLeftover > 0 ? (
              <div className="space-y-2 rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] p-2 text-sm text-[var(--tint-warn-text)]">
                <div className="font-medium">The {fmtMoney(crossLeftover)} leftover must be recorded. Choose one:</div>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cross-leftover"
                    checked={crossForm.leftoverDisposition === 'credit'}
                    onChange={() => editCrossForm((f) => ({ ...f, leftoverDisposition: 'credit' }))}
                  />
                  <span>On-account client credit (usable on a future invoice)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="cross-leftover"
                    checked={crossForm.leftoverDisposition === 'refund'}
                    onChange={() => editCrossForm((f) => ({ ...f, leftoverDisposition: 'refund' }))}
                  />
                  <span>Refund it</span>
                </label>
                {crossForm.leftoverDisposition === 'refund' ? (
                  <div className="pl-6">
                    <Field label="Refund method">
                      <select
                        className={inputClass}
                        value={crossForm.leftoverRefundMethod}
                        onChange={(e) => editCrossForm((f) => ({ ...f, leftoverRefundMethod: e.target.value as RefundMethod }))}
                      >
                        {REFUND_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </Field>
                  </div>
                ) : null}
              </div>
            ) : null}

            <p className="text-xs text-[var(--color-muted)]">
              Recording assigns one permanent payment number across all the invoices. A recorded payment can't be
              edited; correct a mistake by voiding it (which reverses every allocation) and recording again.
            </p>
            {crossError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{crossError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {voidTarget ? (
        <ConfirmDialog
          open={true}
          title="Void this payment?"
          confirmLabel="Void payment"
          confirmVariant="danger"
          busy={busy}
          // #72(b) put a DATA BLOCK in this dialog; at the default width every enumerated row wrapped
          // onto two lines and forced the body into a scroll it does not need.
          size="lg"
          onConfirm={doVoidPayment}
          onCancel={() => { setVoidTarget(null); setVoidReason(''); setVoidReturned(false); setVoidError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Void {voidTarget.documentNumber} ({fmtMoney(voidTarget.amount)})? The record and its number stay
              permanently (voided, never deleted).{' '}
              {(voidTarget.allocations?.length ?? 0) > 1
                ? `This payment was applied to ${voidTarget.allocations?.length ?? 0} invoices; every allocation is reversed. Any of those invoices this payment had settled reopens, and any payment-unlocked documents lock again.`
                : 'If this payment had settled the invoice, the invoice reopens and any payment-unlocked documents lock again.'}
            </p>
            {/* 04 #72(b): ENUMERATE what the void reverses. Naming the count is not enough here,
                because a multi-invoice receipt can span DIFFERENT CLIENTS, which the admin cannot
                infer from anything else on screen. Each row opens the (c) preview. */}
            {(voidTarget.allocations?.length ?? 0) > 1 ? (
              <div className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] p-2">
                <div className="mb-1 text-xs font-semibold text-[var(--color-text)]">
                  This reverses {voidTarget.allocations?.length} allocations
                </div>
                <div className="space-y-0.5">
                  {allocationRows(voidTarget).map((a) => (
                    <button
                      key={a.invoiceId}
                      type="button"
                      disabled={!a.clientId}
                      onClick={() => openAllocPreview(a, 'void')}
                      className={`flex w-full items-center justify-between gap-3 rounded px-1 py-1 text-left text-xs ${a.clientId ? 'hover:bg-[rgb(var(--edge-rgb)/0.05)]' : 'cursor-default'}`}
                    >
                      <span className="min-w-0">
                        <span className="font-medium text-[var(--color-text)]">{a.documentNumber ?? a.invoiceId}</span>
                        {a.clientName ? <span className="text-[var(--color-muted)]"> · {a.clientName}</span> : null}
                        {a.clientId ? <span className="text-[var(--color-muted)]"> · view</span> : null}
                      </span>
                      <span className="shrink-0 text-[var(--color-muted)]">-{fmtMoney(a.amount)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {/* 04 #69 rule 18: a payment that earned early-pay credit note(s) takes them with it. */}
            {(voidTarget.earlyPayDiscountAdjustmentIds?.length ?? 0) > 0 ? (
              <div className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] p-2 text-sm text-[var(--tint-warn-text)]">
                This payment earned {voidTarget.earlyPayDiscountAdjustmentIds!.length === 1 ? 'an early-pay discount credit note' : `${voidTarget.earlyPayDiscountAdjustmentIds!.length} early-pay discount credit notes`}. Voiding it also voids {voidTarget.earlyPayDiscountAdjustmentIds!.length === 1 ? 'that credit note' : 'those credit notes'}, so the invoice reopens for the cash and the discount together. A later payment outside the window earns nothing.
              </div>
            ) : null}
            <label className="flex items-start gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm">
              <input type="checkbox" className="mt-0.5" checked={voidReturned} onChange={(e) => setVoidReturned(e.target.checked)} />
              <span>
                This payment was <span className="font-semibold">returned or bounced (NSF)</span>.
                {voidReturned
                  ? ' The client is notified their payment failed and the balance is restored; the invoice re-ages through the normal billing run.'
                  : ' Leave unchecked for a silent internal correction of a mis-entered record.'}
              </span>
            </label>
            <Field label="Reason" required>
              <input
                className={inputClass}
                autoFocus
                value={voidReason}
                maxLength={500}
                onChange={(e) => setVoidReason(e.target.value)}
              />
            </Field>
            {voidError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{voidError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {/* 04 #70 THE RECEIPT DIALOG: a payment as a first-class record, opened from its number on any
          payment row. Receipt-as-hub (the s93 owner-locked decision): there is no user-facing
          "original invoice", so every invoice the money reached is listed as an equal, each opening the
          #72(c) preview. Sits at z-[60] because it can be opened from the issued-invoice detail modal
          at z-50; the preview it opens sits above it again at z-[70]. Read-only by construction: Void
          stays on the row, where the admin already has the invoice in view. */}
      {receiptTarget
        ? createPortal(
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Payment receipt">
              <div className="absolute inset-0 glass-scrim" onClick={closeReceipt} aria-hidden="true" />
              <div className="relative flex max-h-[85dvh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] shadow-xl">
                <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-5">
                  <div className="min-w-0">
                    <h2 className="font-display text-lg">{receiptTarget.documentNumber ?? 'Reported payment'}</h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                      {receiptTarget.status === 'voided' ? (
                        <span className="rounded bg-[rgb(var(--edge-rgb)/0.06)] px-1.5 py-0.5 font-medium">Voided</span>
                      ) : receiptTarget.status === 'failed' ? (
                        <span className="rounded bg-[rgb(var(--edge-rgb)/0.06)] px-1.5 py-0.5 font-medium">Not received</span>
                      ) : receiptTarget.status === 'pending-confirmation' ? (
                        <span className="rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 font-medium text-[var(--tint-warn-text)]">Reported by client</span>
                      ) : null}
                      <span>{METHOD_LABEL[receiptTarget.method] ?? receiptTarget.method}</span>
                      <span>· received {displayDate(receiptTarget.dateReceived)}</span>
                    </div>
                  </div>
                  <button type="button" className="shrink-0 rounded p-1 text-[var(--color-muted)] hover:bg-[rgb(var(--edge-rgb)/0.05)]" aria-label="Close" onClick={closeReceipt}>✕</button>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto px-5 py-2 text-sm">
                  {receiptError ? (
                    <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{receiptError}</div>
                  ) : !receiptFresh ? (
                    <div className="text-xs text-[var(--color-muted)]">Loading receipt…</div>
                  ) : (
                    (() => {
                      const r = receiptFresh;
                      const rows = allocationRows(r);
                      const applied = round2(rows.reduce((s, a) => s + a.amount, 0));
                      const excess = round2(r.creditedExcess ?? 0);
                      const tip = round2(r.tipAmount ?? 0); // 04 #60 WS4
                      return (
                        <>
                          <div className="space-y-1">
                            <div className="flex justify-between font-semibold"><span>Amount received</span><span>{fmtMoney(r.amount)}</span></div>
                            {/* Shown only when the two differ, so an ordinary receipt is not padded with
                                a line restating its own total. */}
                            {excess > 0 || tip > 0 || rows.length > 1 ? (
                              <div className="flex justify-between"><span className="text-[var(--color-muted)]">Applied to invoices</span><span>{fmtMoney(applied)}</span></div>
                            ) : null}
                            {tip > 0 ? (
                              <div className="flex justify-between"><span className="text-[var(--color-muted)]">Tip</span><span>{fmtMoney(tip)}</span></div>
                            ) : null}
                            {/* creditedExcess is stamped under BOTH #64 leftover dispositions, so the
                                disposition LINKS decide the wording, never the field itself (the s91
                                financial-display invariant). */}
                            {excess > 0 ? (
                              <div className="flex justify-between">
                                <span className="text-[var(--color-muted)]">
                                  {r.overpaymentRefundId ? 'Refunded' : r.overpaymentCreditId ? 'To account credit' : 'Unapplied'}
                                  {r.overpaymentRefundNumber ? ` (${r.overpaymentRefundNumber})` : r.overpaymentCreditNumber ? ` (${r.overpaymentCreditNumber})` : ''}
                                </span>
                                <span>{fmtMoney(excess)}</span>
                              </div>
                            ) : null}
                          </div>
                          <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                            <h3 className="mb-1 font-medium">{rows.length > 1 ? `Applied to ${rows.length} invoices` : 'Applied to'}</h3>
                            {rows.length === 0 ? (
                              <div className="text-xs text-[var(--color-muted)]">No invoice could be resolved for this payment.</div>
                            ) : (
                              <div className="space-y-0.5">
                                {rows.map((a) => (
                                  <button
                                    key={a.invoiceId}
                                    type="button"
                                    disabled={!a.clientId}
                                    onClick={() => openAllocPreview(a, 'receipt')}
                                    className={`flex w-full items-center justify-between gap-3 rounded px-1 py-1 text-left text-xs ${a.clientId ? 'hover:bg-[rgb(var(--edge-rgb)/0.05)]' : 'cursor-default'}`}
                                  >
                                    <span className="min-w-0">
                                      <span className="font-medium text-[var(--color-text)]">{a.documentNumber ?? a.invoiceId}</span>
                                      {a.clientName ? <span className="text-[var(--color-muted)]"> · {a.clientName}</span> : null}
                                      {a.clientId ? <span className="text-[var(--color-muted)]"> · view</span> : null}
                                    </span>
                                    <span className="shrink-0">{fmtMoney(a.amount)}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {r.receiptNumber || r.memo ? (
                            <div className="space-y-0.5 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2 text-xs text-[var(--color-muted)]">
                              {r.receiptNumber ? <div>Reference {r.receiptNumber}</div> : null}
                              {r.memo ? <div>{r.memo}</div> : null}
                            </div>
                          ) : null}
                          {r.status === 'voided' || r.status === 'failed' ? (
                            <div className="rounded-md bg-[rgb(var(--edge-rgb)/0.03)] p-2 text-xs text-[var(--color-muted)]">
                              <div className="font-medium text-[var(--color-text)]">
                                {r.status === 'voided' ? 'This payment was voided' : 'This payment was marked not received'}
                                {r.voidedAt ? ` on ${displayDate(r.voidedAt.slice(0, 10))}` : ''}
                              </div>
                              {r.returnedPayment ? <div>Recorded as returned or bounced (NSF).</div> : null}
                              {r.voidReason ? <div>Reason: {r.voidReason}</div> : null}
                              <div>The amounts above are what this receipt had been applied to before it was reversed.</div>
                            </div>
                          ) : null}
                        </>
                      );
                    })()
                  )}
                </div>
                <div className="flex shrink-0 justify-end border-t border-[rgb(var(--edge-rgb)/0.05)] px-5 py-3">
                  <Button type="button" variant="ghost" onClick={closeReceipt}>Close</Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {/* 04 #72(c): the allocation PREVIEW. Opened from an enumerated row in the void dialog, or from
          the 04 #70 receipt, it shows that invoice's CONTENTS and ALL RELATED PAYMENTS, so the admin
          can see everything else applied to it without leaving what they were doing. Rendered above
          BOTH callers (z-[70]) and closed back to whichever opened it, never replacing it. Read-only
          by construction: no action lives here. */}
      {allocPreview
        ? createPortal(
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Allocated invoice preview">
              <div className="absolute inset-0 glass-scrim" onClick={() => setAllocPreview(null)} aria-hidden="true" />
              <div className="relative flex max-h-[85dvh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] shadow-xl">
                <div className="flex items-start justify-between gap-3 px-5 pb-2 pt-5">
                  <div className="min-w-0">
                    <h2 className="font-display text-lg">{allocPreview.documentNumber ?? 'Invoice'}</h2>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                      {allocPreview.clientName ? <span>{allocPreview.clientName}</span> : null}
                      <span>· {fmtMoney(allocPreview.amount)} of this payment applied here</span>
                    </div>
                  </div>
                  <button type="button" className="shrink-0 rounded p-1 text-[var(--color-muted)] hover:bg-[rgb(var(--edge-rgb)/0.05)]" aria-label="Close" onClick={() => setAllocPreview(null)}>✕</button>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto px-5 py-2 text-sm">
                  {allocPreviewError ? (
                    <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{allocPreviewError}</div>
                  ) : !allocPreviewData ? (
                    <div className="text-xs text-[var(--color-muted)]">Loading invoice…</div>
                  ) : (
                    <>
                      <div>
                        <h3 className="mb-1 font-medium">Line items</h3>
                        <div className="divide-y divide-[rgb(var(--edge-rgb)/0.10)]">
                          {allocPreviewData.invoice.lineItems.map((l) => (
                            <div key={l.lineId} className="flex items-start justify-between gap-3 py-1.5">
                              <div className="min-w-0">
                                <div className="text-[var(--color-text)]">{l.description}</div>
                                <div className="text-xs text-[var(--color-muted)]">{l.qty} × {fmtMoney(l.charge)}</div>
                              </div>
                              <div className="shrink-0 font-medium">{fmtMoney(round2(l.charge * l.qty))}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-1 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                        <div className="flex justify-between"><span className="text-[var(--color-muted)]">Total</span><span>{fmtMoney(allocPreviewData.invoice.totalWithoutConvenienceFee)}</span></div>
                        {allocPreviewData.invoice.amountPaid > 0 ? (
                          <div className="flex justify-between"><span className="text-[var(--color-muted)]">Paid</span><span>{fmtMoney(allocPreviewData.invoice.amountPaid)}</span></div>
                        ) : null}
                        {/* 04 #73 lock 2 (s99): credit settlement is visible wherever the invoice's money
                            story is told; this preview showed Total/Paid/Balance with no credits line, so a
                            credit-settled invoice read as unexplained. */}
                        {appliedCreditsTotal(allocPreviewData.invoice) > 0 ? (
                          <div className="flex justify-between"><span className="text-[var(--color-muted)]">Credits applied</span><span>-{fmtMoney(appliedCreditsTotal(allocPreviewData.invoice))}</span></div>
                        ) : null}
                        <div className="flex justify-between font-semibold"><span>Balance</span><span>{fmtMoney(allocPreviewData.invoice.balanceDue)}</span></div>
                        <div className="flex justify-between text-xs text-[var(--color-muted)]"><span>State</span><span>{STATE_LABEL[allocPreviewData.invoice.state]}</span></div>
                      </div>
                      <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                        <h3 className="mb-1 font-medium">All payments on this invoice</h3>
                        {allocPreviewData.payments.length === 0 ? (
                          <div className="text-xs text-[var(--color-muted)]">No payments recorded yet.</div>
                        ) : (
                          allocPreviewData.payments.map((p) => (
                            <div key={p.id} className="text-xs">
                              <div className="flex justify-between gap-3">
                                <span className={`min-w-0 ${p.status === 'voided' || p.status === 'failed' ? 'text-[var(--color-muted)] line-through' : 'text-[var(--color-muted)]'}`}>
                                  {p.documentNumber ?? 'Reported'} · {METHOD_LABEL[p.method] ?? p.method} · {displayDate(p.dateReceived)}
                                </span>
                                <span className="shrink-0">{fmtMoney(contributionTo(p, allocPreview.invoiceId))}</span>
                              </div>
                              {p.status === 'voided' ? <div className="text-[var(--color-muted)]">Voided</div> : null}
                              {paymentRowContext(p) ? <div className="text-[var(--color-muted)]">{paymentRowContext(p)}</div> : null}
                              {paymentTipRows(p)}
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
                <div className="flex shrink-0 justify-end border-t border-[rgb(var(--edge-rgb)/0.05)] px-5 py-3">
                  <Button type="button" variant="ghost" onClick={() => setAllocPreview(null)}>
                    {allocPreviewFrom === 'receipt' ? 'Back to the receipt' : 'Back to the void'}
                  </Button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      {deleteTarget ? (
        <ConfirmDialog
          open={true}
          title="Delete this draft?"
          confirmLabel="Delete"
          confirmVariant="danger"
          busy={busy}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        >
          <p>
            Delete this draft invoice? Only drafts can be deleted (issued invoices are permanent). Any pulled
            time / mileage / expense lines on it return to the unbilled pool. This cannot be undone.
          </p>
        </ConfirmDialog>
      ) : null}

      <PendingCreditsDialog
        pending={pendingCredits}
        trigger={pendingTrigger}
        clientName={(() => { const c = clients.find((x) => x.id === (pendingCredits?.clientId ?? clientId)); return c ? clientAdminName(c) : 'this client'; })()}
        onClose={() => setPendingCredits(null)}
        onResolved={(res, refunds) => {
          const cid = pendingCredits?.clientId ?? clientId;
          setPendingCredits(null);
          if (res) setFlash(resolvedSummary(res));
          void load(clientId);
          if (refunds.length > 0 && cid) void openRefundForCredits(refunds[0], cid);
        }}
      />

      {composeForm ? (
        <ConfirmDialog
          open={true}
          title="Compose monthly invoice"
          confirmLabel={composeForm.force ? 'Compose anyway' : 'Compose draft'}
          busy={busy}
          onConfirm={doCompose}
          onCancel={() => setComposeForm(null)}
        >
          <div className="space-y-3 text-left">
            <p className="text-xs text-[var(--color-muted)]">
              Builds a Monthly draft from the selected client's active recurring services and packages for the
              chosen month, prorating anything that started, stopped, or changed mid-month. You review and issue
              it like any other draft. A notice and an email will confirm when it's ready.
            </p>
            <Field label="Client" required>
              <select className={inputClass} value={composeForm.clientId} onChange={(e) => setComposeClient(e.target.value)}>
                <option value="">Select a client…</option>
                {clients.filter((c) => !c.isInternal).map((c) => (
                  <option key={c.id} value={c.id}>{clientAdminName(c)}</option>
                ))}
              </select>
            </Field>
            <Field label="Month" required>
              <input
                type="month"
                className={inputClass}
                value={composeForm.month}
                onChange={(e) => setComposeForm((f) => (f ? { ...f, month: e.target.value, force: false } : f))}
              />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={composeForm.pull}
                onChange={(e) => setComposeForm((f) => (f ? { ...f, pull: e.target.checked } : f))}
              />
              Also pull all unbilled items onto this draft
              <InfoTip text="Adds every unbilled billable time, mileage, and expense line in the same step (sources are marked billed immediately, exactly like the picker). The default follows the brand setting in Settings." />
            </label>
            {composeError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{composeError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {pickerOpen ? (
        <ConfirmDialog
          open={true}
          title="Add unbilled items"
          confirmLabel={pickerSelected.size > 0 ? `Add ${pickerSelected.size} item${pickerSelected.size === 1 ? '' : 's'}` : 'Add items'}
          busy={pickerBusy}
          onConfirm={doPull}
          onCancel={closePicker}
        >
          <div className="space-y-3 text-left">
            <p className="text-xs text-[var(--color-muted)]">
              The client's unbilled billable charges (§7.11). Adding an item marks its source as billed
              immediately; it locks against edits and can never be billed twice. Charges are the amounts
              snapshotted on the source; you can adjust each line's charge on the draft afterward.
            </p>
            {!pickerData && !pickerError ? (
              <p className="text-sm text-[var(--color-muted)]">Loading unbilled items…</p>
            ) : null}
            {pickerData ? (
              <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                {pickerData.time.length + pickerData.mileage.length + pickerData.expenses.length === 0 ? (
                  <p className="text-sm text-[var(--color-muted)]">Nothing unbilled right now. Every billable time, mileage, and expense line is already on an invoice.</p>
                ) : null}
                {renderPickerSection('Time', pickerData.time)}
                {renderPickerSection('Mileage', pickerData.mileage)}
                {renderPickerExpenses(pickerData.expenses)}
              </div>
            ) : null}
            {pickerError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{pickerError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}
      {creditForm ? (() => {
        const sel = creditForm.lines.filter((r) => r.sel && (parseFloat(r.amount) || 0) > 0);
        // 04 #149 commit 2 (02 invariant 3 s207): every leg mirrors the server through the ONE shared-ui helper -
        // the revenue leg nets the invoice discount's share, the tax leg uses each line's FROZEN taxed share (WS3,
        // 08 Q59), both cumulative over the invoice's surviving notes and anchored on what they booked (3c).
        const preview = creditNotePreview(creditForm.invoice, selectedCreditLines(sel), creditForm.priors);
        const { listTotal, discountReversed, creditedSubtotal, taxReversed, total: creditTotal } = preview;
        const balance = nonCardRemaining(creditForm.invoice);
        const overBalance = creditTotal > balance + 1e-9;
        // Max PRE-TAX list net still creditable on a line, given the other selected lines' balance consumption
        // (revenue after the invoice discount + tax) and this line's own factor. This is the "before tax" figure
        // per line — the top balance is the "after tax" figure. A single before/after pair can't be right when
        // some lines are taxable and some aren't, so it's shown per line.
        const lineMaxNet = (idx: number): number => {
          const row = creditForm.lines[idx];
          const otherConsumed = creditForm.lines.reduce(
            (s, x, j) => (j !== idx && x.sel ? s + (parseFloat(x.amount) || 0) * x.balanceFactor : s),
            0,
          );
          const room = Math.max(0, balance - otherConsumed);
          return round2(Math.min(row.net, room / row.balanceFactor));
        };
        return (
          <ConfirmDialog open={true} title="Issue a credit note" confirmLabel="Issue credit note" busy={busy} confirmDisabled={overBalance || sel.length === 0} onConfirm={() => doCreateCredit()} onCancel={() => setCreditForm(null)}>
            <div className="space-y-3 text-left">
              {creditForm.entityCredit > 0 ? (
                <div className="flex items-start justify-between gap-2 rounded-md bg-[var(--tint-warn-bg)] p-2 text-sm text-[var(--tint-warn-text)]">
                  <span>
                    This account has <span className="font-semibold">{fmtMoney(creditForm.entityCredit)}</span> of credit available.
                    A credit note reverses charges and their tax. If you mean to settle this invoice with existing credit instead, record a payment and fund it from the credit there.
                  </span>
                  <PillButton tone="amber" onClick={() => { const inv = creditForm.invoice; setCreditForm(null); openPay(inv); }}>Record payment instead</PillButton>
                </div>
              ) : null}
              <p className="text-sm">
                Credit lines on {creditForm.invoice.documentNumber ?? 'this invoice'}. Tax reverses automatically against the
                invoice's stored rate. The outstanding balance is <span className="font-semibold">{fmtMoney(nonCardRemaining(creditForm.invoice))}</span>.
              </p>
              <div className="max-h-[45vh] space-y-2 overflow-y-auto pr-1">
                {creditForm.lines.map((r, i) => (
                  <div key={r.lineId} className="flex items-center gap-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-2 text-sm">
                    <input
                      type="checkbox"
                      className="shrink-0"
                      checked={r.sel}
                      onChange={(e) => setCreditForm((f) => {
                        if (!f) return f;
                        const checked = e.target.checked;
                        // Default a newly-checked line so the TOTAL credit (net + reversed tax) stays
                        // within the invoice's outstanding balance. The reversal is proportional to the
                        // invoice's stored rate, so a taxable line consumes (1 + rate) of balance per net
                        // dollar — filling net-only (the old behavior) overshot once tax was added.
                        const balance = nonCardRemaining(f.invoice);
                        // WS3 (s173) + 04 #149: by the line's balance factor (discount factor + its FROZEN taxed share).
                        const otherConsumed = f.lines.reduce(
                          (s, x, j) => (j !== i && x.sel ? s + (parseFloat(x.amount) || 0) * x.balanceFactor : s),
                          0,
                        );
                        const room = Math.max(0, balance - otherConsumed);
                        return {
                          ...f,
                          lines: f.lines.map((x, j) => {
                            if (j !== i) return x;
                            const netMax = round2(room / x.balanceFactor);
                            return { ...x, sel: checked, amount: checked && !x.amount ? String(Math.min(x.net, netMax)) : x.amount };
                          }),
                        };
                      })}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{r.description}</div>
                      <div className="text-xs text-[var(--color-muted)]">{creditRowHint(creditForm.invoice, r, 'credited')}</div>
                    </div>
                    <div className="w-28 shrink-0">
                      <input
                        className={inputClass}
                        inputMode="decimal"
                        placeholder="0.00"
                        value={r.amount}
                        disabled={!r.sel}
                        onChange={(e) => { const n = parseFloat(e.target.value); const cap = lineMaxNet(i); const v = Number.isFinite(n) && n > cap ? cap.toFixed(2) : e.target.value; setCreditForm((f) => (f ? { ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, amount: v } : x)) } : f)); }}
                      />
                      {r.sel ? <div className="mt-0.5 text-right text-[10px] text-[var(--color-muted)]">max {fmtMoney(lineMaxNet(i))} before tax</div> : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--color-muted)]">
                {discountReversed > 0.005 ? <span>Lines credited <span className="font-semibold text-[var(--color-text)]">{fmtMoney(listTotal)}</span></span> : null}
                {discountReversed > 0.005 ? <span>Discount reversed <span className="font-semibold text-[var(--color-text)]">-{fmtMoney(discountReversed)}</span></span> : null}
                <span>Credited subtotal <span className="font-semibold text-[var(--color-text)]">{fmtMoney(creditedSubtotal)}</span></span>
                <span>Tax reversed <span className="font-semibold text-[var(--color-text)]">{fmtMoney(taxReversed)}</span></span>
                <span className={overBalance ? 'text-[var(--tint-red-text)]' : ''}>Total credit <span className={`font-semibold ${overBalance ? 'text-[var(--tint-red-text)]' : 'text-[var(--color-text)]'}`}>{fmtMoney(creditTotal)}</span></span>
                <span className={overBalance ? 'text-[var(--tint-red-text)]' : ''}>Remaining balance <span className={`font-semibold ${overBalance ? 'text-[var(--tint-red-text)]' : 'text-[var(--color-text)]'}`}>{fmtMoney(round2(balance - creditTotal))}</span></span>
              </div>
              {overBalance ? (
                <p className="text-xs text-[var(--tint-red-text)]">That is more than the {fmtMoney(balance)} outstanding balance. Lower the amounts, or use a refund to return money beyond the balance.</p>
              ) : null}
              <Field label="Reason" required>
                <input className={inputClass} autoFocus value={creditForm.reason} maxLength={500} onChange={(e) => setCreditForm((f) => (f ? { ...f, reason: e.target.value } : f))} />
              </Field>
              <p className="text-xs text-[var(--color-muted)]">
                Issuing assigns a permanent credit-note number. If the credit fully settles the invoice, any pay-on-delivery
                documents are released. You'll be asked to confirm first.
              </p>
              {creditError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{creditError}</div> : null}
            </div>
          </ConfirmDialog>
        );
      })() : null}

      {voidInvTarget ? (
        <ConfirmDialog
          open={true}
          title="Void this invoice?"
          confirmLabel="Void invoice"
          confirmVariant="danger"
          busy={busy}
          onConfirm={doVoidInvoice}
          onCancel={() => { setVoidInvTarget(null); setVoidInvReason(''); setVoidInvError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Voiding {voidInvTarget.documentNumber ?? 'this invoice'} reverses it as if it never happened, with no bad-debt loss
              booked (use Write off for uncollectable debt). The number is kept and marked Void, the balance goes to zero, and no
              pay-on-delivery documents are released. If any non-payment suspension was driven by this invoice, it is lifted
              automatically. If the invoice already has recorded/pending payments or an applied credit note, reverse those first.
            </p>
            {(() => {
              // late-fee.ts mints at most ONE fee per principal (applyLateFee returns skipped-already
              // once lateFeeApplied is stamped), so this single back-link resolves the whole picture.
              // The server stays the authority for both cases; this is the heads-up.
              const lf = voidInvTarget.lateFeeInvoiceId
                ? invoices.find((i) => i.id === voidInvTarget.lateFeeInvoiceId)
                : null;
              if (lf?.state === 'issued')
                return (
                  <p className="text-sm">
                    Its linked late-fee invoice {lf.documentNumber ?? lf.id} is still open and will be voided with it (the
                    finance charge follows the principal). If that late fee has payments or an applied credit, reverse those
                    first.
                  </p>
                );
              // 04 #109: a SETTLED fee is outside the cascade predicate, so it survives the void.
              if (lf?.state === 'paid')
                return (
                  <p className="text-sm text-[var(--tint-warn-text)]">
                    Its linked late-fee invoice {lf.documentNumber ?? lf.id} is already settled, so it will NOT be reversed
                    by this void and the finance charge stays collected. You will be asked to confirm.
                  </p>
                );
              return null;
            })()}
            <Field label="Reason" required>
              <input className={inputClass} autoFocus value={voidInvReason} maxLength={500} onChange={(e) => setVoidInvReason(e.target.value)} />
            </Field>
            {voidInvError ? <div ref={voidInvErrorRef} className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{voidInvError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {resumeInvTarget ? (
        <ConfirmDialog
          open={true}
          title="Resume services"
          confirmLabel="Resume selected"
          busy={resumeBusy}
          confirmDisabled={resumeBusy || resumeSelected.size === 0}
          onConfirm={() => void doResumeServices()}
          onCancel={() => { setResumeInvTarget(null); setResumeUnits([]); setResumeSelected(new Set()); setResumeError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Reactivate services this client had suspended for non-payment. The ones {resumeInvTarget.documentNumber ?? 'this invoice'} suspended
              are pre-selected; adjust as needed. Reactivating restores access, reprovisions, and notifies the client. A globally-disabled
              package member stays suspended until the service itself is re-enabled.
            </p>
            {resumeBusy && resumeUnits.length === 0 ? (
              <p className="text-sm text-[var(--color-muted)]">Loading…</p>
            ) : resumeUnits.length === 0 ? (
              <p className="text-sm text-[var(--color-muted)]">This client has no services suspended for non-payment.</p>
            ) : (
              <ul className="space-y-1">
                {resumeUnits.map((u) => {
                  const key = resumeKey(u);
                  return (
                    <li key={key} className="flex items-center gap-2 text-sm">
                      <input type="checkbox" id={`resume-${key}`} checked={resumeSelected.has(key)} disabled={resumeBusy} onChange={() => toggleResume(key)} />
                      <label htmlFor={`resume-${key}`} className="cursor-pointer">{u.name}</label>
                    </li>
                  );
                })}
              </ul>
            )}
            {resumeError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{resumeError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {writeOffTarget ? (
        <ConfirmDialog
          open={true}
          title="Write off this invoice?"
          confirmLabel="Write off"
          confirmVariant="danger"
          busy={busy}
          onConfirm={doWriteOff}
          onCancel={() => { setWriteOffTarget(null); setWriteOffReason(''); setWriteOffAmount(''); setWriteOffError(null); }}
        >
          {(() => {
            const remaining = nonCardRemaining(writeOffTarget);
            const amt = round2(Number(writeOffAmount));
            const isPartial = Number.isFinite(amt) && amt > 0 && amt < remaining - 1e-9;
            // 04 #113: resolve the linked fee with NO state filter, then branch - the shape the
            // void dialog has carried since 04 #100. The cascade is FULL-write-off only (04 #66),
            // so the "also written off" line stays gated on !isPartial.
            //
            // WHY THE SETTLED CASE IS INFORMATIONAL AND NOT A 04 #109-STYLE CONSENT GATE (owner
            // ruling s114; 13's own row is the authority): a VOID un-recognizes revenue and tax,
            // while a WRITE-OFF keeps them recognized and books the loss to Bad Debt. Under a
            // write-off the debt was REAL and the client WAS genuinely late, so the finance charge
            // is legitimate and the client having paid it is a GOOD outcome that partly offsets the
            // bad debt. WS2d cascades to OPEN fees only because they are uncollectable from the
            // same non-paying debtor; a fee already collected is by definition not uncollectable.
            // A gate would warn that something is wrong when nothing is. Do NOT upgrade this.
            const linkedFee = writeOffTarget.lateFeeInvoiceId
              ? invoices.find((i) => i.id === writeOffTarget.lateFeeInvoiceId)
              : undefined;
            const lf = !isPartial && linkedFee?.state === 'issued' ? linkedFee : undefined;
            return (
              <div className="space-y-3 text-left">
                <p className="text-sm">
                  {isPartial ? (
                    <>Writing off <span className="font-semibold">{fmtMoney(amt)}</span> of {writeOffTarget.documentNumber ?? 'this invoice'}'s {fmtMoney(remaining)} balance
                      as uncollectable (bad debt). The invoice stays open for the remaining {fmtMoney(round2(remaining - amt))}. It issues a credit note and does not release any pay-on-delivery documents. To reverse it, void the credit note.</>
                  ) : (
                    <>Writing off {writeOffTarget.documentNumber ?? 'this invoice'} records its <span className="font-semibold">{fmtMoney(remaining)}</span> balance
                      as uncollectable (bad debt). It issues a credit note and closes the invoice as written off. It does not release any pay-on-delivery documents. To reverse it, void the credit note.</>
                  )}
                </p>
                {lf ? (
                  <p className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)]">
                    This also writes off the linked late-fee invoice {lf.documentNumber ?? lf.id} ({fmtMoney(nonCardRemaining(lf))}).
                    The finance charge follows the principal.
                  </p>
                ) : null}
                {linkedFee?.state === 'paid' ? (
                  <p className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)]">
                    Its linked late-fee invoice {linkedFee.documentNumber ?? linkedFee.id} is already settled and stays as it is.
                    The finance charge was collected and writing this invoice off does not reverse it.
                  </p>
                ) : null}
                <Field label="Amount to write off" required>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={writeOffAmount}
                    onChange={(e) => setWriteOffAmount(e.target.value)}
                    placeholder={String(remaining)}
                  />
                  <p className="mt-1 text-xs text-[var(--color-muted)]">Full balance is {fmtMoney(remaining)}. Enter less to write off only part of it.</p>
                </Field>
                <Field label="Reason" required>
                  <input className={inputClass} value={writeOffReason} maxLength={500} onChange={(e) => setWriteOffReason(e.target.value)} />
                </Field>
                {writeOffError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{writeOffError}</div> : null}
              </div>
            );
          })()}
        </ConfirmDialog>
      ) : null}

      {reinstateTarget ? (
        <ConfirmDialog
          open={true}
          title="Reinstate this invoice?"
          confirmLabel="Reinstate"
          busy={busy}
          onConfirm={doReinstate}
          onCancel={() => { setReinstateTarget(null); setReinstateReason(''); setReinstateError(null); }}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Reinstating {reinstateTarget.documentNumber ?? 'this invoice'} voids its write-off and reopens it for payment.
              The written-off amount returns to the balance and the credit note is marked void. Use this when a client pays a
              debt you had written off. Then record the payment as usual.
            </p>
            {(() => {
              const lf = reinstateTarget.lateFeeInvoiceId
                ? invoices.find((i) => i.id === reinstateTarget.lateFeeInvoiceId && i.state === 'written-off')
                : undefined;
              return lf ? (
                <p className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)]">
                  Its linked late fee {lf.documentNumber ?? lf.id} was also written off and will stay written off. Reinstate it
                  separately if you want to re-charge it.
                </p>
              ) : null;
            })()}
            <Field label="Reason" required>
              <input className={inputClass} value={reinstateReason} maxLength={500} onChange={(e) => setReinstateReason(e.target.value)} />
            </Field>
            {reinstateError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{reinstateError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {refundForm ? (
        <ConfirmDialog open={true} title="Record a refund" confirmLabel="Record refund" busy={busy} onConfirm={() => doRefund()} onCancel={() => setRefundForm(null)} dismissOnBackdrop={!refundForm.forCredits}>
          <div className="space-y-3 text-left">
            {/* 04 #76(c): the header's single cap describes the CASH mode only; in Return mode the
                binding constraint is each line's remaining net, so the header names the mode's real
                governor instead of quoting a ceiling the form below will not honor. */}
            <p className="text-sm">
              {refundForm.reverseLines ? (
                <>Refunding on {refundForm.invoice.documentNumber ?? 'this invoice'}. Up to <span className="font-semibold">{fmtMoney(refundableFor(refundForm.invoice))}</span> of cash is refundable overall, and in this mode each line is capped at its remaining value (the max shown per line), which can be less. Pick the lines below.</>
              ) : (
                <>Refunding on {refundForm.invoice.documentNumber ?? 'this invoice'}. Up to <span className="font-semibold">{fmtMoney(refundableFor(refundForm.invoice))}</span> is refundable (the cash received, less prior refunds). Choose how it should be handled below.</>
              )}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={refundForm.reverseLines ? 'Amount (from the lines below)' : 'Amount'} required>
                {refundForm.reverseLines ? (
                  <div className={`${inputClass} bg-[rgb(var(--edge-rgb)/0.03)] text-[var(--color-muted)]`}>{fmtMoney(returnGrossTotal(refundForm))}</div>
                ) : (
                  <input className={inputClass} autoFocus inputMode="decimal" value={refundForm.amount} onChange={(e) => setRefundForm((f) => (f ? { ...f, amount: e.target.value } : f))} />
                )}
              </Field>
              <Field label="Method" required>
                <select className={inputClass} value={refundForm.method} onChange={(e) => setRefundForm((f) => (f ? { ...f, method: e.target.value as RefundMethod } : f))}>
                  {REFUND_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </Field>
              <Field label="Date refunded" required>
                <input type="date" className={inputClass} value={refundForm.dateRefunded} onChange={(e) => setRefundForm((f) => (f ? { ...f, dateRefunded: e.target.value } : f))} />
              </Field>
            </div>
            <Field label="Reason" required>
              <input className={inputClass} value={refundForm.reason} maxLength={500} onChange={(e) => setRefundForm((f) => (f ? { ...f, reason: e.target.value } : f))} />
            </Field>
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">How should this refund be handled?</legend>
              <label className={['flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm', !refundForm.reverseLines ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[rgb(var(--edge-rgb)/0.10)]'].join(' ')}>
                <input type="radio" name="refund-mode" className="mt-0.5" checked={!refundForm.reverseLines} onChange={() => setRefundForm((f) => (f ? { ...f, reverseLines: false } : f))} />
                <span><span className="font-medium">Cash refund only.</span> Records the cash returned. The invoice, its revenue, and its tax stay recognized as-is. Use for an overpayment or a goodwill refund.</span>
              </label>
              <label className={['flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm', refundForm.reverseLines ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5' : 'border-[rgb(var(--edge-rgb)/0.10)]'].join(' ')}>
                <input type="radio" name="refund-mode" className="mt-0.5" checked={refundForm.reverseLines} onChange={() => setRefundForm((f) => (f ? { ...f, reverseLines: true } : f))} />
                <span><span className="font-medium">Return part of the invoice.</span> Reverses the lines you pick below (backs out their revenue and their tax) and issues a linked credit note. Use when the client is returning something that was invoiced.</span>
              </label>
            </fieldset>
            {refundForm.reverseLines ? (
              <div className="max-h-[35vh] space-y-2 overflow-y-auto pr-1">
                {refundForm.lines.map((r, i) => (
                  <div key={r.lineId} className="flex items-center gap-2 rounded-md border border-[rgb(var(--edge-rgb)/0.10)] p-2 text-sm">
                    <input
                      type="checkbox"
                      className="shrink-0"
                      checked={r.sel}
                      onChange={(e) => setRefundForm((f) => (f ? { ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, sel: e.target.checked, amount: e.target.checked && !x.amount ? String(refundLineMaxNet(f, i)) : x.amount } : x)) } : f))}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{r.description}</div>
                      <div className="text-xs text-[var(--color-muted)]">{creditRowHint(refundForm.invoice, r, 'refunded')}</div>
                    </div>
                    <div className="w-28 shrink-0">
                      <input
                        className={inputClass}
                        inputMode="decimal"
                        placeholder="0.00"
                        value={r.amount}
                        disabled={!r.sel}
                        onChange={(e) => { const n = parseFloat(e.target.value); const cap = refundLineMaxNet(refundForm, i); const v = Number.isFinite(n) && n > cap ? cap.toFixed(2) : e.target.value; setRefundForm((f) => (f ? { ...f, lines: f.lines.map((x, j) => (j === i ? { ...x, amount: v } : x)) } : f)); }}
                      />
                      {r.sel ? <div className="mt-0.5 text-right text-[10px] text-[var(--color-muted)]">max {fmtMoney(refundLineMaxNet(refundForm, i))} before tax</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {refundError ? <div className="rounded-md bg-[var(--tint-red-bg-soft)] p-2 text-sm text-[var(--tint-red-text)]">{refundError}</div> : null}
          </div>
        </ConfirmDialog>
      ) : null}

      {releaseHeadsUp ? (
        <ConfirmDialog
          open={true}
          title="Release documents to the client?"
          confirmLabel="Credit and release"
          busy={busy}
          onConfirm={() => releaseHeadsUp.retry()}
          onCancel={() => setReleaseHeadsUp(null)}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              This credit fully settles the invoice, which releases {releaseHeadsUp.documents.length} pay-on-delivery
              document{releaseHeadsUp.documents.length === 1 ? '' : 's'} to the client:
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {releaseHeadsUp.documents.map((d) => <li key={d.id}>{d.title}</li>)}
            </ul>
            <p className="text-xs text-[var(--color-muted)]">Continue to issue the credit and release the documents, or cancel to leave the invoice as it is.</p>
          </div>
        </ConfirmDialog>
      ) : null}

      {paidFeeHeadsUp ? (
        <ConfirmDialog
          open={true}
          title="Its late fee is already settled"
          confirmLabel="Void anyway"
          confirmVariant="danger"
          busy={busy}
          onConfirm={() => paidFeeHeadsUp.retry()}
          onCancel={() => setPaidFeeHeadsUp(null)}
        >
          <div className="space-y-3 text-left">
            <p className="text-sm">
              Voiding this invoice does NOT reverse a late fee that has already been settled, so the finance charge stays
              as it is:
            </p>
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {paidFeeHeadsUp.fees.map((f) => (
                <li key={f.invoiceId}>
                  <span className="font-semibold">{f.documentNumber ?? f.invoiceId}</span>{' '}
                  {f.settledBy === 'credit' ? (
                    <>
                      was settled by credit note ({fmtMoney(f.creditsApplied)}). That note already reversed the fee, so
                      leaving it standing costs the client nothing.
                    </>
                  ) : f.settledBy === 'cash' ? (
                    <>
                      was PAID IN CASH ({fmtMoney(f.cashPaid)}). Voiding keeps that money collected as a finance charge on
                      an invoice you are cancelling.
                    </>
                  ) : (
                    <>
                      was settled by {fmtMoney(f.cashPaid)} cash and {fmtMoney(f.creditsApplied)} credit. The cash half
                      stays collected as a finance charge on an invoice you are cancelling.
                    </>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-sm">
              To reverse the fee too: cancel here, void its credit note or return its payment first, then void this
              invoice again. That reopens the fee and it is voided with the invoice automatically.
            </p>
            <p className="text-xs text-[var(--color-muted)]">
              Continuing records the decision on this invoice's audit trail.
            </p>
          </div>
        </ConfirmDialog>
      ) : null}
      {detailTarget ? (() => {
        const inv = detailTarget;
        const convenienceFeeOn = inv.convenienceFee?.enabled !== false && round2(inv.totalWithConvenienceFee) !== round2(inv.totalWithoutConvenienceFee);
        const pmts = (paymentsByInvoice[inv.id] ?? []).filter((p) => p.status === 'received');
        // 04 #73(a)/lock 2 (s99): application-aware, so an APPLIED CLIENT CREDIT shows here too (it
        // names this invoice through applications[] or the appliedToInvoiceId anchor, never its own
        // invoiceId, which points at the credit's SOURCE invoice). Each row shows the per-invoice
        // applied slice, and an applied client credit is labeled distinctly from a credit note: the
        // two render identically otherwise while the accounting differs (a credit note reverses
        // revenue; an applied client credit spends a liability).
        const invAdjustments = adjustments.filter(
          (a) =>
            a.status !== 'voided' &&
            (a.invoiceId === inv.id || a.appliedToInvoiceId === inv.id || (a.applications ?? []).some((x) => x.invoiceId === inv.id)),
        );
        const adjAmountHere = (a: CreditOrAdjustment): number => {
          if (a.kind === 'refund') return a.amount;
          const app = a.applications?.find((x) => x.invoiceId === inv.id);
          if (app) return app.amount;
          const keys = [a.id, `${a.id}:${inv.id}`];
          const entry = (inv.appliedCredits ?? []).find((c) => keys.includes(c.adjustmentId));
          if (entry) return entry.amount;
          return a.invoiceId === inv.id || a.appliedToInvoiceId === inv.id ? a.amount : 0;
        };
        return createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Invoice detail">
            <div className="absolute inset-0 glass-scrim" onClick={() => setDetailTarget(null)} aria-hidden="true" />
            <div className="relative flex max-h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-[rgb(var(--edge-rgb)/0.10)] bg-[var(--surface-solid)] shadow-xl">
              <div className="flex items-start justify-between gap-3 px-6 pb-2 pt-6">
                <div className="min-w-0">
                  <h2 className="font-display text-xl">{inv.documentNumber ?? 'Invoice'}</h2>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[var(--color-muted)]">
                    <span className={`rounded-full px-2 py-0.5 font-medium ${STATE_TONE[inv.state]}`}>{STATE_LABEL[inv.state]}</span>
                    <span>{inv.billingType === 'monthly' ? 'Monthly' : 'One-off'}</span>
                    <span>· issued {displayDate(inv.issueDate)}</span>
                    <span>· due {displayDate(inv.dueDate)}</span>
                  </div>
                  {/* 04 #110: this header named the ISSUING client only, so on a linked invoice it
                      was silent about who actually owes the money - the same harm the card fixes,
                      surviving on a second surface. ONE helper serves both, so they cannot drift. */}
                  <div className="mt-1">{renderInvoiceParties(inv)}</div>
                </div>
                <button type="button" className="shrink-0 rounded p-1 text-[var(--color-muted)] hover:bg-[rgb(var(--edge-rgb)/0.05)]" aria-label="Close" onClick={() => setDetailTarget(null)}>✕</button>
              </div>
              <div className="flex-1 space-y-4 overflow-y-auto px-6 py-2 text-sm">
                <div>
                  <h3 className="mb-1 font-medium">Line items</h3>
                  <div className="divide-y divide-[rgb(var(--edge-rgb)/0.10)]">
                    {inv.lineItems.map((l) => (
                      <div key={l.lineId} className="flex items-start justify-between gap-3 py-1.5">
                        <div className="min-w-0">
                          <div className="text-[var(--color-text)]">{l.description}</div>
                          <div className="text-xs text-[var(--color-muted)]">
                            {l.qty} × {fmtMoney(l.charge)}
                            {l.taxable === false ? ' · tax-excluded' : ''}
                            {sourceLinkFor(l, inv)}
                          </div>
                        </div>
                        <div className="shrink-0 font-medium">{fmtMoney(round2(l.charge * l.qty))}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="space-y-1 border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                  <div className="flex justify-between"><span className="text-[var(--color-muted)]">Subtotal</span><span>{fmtMoney(inv.subtotal)}</span></div>
                  {inv.discount ? <div className="flex justify-between"><span className="text-[var(--color-muted)]">Discount</span><span>-{fmtMoney(inv.discount.amount)}</span></div> : null}
                  {inv.tax.enabled ? <div className="flex justify-between"><span className="text-[var(--color-muted)]">Tax</span><span>{fmtMoney(inv.tax.amount)}</span></div> : null}
                  {convenienceFeeOn ? (
                    <>
                      <div className="flex justify-between"><span className="text-[var(--color-muted)]">Convenience fee</span><span>{fmtMoney(inv.convenienceFee.amount)}</span></div>
                      <div className="flex justify-between font-semibold"><span>Total (w/convenience fee)</span><span>{fmtMoney(inv.totalWithConvenienceFee)}</span></div>
                      <div className="flex justify-between"><span>Total by Cash or Zelle *</span><span>{fmtMoney(inv.totalWithoutConvenienceFee)}</span></div>
                      <div className="text-[11px] text-[var(--color-muted)]">* Check or money order are accepted at this same total.</div>
                    </>
                  ) : (
                    <div className="flex justify-between font-semibold"><span>Total</span><span>{fmtMoney(inv.totalWithoutConvenienceFee)}</span></div>
                  )}
                  {inv.amountPaid > 0 ? <div className="flex justify-between"><span className="text-[var(--color-muted)]">Paid</span><span>{fmtMoney(inv.amountPaid)}</span></div> : null}
                  {appliedCreditsTotal(inv) > 0 ? <div className="flex justify-between"><span className="text-[var(--color-muted)]">Credits applied</span><span>-{fmtMoney(appliedCreditsTotal(inv))}</span></div> : null}
                  {inv.balanceDue > 0 ? <div className="flex justify-between font-semibold"><span>Balance</span><span>{fmtMoney(inv.balanceDue)}</span></div> : null}
                </div>
                {pmts.length > 0 ? (
                  <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                    <h3 className="mb-1 font-medium">Payments</h3>
                    {pmts.map((p) => (
                      <div key={p.id} className="text-xs">
                        <div className="flex justify-between gap-3">
                          {/* 04 #72(d): per-invoice allocation, not the receipt total. */}
                          <span className="min-w-0 text-[var(--color-muted)]">
                            {/* 04 #70: the second render site. This modal has its own inline markup and
                                never calls renderPayments, so the trigger has to be added here too or the
                                hub is missing from half the surfaces an admin actually reads payments on.
                                METHOD_LABEL matches site 1 (this one printed the raw enum). */}
                            <button
                              type="button"
                              className="font-semibold text-[var(--color-text)] underline decoration-dotted underline-offset-2 hover:decoration-solid"
                              onClick={() => openReceipt(p)}
                            >
                              {p.documentNumber ?? 'Reported'}
                            </button>
                            {' '}· {METHOD_LABEL[p.method] ?? p.method} · {displayDate(p.dateReceived)}
                          </span>
                          <span className="shrink-0">{fmtMoney(contributionTo(p, inv.id))}</span>
                        </div>
                        {paymentRowContext(p) ? (
                          <div className="text-[var(--color-muted)]">{paymentRowContext(p)}</div>
                        ) : null}
                        {paymentTipRows(p)}
                      </div>
                    ))}
                  </div>
                ) : null}
                {invAdjustments.length > 0 ? (
                  <div className="border-t border-[rgb(var(--edge-rgb)/0.10)] pt-2">
                    <h3 className="mb-1 font-medium">Credits &amp; refunds</h3>
                    {/* 04 #93 (b), 02d #93 ruling 8 as extended s213: issued-versus-applied is explicit. A
                        client credit's line reads the date it was applied to THIS invoice and carries the
                        Un-apply pill (ruling 10's home for it); every other kind reads its own date. The
                        number is never truncated (04 #93(i)); the pill wraps under the text on a narrow
                        screen instead of squeezing the number beside it. */}
                    {invAdjustments.map((a) => {
                      const appliedHere = a.kind === 'client-credit'
                        ? a.applications?.find((x) => x.invoiceId === inv.id)?.appliedAt ?? a.appliedAt ?? null
                        : null;
                      const dateLine = a.kind === 'client-credit'
                        ? appliedHere ? `applied ${displayDate(appliedHere.slice(0, 10))}` : 'applied'
                        : a.kind === 'refund'
                          ? `refunded ${displayDate((a.dateRefunded ?? a.createdAt).slice(0, 10))}`
                          : `issued ${displayDate(a.createdAt.slice(0, 10))}`;
                      return (
                        <div key={a.id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs">
                          <span className="min-w-0 text-[var(--color-muted)]">
                            {a.kind === 'client-credit' ? 'Client credit applied' : ADJ_LABEL[a.kind] ?? a.kind} · <span className="font-medium text-[var(--color-text)]">{a.documentNumber}</span> · {dateLine}
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            <span>{fmtMoney(adjAmountHere(a))}</span>
                            {a.kind === 'client-credit' && adjAmountHere(a) > 0 ? (
                              <PillButton size="xs" disabled={busy} onClick={() => void doUnapply(a, inv.id)}>Un-apply</PillButton>
                            ) : null}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[rgb(var(--edge-rgb)/0.05)] px-6 py-4">
                <div className="flex gap-4 text-sm">
                  <a
                    href={api.admin.invoiceFileUrl(inv.id, inv.clientId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-text)] underline decoration-dotted underline-offset-2 hover:opacity-80"
                  >
                    View PDF
                  </a>
                  <a
                    href={api.admin.invoiceFileUrl(inv.id, inv.clientId, true)}
                    className="text-[var(--color-text)] underline decoration-dotted underline-offset-2 hover:opacity-80"
                  >
                    Download PDF
                  </a>
                </div>
                <Button type="button" variant="ghost" onClick={() => setDetailTarget(null)}>Close</Button>
              </div>
            </div>
          </div>,
          document.body,
        );
      })() : null}
    </div>
  );
}
